import { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    faDatabase,
    faChartBar,
    faTable,
    faProjectDiagram,
    faCode,
    faTriangleExclamation,
    faCircleCheck,
    faCircleXmark,
    faMagnifyingGlassChart,
    faRotate,
    faChevronLeft,
    faChevronRight,
    faGaugeHigh,
    faCircleInfo,
    faEye,
    faXmark,
    faColumns,
    faCalculator,
    faUsers,
    faShieldHalved,
    faSitemap,
    faClipboardCheck,
    faBug,
    faKey,
    faLayerGroup,
    faRocket,
    faClock,
    faFileExcel,
    faWindowRestore,
    faFileLines,
    faFilter,
    faSquareRootVariable,
    faIndent,
    faObjectGroup,
    faTags,
    faUserGroup,
    faChevronDown,
    faFolder,
    faSpinner,
    faClockRotateLeft,
    faSearch
} from '@fortawesome/free-solid-svg-icons';

import toast from 'react-hot-toast';

import { useAssessmentJob } from '../hooks/useAssessmentJob';
import { assessmentApi } from '../services/api';
import { exportWorkbook, summaryRowsFromObject } from '../utils/exportToExcel';

import '../assets/discovery.css';


/*
|--------------------------------------------------------------------------
| Assess
|--------------------------------------------------------------------------
*/

export default function Assess() {

    const { id: historyId } = useParams();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] =
        useState('overall-status');

    const [search, setSearch] =
        useState('');

    const [page, setPage] =
        useState(1);

    const [selectedPage, setSelectedPage] =
        useState(null);

    const [showAssessmentInfo, setShowAssessmentInfo] =
        useState(false);

    /*
    |--------------------------------------------------------------------------
    | Page Complexity Config (weights + High/Medium/Low thresholds)
    |--------------------------------------------------------------------------
    | Loaded from the backend so Page Complexity (used on the Pages tab,
    | the Overall Complexity rollup, and Migration Waves) reflects whatever
    | config was last saved via the Assessment Information -> Technical
    | Details modal, instead of always using the hardcoded defaults.
    */

    const [pageComplexityConfig, setPageComplexityConfig] =
        useState(DEFAULT_PAGE_COMPLEXITY_CONFIG);

    useEffect(() => {

        let cancelled = false;

        (async () => {
            try {
                const resp = await assessmentApi.getPageComplexityConfig();
                const data = resp.data?.data || resp.data || null;
                if (!cancelled && data) {
                    setPageComplexityConfig({
                        weights: { ...DEFAULT_PAGE_COMPLEXITY_CONFIG.weights, ...(data.weights || {}) },
                        thresholds: { ...DEFAULT_PAGE_COMPLEXITY_CONFIG.thresholds, ...(data.thresholds || {}) }
                    });
                }
            } catch {
                // Keep defaults if the backend doesn't have this endpoint yet
                // or the request fails - Page Complexity still works either way.
            }
        })();

        return () => { cancelled = true; };

    }, []);

    const [showScopeModal, setShowScopeModal] =
        useState(false);


    const pageSize = 10;


    /*
    |--------------------------------------------------------------------------
    | Assessment Job (Async)
    |--------------------------------------------------------------------------
    | When historyId is present (route: /assess/:id), the hook loads that
    | specific historical assessment run instead of the latest live result.
    */

    const {
        status: jobStatus,
        result: jobResult,
        progress,
        isRunning,
        isLoading: jobLoading,
        startAssessment,
        cancelAssessment,
        refetchResult,
        isHistoryView,
        historyError
    } = useAssessmentJob(historyId, historyId ? `assess-history:${historyId}` : 'assess-live');

    const isLoading = jobLoading;
    const isFetching = isRunning;

    const [rerunningById, setRerunningById] = useState(false);

    const handleRefresh = () => {

        // Viewing a specific assess scope (/assess/:id, e.g. reached from a
        // Project's "Start Assessment" button) — re-run that same scope
        // directly by id, no need to ask for a project name or re-pick
        // apps/pages since this assess id already has them configured.
        if (historyId) {
            handleRerunById();
            return;
        }

        setShowScopeModal(true);
    };

    const handleRerunById = async () => {

        setRerunningById(true);

        try {
            // useAssessmentJob's isRunning/progress now reflect the real,
            // live-polled job status regardless of historyId, so the
            // progress bar shows right here on /assess/:id without needing
            // to navigate away to the live /assess view.
            await startAssessment({ assessId: historyId });
        } finally {
            setRerunningById(false);
        }

    };

    const handleStartAssessment = (scope) => {
        setShowScopeModal(false);
        startAssessment(scope);
    };


    /*
    |--------------------------------------------------------------------------
    | Assessment Data
    |--------------------------------------------------------------------------
    */

    const assessment =
        jobResult?.discovery || {};


    const discovery =
        assessment;


    /*
    |--------------------------------------------------------------------------
    | Summary
    |--------------------------------------------------------------------------
    */

    const summary =
        jobResult?.summary ||
        assessment.summary ||
        discovery.summary ||
        {
            datasets: 0,
            columns: 0,
            cards: 0,
            pages: 0,
            dataflows: 0,
            beastmodes: 0,
            users: 0,
            groups: 0,
            dependencies: 0,
            assessmentScore: 0,
            overallComplexity: 'Low',
            migrationReadiness: 'Ready',
            issueCount: 0
        };


    /*
    |--------------------------------------------------------------------------
    | Discovery Collections
    |--------------------------------------------------------------------------
    */

    const datasets =
        Array.isArray(discovery.datasets)
            ? discovery.datasets
            : [];

    const columns =
        Array.isArray(discovery.columns)
            ? discovery.columns
            : [];

    const cards =
        Array.isArray(discovery.cards)
            ? discovery.cards
            : [];

    const pages =
        Array.isArray(discovery.pages)
            ? discovery.pages
            : [];

    const dataflows =
        Array.isArray(discovery.dataflows)
            ? discovery.dataflows
            : [];

    const beastmodes =
        Array.isArray(discovery.beastmodes)
            ? discovery.beastmodes
            : [];

        const users =
        Array.isArray(discovery.users)
            ? discovery.users
            : [];

    const groups =
        Array.isArray(discovery.groups)
            ? discovery.groups
            : [];

    const dependencies =
        Array.isArray(discovery.dependencies)
            ? discovery.dependencies
            : [];
    
    const apps =
        Array.isArray(discovery.apps)
            ? discovery.apps
            : [];

    const userGroupMemberships =
        Array.isArray(discovery.userGroupMemberships)
            ? discovery.userGroupMemberships
            : [];

    const security =
        Array.isArray(discovery.security)
            ? discovery.security
            : [];

    const governance =
        Array.isArray(discovery.governance)
            ? discovery.governance
            : [];

    const lineage =
        Array.isArray(discovery.lineage)
            ? discovery.lineage
            : [];

    const apiCoverage =
        Array.isArray(discovery.apiCoverage)
            ? discovery.apiCoverage
            : [];

    /*
    |--------------------------------------------------------------------------
    | Assessment Collections
    |--------------------------------------------------------------------------
    */

    const assessmentDatasets =
        Array.isArray(assessment.datasets)
            ? assessment.datasets
            : datasets;

    const assessmentCards =
        Array.isArray(assessment.cards)
            ? assessment.cards
            : cards;
    
    const assessmentPages =
        Array.isArray(assessment.pages)
            ? assessment.pages
            : pages;

    const assessmentDataflows =
        Array.isArray(assessment.dataflows)
            ? assessment.dataflows
            : dataflows;

    const assessmentBeastmodes =
        Array.isArray(assessment.beastmodes)
            ? assessment.beastmodes
            : beastmodes;

    /*
    |--------------------------------------------------------------------------
    | Assessment Issues
    |--------------------------------------------------------------------------
    */

    const issues =
        Array.isArray(assessment?.assessment?.issues)
            ? assessment.assessment.issues
            : (
                Array.isArray(assessment.issues)
                    ? assessment.issues
                    : []
            );

    
    /*
    |--------------------------------------------------------------------------
    | Complexity
    |--------------------------------------------------------------------------
    */

    const complexityItems =
        useMemo(() => {

            return [
                ...assessmentDatasets,
                ...assessmentCards,
                ...assessmentPages,
                ...assessmentDataflows,
                ...assessmentBeastmodes
            ];

        }, [
            assessmentDatasets,
            assessmentCards,
            assessmentPages,
            assessmentDataflows,
            assessmentBeastmodes
        ]);


    // const complexityCounts =
    //     useMemo(
    //         () =>
    //             getAssessmentCounts(
    //                 complexityItems,
    //                 'complexity'
    //             ),
    //         [complexityItems]
    //     );
    
    const dashboardAnalysis =
        useMemo(() => {

            if (
                !Array.isArray(dependencies) ||
                !dependencies.length
            ) {
                return buildDashboardAnalysis(
                    pages,
                    cards,
                    datasets,
                    beastmodes,
                    assessmentPages
                );
            }

            return dependencies.map(
                (dependency) => {

                    const datasetCards =
                        Array.isArray(
                            dependency.cards
                        )
                            ? dependency.cards
                            : [];

                    const datasetBeastmodes =
                        Array.isArray(
                            dependency.beastmodes
                        )
                            ? dependency.beastmodes
                            : [];

                    const datasetPages =
                        Array.isArray(
                            dependency.pages
                        )
                            ? dependency.pages
                            : [];

                    const issueCount =
                        Number(
                            dependency.issueCount ||
                            0
                        );

                    const cardCount =
                        datasetCards.length;

                    const beastmodeCount =
                        datasetBeastmodes.length;

                    const pageCount =
                        datasetPages.length;

                    const complexity =
                        calculateComplexity({
                            cardCount,
                            beastmodeCount,
                            pageCount,
                            issueCount
                        });

                    const readiness =
                        calculateReadiness({
                            issueCount,
                            complexity
                        });

                    return {

                        ...dependency,

                        datasetId:
                            dependency.datasetId,

                        datasetName:
                            dependency.datasetName ||
                            '-',

                        cards:
                            datasetCards,

                        pages:
                            datasetPages,

                        beastmodes:
                            datasetBeastmodes,

                        cardCount,

                        pageCount,

                        beastmodeCount,

                        issueCount,

                        complexity,

                        migrationReadiness:
                            readiness
                    };

                }
            );

        }, [
            dependencies,
            pages,
            cards,
            datasets,
            beastmodes,
            assessmentPages
        ]);
    /*
    |--------------------------------------------------------------------------
    | Readiness
    |--------------------------------------------------------------------------
    */

    const readinessItems =
        useMemo(() => {

            return [
                ...assessmentDatasets,
                ...assessmentCards,
                ...assessmentPages,
                ...assessmentDataflows,
                ...assessmentBeastmodes
            ];

        }, [
            assessmentDatasets,
            assessmentCards,
            assessmentPages,
            assessmentDataflows,
            assessmentBeastmodes
        ]);

    
    const readinessCounts = useMemo(() => {

    const counts = {
        ready: 0,
        review: 0,
        blocked: 0
    };

    if (!Array.isArray(dashboardAnalysis)) {
        return counts;
    }

    dashboardAnalysis.forEach(item => {

        const readiness =
            String(
                item?.migrationReadiness || ''
            )
                .trim()
                .toLowerCase();

        if (readiness === 'ready') {
            counts.ready++;
        }

        else if (
            readiness === 'review' ||
            readiness === 'review required'
        ) {
            counts.review++;
        }

        else if (readiness === 'blocked') {
            counts.blocked++;
        }

    });

    return counts;

}, [dashboardAnalysis]);

    /*
    |--------------------------------------------------------------------------
    | Overall Readiness
    |--------------------------------------------------------------------------
    */

    const backendReadiness =
        summary.migrationReadiness;


    const overallReadiness =
        backendReadiness &&
        backendReadiness !== 'Ready'
            ? backendReadiness
            : (
                readinessItems.length > 0
                    ? getOverallReadiness(
                        readinessCounts
                    )
                    : (
                        backendReadiness ||
                        'Ready'
                    )
            );


    /*
    |--------------------------------------------------------------------------
    | Overall Complexity
    |--------------------------------------------------------------------------
    */

    const backendComplexity =
        summary.overallComplexity;
    
    const complexityCounts = useMemo(() => {

    const counts = {
        low: 0,
        medium: 0,
        high: 0
    };

    const pageList =
        Array.isArray(assessmentPages) && assessmentPages.length
            ? assessmentPages
            : pages;

    if (!Array.isArray(pageList)) {
        return counts;
    }

    pageList.forEach(page => {

        const { complexity } =
            calculatePageComplexity(
                page,
                pageList,
                assessmentDatasets,
                assessmentCards,
                assessmentBeastmodes,
                pageComplexityConfig
            );

        const key =
            String(complexity || '')
                .trim()
                .toLowerCase();

        if (key === 'low') {
            counts.low++;
        }

        else if (key === 'medium') {
            counts.medium++;
        }

        else if (key === 'high') {
            counts.high++;
        }

    });

    return counts;

        }, [assessmentPages, pages, assessmentDatasets, assessmentCards, assessmentBeastmodes, pageComplexityConfig]);

    /*
     * Overall Complexity now rolls up from each page's own complexity
     * (calculatePageComplexity: cards, datasets, variables, sets, groups,
     * hierarchy, app-linked pages) rather than the backend's
     * assessmentScore-based slab, so this matches the Pages tab and the
     * Complexity breakdown card exactly. The backend value is only used
     * as a fallback when there's no page data to compute from.
     */
    const overallComplexity =
        (Array.isArray(assessmentPages) && assessmentPages.length) ||
        (Array.isArray(pages) && pages.length)
            ? getOverallComplexity(complexityCounts)
            : (backendComplexity || 'Low');


    /*
    |--------------------------------------------------------------------------
    | Tabs
    |--------------------------------------------------------------------------
    */

    const tabs = [
        { key: 'overall-status', label: 'Overall Status', icon: faGaugeHigh },
        { key: 'merge-sets', label: 'Merge Sets', icon: faSitemap },
        { key: 'migration-waves', label: 'Migration Waves', icon: faRocket },
        { key: 'pages', label: 'Pages', icon: faTable, count: Number(summary.pages ?? pages.length) },
        { key: 'cards', label: 'Cards', icon: faChartBar, count: Number(summary.cards ?? cards.length) },
        { key: 'datasets', label: 'Datasets', icon: faDatabase, count: Number(summary.datasets ?? datasets.length) },
        { key: 'dependencies', label: 'Dependencies', icon: faSitemap, count: dependencies.length || lineage.length || Number(summary.dependencies || summary.lineageEdges || 0) },
        { key: 'metrics', label: 'Metrics', icon: faGaugeHigh },
        { key: 'columns', label: 'Columns', icon: faColumns, count: summary.columns || columns.length },
        
        { key: 'dataflows', label: 'DataFlows', icon: faProjectDiagram, count: Number(summary.dataflows ?? dataflows.length) },
        { key: 'beastmodes', label: 'Beast Mode', icon: faCode, count: Number(summary.beastmodes ?? beastmodes.length) },
        { key: 'data-model', label: 'Data Mapping', icon: faLayerGroup, count: columns.length || datasets.length },
        { key: 'users-groups', label: 'Users & Groups', icon: faUsers, count: (users.length + groups.length) || (Number(summary.users || 0) + Number(summary.groups || 0)) },
        
        { key: 'security-access', label: 'Security & Access', icon: faShieldHalved, count: security.length },
        { key: 'issues', label: 'Issues', icon: faBug, count: issues.length },
        // { key: 'api-coverage', label: 'API Coverage', icon: faKey, count: apiCoverage.length }
    ];


    /*
    |--------------------------------------------------------------------------
    | Current Data
    |--------------------------------------------------------------------------
    */

    const currentData =
        getCurrentData(
            activeTab,
            assessmentDatasets,
            assessmentCards,
            assessmentPages,
            assessmentDataflows,
            assessmentBeastmodes,
            issues,
            columns,
            users,
            groups,
            dependencies,
            security,
            governance,
            apiCoverage
        );


    /*
    |--------------------------------------------------------------------------
    | Search
    |--------------------------------------------------------------------------
    */

    const filteredData =
        useMemo(() => {

            if (!search.trim()) {
                return currentData;
            }

            const searchValue =
                search.toLowerCase();

            return currentData.filter(
                item =>
                    JSON.stringify(item)
                        .toLowerCase()
                        .includes(searchValue)
            );

        }, [
            currentData,
            search
        ]);


    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredData.length /
                pageSize
            )
        );


    const paginatedData =
        useMemo(() => {

            const start =
                (page - 1) *
                pageSize;

            return filteredData.slice(
                start,
                start + pageSize
            );

        }, [
            filteredData,
            page
        ]);


    /*
    |--------------------------------------------------------------------------
    | Handlers
    |--------------------------------------------------------------------------
    */

    const handleTabChange =
        key => {

            setActiveTab(key);
            setSearch('');
            setPage(1);

        };


    const handleSearch =
        event => {

            setSearch(
                event.target.value
            );

            setPage(1);

        };


    const handleViewPage =
        pageItem => {

            setSelectedPage(
                pageItem
            );

        };


    const handleClosePage =
        () => {

            setSelectedPage(null);

        };


    /*
    |--------------------------------------------------------------------------
    | Export To Excel
    |--------------------------------------------------------------------------
    */

    const handleExportExcel = () => {

        const summaryRows = summaryRowsFromObject(summary, {
            datasets: 'Datasets',
            columns: 'Columns',
            cards: 'Cards',
            pages: 'Pages',
            dataflows: 'DataFlows',
            beastmodes: 'Beast Modes',
            filters: 'Filters',
            calc: 'Calculated Fields',
            variables: 'Variables',
            sets: 'Sets',
            groups_: 'Groups',
            hierarchy: 'Hierarchy',
            users: 'Users',
            groups: 'Groups (Domo)',
            dependencies: 'Dependencies',
            assessmentScore: 'Assessment Score',
            overallComplexity: 'Overall Complexity',
            migrationReadiness: 'Migration Readiness',
            issueCount: 'Issue Count'
        });

        const exportableTabs = tabs.filter(tab =>
            !['overview', 'metrics', 'merge-sets', 'migration-waves', 'overall-status', 'data-model'].includes(tab.key)
        );

        const tabSheets = exportableTabs.map(tab => {

            const data = getCurrentData(
                tab.key,
                assessmentDatasets,
                assessmentCards,
                assessmentPages,
                assessmentDataflows,
                assessmentBeastmodes,
                issues,
                columns,
                users,
                groups,
                dependencies,
                security,
                governance,
                apiCoverage
            );

            const rows = flattenRowsForExport(tab.key, data);

            return {
                name: tab.label,
                rows
            };

        });

        exportWorkbook([
            { name: 'Summary', rows: summaryRows },
            ...tabSheets
        ], 'Domo_Assessment');

    };


    /*
    |--------------------------------------------------------------------------
    | Export Current Tab To Excel
    |--------------------------------------------------------------------------
    | Exports only the active tab's data (respecting the current search
    | filter) as a single-sheet workbook, for tabs backed by a plain data
    | table (datasets, cards, pages, dataflows, beast modes, issues,
    | columns, users & groups, dependencies, security, governance, api
    | coverage). Diagram/analysis tabs (Metrics, Data Model, Merge Sets,
    | Migration Waves, Overall Status) have their own dedicated views and
    | aren't exportable this way - "Export Tab" isn't shown for those.
    |--------------------------------------------------------------------------
    */

    const handleExportCurrentTab = () => {

        if (!filteredData.length) return;

        const tabLabel = tabs.find(tab => tab.key === activeTab)?.label || activeTab;
        const rows = flattenRowsForExport(activeTab, filteredData);

        exportWorkbook([
            { name: tabLabel, rows }
        ], `Domo_Assessment_${tabLabel.replace(/[^a-z0-9]+/gi, '_')}`);

    };


    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (

        <div
            style={{
                padding: '24px',
                color: 'var(--text-primary)'
            }}
        >

            {/* HEADER */}

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '20px'
                }}
            >

                <div>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '11px'
                        }}
                    >

                        <div
                            style={{
                                width: '38px',
                                height: '38px',
                                borderRadius: 'var(--radius-lg)',
                                background: 'var(--accent)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: 'var(--shadow-sm)'
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faMagnifyingGlassChart}
                                style={{
                                    color: '#fff',
                                    fontSize: '16px'
                                }}
                            />
                        </div>

                        <h2
                            style={{
                                margin: 0,
                                fontSize: '21px',
                                fontWeight: 700,
                                letterSpacing: '-0.01em'
                            }}
                        >
                            {historyId ? jobResult?.projectName : "Overall Assessment"}
                        </h2>

                    </div>

                    <div
                        style={{
                            fontSize: '12.5px',
                            color: 'var(--text-secondary)',
                            marginTop: '6px',
                            marginLeft: '49px'
                        }}
                    >
                        Migration readiness and complexity analysis
                    </div>

                    {/* {isHistoryView && (
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                marginTop: '9px',
                                marginLeft: '49px',
                                padding: '5px 11px',
                                borderRadius: 'var(--radius-lg)',
                                background: 'var(--warning-bg)',
                                color: 'var(--warning-text)',
                                fontSize: '11px',
                                fontWeight: 600
                            }}
                        >
                            <FontAwesomeIcon icon={faClockRotateLeft} />
                            Viewing historical assessment
                            <button
                                type="button"
                                onClick={() => navigate('/assess')}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: 'var(--warning-text)',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    padding: 0,
                                    marginLeft: '4px'
                                }}
                            >
                                Back to Live
                            </button>
                        </div>
                    )} */}

                    {isHistoryView && historyError && (
                        <div
                            style={{
                                marginTop: '8px',
                                marginLeft: '49px',
                                fontSize: '11px',
                                color: 'var(--danger)'
                            }}
                        >
                            Failed to load this assessment run: {historyError}
                        </div>
                    )}

                </div>


                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '8px'
                    }}
                >

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                    {isRunning ? (
                        <button
                            type="button"
                            onClick={cancelAssessment}
                            style={{
                                border: '1px solid var(--danger)',
                                borderRadius: 'var(--radius-md)',
                                padding: '9px 14px',
                                background: 'var(--danger-bg)',
                                color: 'var(--danger)',
                                fontSize: '13px',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                transition: 'filter .15s'
                            }}
                            onMouseOver={e => { e.currentTarget.style.filter = 'brightness(0.97)'; }}
                            onMouseOut={e => { e.currentTarget.style.filter = 'none'; }}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                            Cancel
                        </button>
                    ) : null}

                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={isRunning || rerunningById}
                        style={{
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            padding: '9px 16px',
                            background: 'var(--accent)',
                            color: '#fff',
                            fontSize: '13px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: 'var(--shadow-sm)',
                            cursor:
                                (isRunning || rerunningById)
                                    ? 'not-allowed'
                                    : 'pointer',
                            opacity:
                                (isRunning || rerunningById)
                                    ? 0.7
                                    : 1,
                            transition: 'transform .15s, box-shadow .15s'
                        }}
                        onMouseOver={e => { if (!isRunning && !rerunningById) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; } }}
                        onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
                    >

                        <FontAwesomeIcon
                            icon={faRotate}
                            spin={isRunning || rerunningById}
                        />

                        {isRunning
                            ? `Running... ${progress.percent || 0}%`
                            : rerunningById
                                ? 'Starting...'
                                : (historyId ? 'Restart Assessment' : 'Start Assessment')
                        }

                    </button>

                    <button
                        type="button"
                        onClick={handleExportExcel}
                        title="Export assessment to Excel"
                        aria-label="Export assessment to Excel"
                        style={{
                            border: '1px solid var(--border-color)',
                            borderRadius: 'var(--radius-md)',
                            padding: '9px 14px',
                            background: 'var(--card-bg)',
                            color: 'var(--success)',
                            fontSize: '13px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            transition: 'background .15s'
                        }}
                        onMouseOver={e => { e.currentTarget.style.background = 'var(--success-bg)'; }}
                        onMouseOut={e => { e.currentTarget.style.background = 'var(--card-bg)'; }}
                    >
                        <FontAwesomeIcon icon={faFileExcel} />
                        Export to Excel
                    </button>

                    <button
                        type="button"
                        onClick={() => setShowAssessmentInfo(true)}
                        title="Assessment methodology and tab guide"
                        aria-label="Assessment methodology and tab guide"
                        style={{
                            width: '38px',
                            height: '38px',
                            border: '1px solid var(--border-color)',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--card-bg)',
                            color: 'var(--accent)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '16px',
                            transition: 'background .15s'
                        }}
                        onMouseOver={e => { e.currentTarget.style.background = 'var(--accent-light)'; }}
                        onMouseOut={e => { e.currentTarget.style.background = 'var(--card-bg)'; }}
                    >
                        <FontAwesomeIcon icon={faCircleInfo} />
                    </button>
                    </div>

                    {/* Job Status Info */}
                    <AssessmentStatusBanner
                        jobStatus={jobStatus}
                        progress={progress}
                        isRunning={isRunning}
                        summary={summary}
                        isHistoryView={isHistoryView}
                    />

                </div>

            </div>


            {/* RESOURCE METRICS */}

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns:
                        'repeat(auto-fit, minmax(170px, 1fr))',
                    gap: '10px',
                    marginBottom: '20px'
                }}
            >

                <ResourceMetric
                    title="Complexity"
                    value={overallComplexity}
                    icon={faGaugeHigh}
                    breakdown={[
                        { label: 'Low', value: complexityCounts.low, color: '#22c55e' },
                        { label: 'Medium', value: complexityCounts.medium, color: '#f59e0b' },
                        { label: 'High', value: complexityCounts.high, color: '#ef4444' }
                    ]}
                />

                <ResourceMetric
                    title="Apps"
                    value={summary.app_count}
                    icon={faWindowRestore}
                />

                <ResourceMetric
                    title="Pages"
                    value={summary.pages}
                    icon={faFileLines}
                />

                <ResourceMetric
                    title="Datasets"
                    value={summary.datasets}
                    icon={faDatabase}
                />

            </div>


            {/* MAIN CARD */}

            <div
                style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-sm)',
                    overflow: 'hidden'
                }}
            >

                {/* TABS */}

                <div
                    style={{
                        display: 'flex',
                        overflowX: 'auto',
                        borderBottom:
                            '1px solid var(--border-color)'
                    }}
                >

                    {tabs.map(tab => {

                        const active =
                            activeTab === tab.key;

                        return (

                            <button
                                key={tab.key}
                                type="button"
                                onClick={() =>
                                    handleTabChange(tab.key)
                                }
                                style={{
                                    border: 'none',
                                    borderBottom:
                                        active
                                            ? '2px solid var(--accent)'
                                            : '2px solid transparent',
                                    background:
                                        active
                                            ? 'var(--accent-light)'
                                            : 'transparent',
                                    color:
                                        active
                                            ? 'var(--accent)'
                                            : 'var(--text-secondary)',
                                    padding:
                                        '13px 16px',
                                    whiteSpace: 'nowrap',
                                    fontSize: '13px',
                                    fontWeight:
                                        active
                                            ? 600
                                            : 400,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                    transition: 'background .15s, color .15s'
                                }}
                                onMouseOver={e => { if (!active) e.currentTarget.style.background = 'var(--hover-bg)'; }}
                                onMouseOut={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                            >

                                <FontAwesomeIcon
                                    icon={tab.icon}
                                />

                                <span>
                                    {tab.label}
                                </span>

                                {tab.count !== undefined && (

                                    <span
                                        style={{
                                            background:
                                                active
                                                    ? 'var(--bg-card)'
                                                    : 'var(--hover-bg)',
                                            color:
                                                active
                                                    ? 'var(--accent)'
                                                    : 'var(--text-secondary)',
                                            borderRadius: '10px',
                                            padding: '2px 7px',
                                            fontSize: '11px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {tab.count || 0}
                                    </span>

                                )}

                            </button>

                        );

                    })}

                </div>


                {activeTab === 'metrics' ? (

                    <MetricsTab
                        summary={summary}
                        columns={columns}
                        beastmodes={beastmodes}
                        users={users}
                        groups={groups}
                        dependencies={dependencies}
                        lineage={lineage}
                        readinessCounts={readinessCounts}
                        overallReadiness={overallReadiness}
                        resourceCount={readinessItems.length}
                        complexityCounts={complexityCounts}
                        overallComplexity={overallComplexity}
                    />

                ) : activeTab === 'columns' ? (

                    <ColumnsTab data={columns} datasets={assessmentDatasets} />

                ) : activeTab === 'data-model' ? (

                    <DataModelTab datasets={assessmentDatasets} columns={columns} cards={assessmentCards} pages={assessmentPages} beastmodes={assessmentBeastmodes} />

                ) : activeTab === 'users-groups' ? (

                    <UsersGroupsTab users={users} groups={groups} memberships={userGroupMemberships} />

                ) : activeTab === 'dependencies' ? (

                    <DependenciesTab dependencies={dependencies} lineage={lineage} datasets={datasets} cards={cards} pages={pages} dataflows={dataflows} beastmodes={beastmodes} apps = {apps} />

                ) : activeTab === 'security-access' ? (

                    <SecurityAccessTab data={security} />

                ) : activeTab === 'governance' ? (

                    <GovernanceTab data={governance} />

                ) : activeTab === 'issues' ? (

                    <IssuesTab data={issues} />

                ) : activeTab === 'api-coverage' ? (

                    <ApiCoverageTab data={apiCoverage} />

                ) : activeTab === 'merge-sets' ? (

                    <MergeSetsTab
                        pages={assessmentPages}
                        cards={assessmentCards}
                        datasets={assessmentDatasets}
                        beastmodes={assessmentBeastmodes}
                    />

                ) : activeTab === 'migration-waves' ? (

                    <MigrationWavesTab
                        pages={assessmentPages}
                        cards={assessmentCards}
                        datasets={assessmentDatasets}
                        beastmodes={assessmentBeastmodes}
                        pageComplexityConfig={pageComplexityConfig}
                    />

                ) : activeTab === 'overall-status' ? (

    <OverallStatusTab
        pages={assessmentPages}
        cards={assessmentCards}
        datasets={assessmentDatasets}
        beastmodes={assessmentBeastmodes}
    />

) :  (

                    <>

                        {/* SEARCH + PER-TAB EXPORT */}

                        <div
                            style={{
                                padding: '14px 16px',
                                borderBottom:
                                    '1px solid var(--border-color)',
                                display: 'flex',
                                gap: '10px',
                                alignItems: 'center'
                            }}
                        >

                            <div
                                style={{
                                    position: 'relative',
                                    flex: 1
                                }}
                            >

                                <FontAwesomeIcon
                                    icon={faMagnifyingGlassChart}
                                    style={{
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform:
                                            'translateY(-50%)',
                                        color:
                                            'var(--text-tertiary)',
                                        fontSize: '13px'
                                    }}
                                />

                                <input
                                    type="text"
                                    value={search}
                                    onChange={handleSearch}
                                    placeholder={
                                        `Search ${
                                            tabs.find(
                                                tab =>
                                                    tab.key ===
                                                    activeTab
                                            )?.label || ''
                                        }...`
                                    }
                                    style={{
                                        width: '100%',
                                        height: '36px',
                                        border:
                                            '1px solid var(--border-color)',
                                        borderRadius: '6px',
                                        padding:
                                            '0 12px 0 34px',
                                        outline: 'none',
                                        background:
                                            'var(--input-bg)',
                                        color:
                                            'var(--text-primary)',
                                        fontSize: '13px',
                                        boxSizing: 'border-box'
                                    }}
                                />

                            </div>

                            <button
                                type="button"
                                onClick={handleExportCurrentTab}
                                disabled={!filteredData.length}
                                title={`Export ${getTabLabel(activeTab)} to Excel`}
                                aria-label={`Export ${getTabLabel(activeTab)} to Excel`}
                                style={{
                                    height: '36px',
                                    flexShrink: 0,
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px',
                                    padding: '0 14px',
                                    background: 'var(--card-bg)',
                                    color: filteredData.length ? 'var(--success)' : 'var(--text-tertiary)',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: filteredData.length ? 'pointer' : 'not-allowed',
                                    whiteSpace: 'nowrap',
                                    transition: 'background .15s'
                                }}
                                onMouseOver={e => { if (filteredData.length) e.currentTarget.style.background = 'var(--success-bg)'; }}
                                onMouseOut={e => { e.currentTarget.style.background = 'var(--card-bg)'; }}
                            >
                                <FontAwesomeIcon icon={faFileExcel} />
                                Export Tab
                            </button>

                        </div>


                        {/* TABLE */}

                        {isLoading ? (

                            <Loading />

                        ) : (

                            <AssessmentTable
                                type={activeTab}
                                data={paginatedData}
                                pages={pages}
                                datasets={assessmentDatasets}
                                cards={assessmentCards}
                                beastmodes={assessmentBeastmodes}
                                onViewPage={handleViewPage}
                                pageComplexityConfig={pageComplexityConfig}
                            />

                        )}


                        {/* PAGINATION */}

                        {!isLoading &&
                            filteredData.length >
                                pageSize && (

                                <Pagination
                                    page={page}
                                    totalPages={totalPages}
                                    onPageChange={setPage}
                                    total={filteredData.length}
                                    pageSize={pageSize}
                                />

                            )}

                    </>

                )}

            </div>


            {/* =========================================================
                PAGE DETAILS MODAL
            ========================================================== */}

            {selectedPage && (

                <PageDetailsModal
                    page={selectedPage}
                    datasets={assessmentDatasets}
                    cards={assessmentCards}
                    beastmodes={assessmentBeastmodes}
                    onClose={handleClosePage}
                />

            )}

            {showAssessmentInfo && (
                <AssessmentInfoModal
                    summary={summary}
                    pageComplexityConfig={pageComplexityConfig}
                    onPageComplexityConfigChange={setPageComplexityConfig}
                    onClose={() => setShowAssessmentInfo(false)}
                />
            )}

            {showScopeModal && (
                <AssessmentScopeModal
                    onClose={() => setShowScopeModal(false)}
                    onStart={handleStartAssessment}
                />
            )}


        </div>
    );
}


/*
|--------------------------------------------------------------------------
| ASSESSMENT HISTORY MODAL
/*
|--------------------------------------------------------------------------
| ASSESSMENT SCOPE MODAL (Start Assessment -> choose Apps / Pages)
|--------------------------------------------------------------------------
| Fetches GET /apps-pages, which returns the Apps and Pages available in
| Domo. Both are shown as top-level, expandable groups (Apps and Pages are
| each a "parent" with their own items underneath). The user can select
| individual items from either group, use "Select All" to select
| everything, and must give the assessment a project name before starting.
| Only the selected app/page IDs (plus the project name) are sent to
| /assessment/start.
*/

function AssessmentScopeModal({ onClose, onStart }) {

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [apps, setApps] = useState([]);
    const [history, setHistory] = useState([])
    const [pages, setPages] = useState([]);
    const [projectName, setProjectName] = useState('Overall assessment');
    const [selectedAppIds, setSelectedAppIds] = useState([]);
    const [selectedPageIds, setSelectedPageIds] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);

    useEffect(() => {

        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const resp = await assessmentApi.appsPages();
                const payload = resp.data?.data || resp.data || {};
                if (cancelled) return;
                setApps(Array.isArray(payload.apps) ? payload.apps : []);
                setPages(Array.isArray(payload.pages) ? payload.pages : []);
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.error || err.message || 'Failed to load apps and pages');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };

    }, []);

    const itemId = item => item?.id ?? item?.appId ?? item?.pageId ?? item?.dataAppId ?? item?.value;
    const itemName = item => item?.name ?? item?.title ?? item?.label ?? item?.pageName ?? item?.appName ?? String(itemId(item));
    const itemChildren = item => item?.children || item?.pages || item?.items || item?.cards || null;

    const toggleExpanded = key => {
        setExpandedKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const toggleSelected = (list, setList, id) => {
        setList(prev =>
            prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
        );
    };

    const allAppIds = apps.map(itemId).filter(id => id != null);
    const allPageIds = pages.map(itemId).filter(id => id != null);

    const allSelected =
        allAppIds.length + allPageIds.length > 0 &&
        allAppIds.every(id => selectedAppIds.includes(id)) &&
        allPageIds.every(id => selectedPageIds.includes(id));

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedAppIds([]);
            setSelectedPageIds([]);
        } else {
            setSelectedAppIds(allAppIds);
            setSelectedPageIds(allPageIds);
        }
    };

    const totalSelected = selectedAppIds.length + selectedPageIds.length;
    // Project Name input is currently hidden from this modal (defaults to
    // "Overall assessment"), so starting only requires at least one
    // app/page selected.
    const canStart = totalSelected > 0;

    const renderTree = (list, selected, setSelected, groupKey) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {list.map((item, index) => {

                const id = itemId(item);
                const key = `${groupKey}-${id ?? index}`;
                const children = itemChildren(item);
                const hasChildren = Array.isArray(children) && children.length > 0;
                const expanded = expandedKeys.includes(key);
                const checked = selected.includes(id);

                return (
                    <div key={key}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 4px',
                                fontSize: '13px'
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => hasChildren && toggleExpanded(key)}
                                aria-label={hasChildren ? (expanded ? 'Collapse' : 'Expand') : undefined}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: hasChildren ? 'pointer' : 'default',
                                    padding: 0,
                                    width: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'var(--text-tertiary)',
                                    fontSize: '10px'
                                }}
                            >
                                {hasChildren ? (
                                    <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                                ) : null}
                            </button>

                            <FontAwesomeIcon icon={faFolder} style={{ color: '#f0b429', fontSize: '13px' }} />

                            <span
                                style={{
                                    flex: '1 1 auto',
                                    color: '#1e447e',
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={() => id != null && toggleSelected(selected, setSelected, id)}
                            >
                                {itemName(item)}
                            </span>

                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => id != null && toggleSelected(selected, setSelected, id)}
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                            />
                        </div>

                        {hasChildren && expanded && (
                            <div style={{ paddingLeft: '22px' }}>
                                {renderTree(children, selected, setSelected, `${key}-child`)}
                            </div>
                        )}
                    </div>
                );

            })}
        </div>
    );

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Choose assessment scope"
            onMouseDown={event => {
                if (event.target === event.currentTarget) onClose();
            }}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15, 23, 42, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '24px'
            }}
        >
            <div
                style={{
                    background: 'var(--card-bg, #fff)',
                    borderRadius: '10px',
                    maxWidth: '1000px',
                    width: '100%',
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.25)'
                }}
            >

                {/* HEADER */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 20px',
                        borderBottom: '1px solid var(--border-color)'
                    }}
                >
                    <div style={{ fontSize: '15px', fontWeight: 600 }}>
                        Start Assessment
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: '16px',
                            color: 'var(--text-secondary)',
                            padding: '4px'
                        }}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                
                {/* PROJECT NAME */}
                {/* <div
                    style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid var(--border-color)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}
                >
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        Project Name
                    </label>
                    <input
                        type="text"
                        value={projectName}
                        onChange={e => setProjectName(e.target.value)}
                        placeholder="Enter a name for this assessment"
                        style={{
                            flex: '1 1 auto',
                            height: '36px',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            padding: '0 12px',
                            outline: 'none',
                            background: 'var(--input-bg)',
                            color: 'var(--text-primary)',
                            fontSize: '13px'
                        }}
                    />
                    <button
                        type="button"
                        onClick={handleSelectAll}
                        disabled={loading || !!error || (allAppIds.length + allPageIds.length === 0)}
                        style={{
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            padding: '8px 14px',
                            fontSize: '12px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            background: allSelected ? '#1e447e' : 'var(--card-bg, #fff)',
                            color: allSelected ? '#fff' : 'var(--text-secondary)'
                        }}
                    >
                        {allSelected ? 'Deselect All' : 'Select All'}
                    </button>
                </div> */}

                {/* BODY */}
                <div style={{ flex: '1 1 auto', overflow: 'auto', padding: '16px 20px' }}>

                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px', padding: '30px 0', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Loading apps and pages...
                        </div>
                    ) : error ? (
                        <div style={{ color: '#dc2626', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>
                            {error}
                        </div>
                    ) : (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '24px'
                            }}
                        >
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px' }}>
                                    Apps ({apps.length})
                                </div>
                                {!apps.length ? (
                                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>No apps found.</div>
                                ) : renderTree(apps, selectedAppIds, setSelectedAppIds, 'app')}
                            </div>

                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px' }}>
                                    Pages ({pages.length})
                                </div>
                                {!pages.length ? (
                                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>No pages found.</div>
                                ) : renderTree(pages, selectedPageIds, setSelectedPageIds, 'page')}
                            </div>
                        </div>
                    )}

                </div>

                {/* FOOTER */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px 20px',
                        borderTop: '1px solid var(--border-color)'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {totalSelected} item{totalSelected === 1 ? '' : 's'} selected
                        </span>
                        <button
                            type="button"
                            onClick={handleSelectAll}
                            disabled={loading || !!error || (allAppIds.length + allPageIds.length === 0)}
                            style={{
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                cursor: (loading || !!error || (allAppIds.length + allPageIds.length === 0)) ? 'not-allowed' : 'pointer',
                                opacity: (loading || !!error || (allAppIds.length + allPageIds.length === 0)) ? 0.6 : 1,
                                background: allSelected ? '#1e447e' : 'var(--card-bg, #fff)',
                                color: allSelected ? '#fff' : 'var(--text-secondary)'
                            }}
                        >
                            {allSelected ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                padding: '9px 16px',
                                fontSize: '13px',
                                fontWeight: 500,
                                background: 'var(--card-bg, #fff)',
                                color: 'var(--text-secondary)',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => canStart && onStart({
                                projectName: projectName.trim(),
                                appIds: selectedAppIds,
                                pageIds: selectedPageIds
                            })}
                            disabled={!canStart}
                            title={!canStart ? 'Select at least one app or page' : 'Start assessment'}
                            style={{
                                border: 'none',
                                borderRadius: '6px',
                                padding: '9px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                background: canStart ? '#1e447e' : '#94a3b8',
                                color: '#fff',
                                cursor: canStart ? 'pointer' : 'not-allowed'
                            }}
                        >
                            <FontAwesomeIcon icon={faCircleCheck} style={{ marginRight: '6px' }} />
                            Start Assessment
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
}


/*
|--------------------------------------------------------------------------
| ASSESSMENT INFORMATION / METHODOLOGY MODAL
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| ASSESSMENT PLAIN-ENGLISH SUMMARY
|--------------------------------------------------------------------------
| Client-facing explanation of the assessment, written without formulas,
| algorithm names, or technical jargon (no "Jaccard", "MinHash/LSH",
| weighted formulas, etc). This is the default tab in the Assessment
| Information modal so a non-technical stakeholder gets a plain-language
| explanation first, with the detailed math available separately under
| "Technical Details" for anyone who wants it.
|--------------------------------------------------------------------------
*/

function AssessmentPlainEnglishSummary({ summary = {} }) {

    const readiness = summary.migrationReadiness || 'Ready';
    const complexity = summary.overallComplexity || 'Low';
    const score = summary.assessmentScore ?? 0;
    const issueCount = summary.issueCount ?? 0;

    const readinessCopy = {
        ready: {
            color: '#078653',
            bg: '#e8fbf2',
            text: 'Your Domo environment looks ready to migrate. We didn\u2019t find any blocking issues.'
        },
        'review required': {
            color: '#b27700',
            bg: '#fff5d9',
            text: 'A few things need a second look before migrating, but nothing is blocking. We found some items worth reviewing.'
        },
        blocked: {
            color: '#d91f28',
            bg: '#fff0f1',
            text: 'Some content has a serious issue that should be resolved before migrating it. See the Issues tab for specifics.'
        }
    };

    const readinessInfo = readinessCopy[String(readiness).toLowerCase()] || readinessCopy.ready;

    const complexityCopy = {
        low: 'This content is straightforward - migration should be quick with minimal rework.',
        medium: 'This content has a moderate amount of setup (cards, datasets, calculations) - expect a normal amount of migration work.',
        high: 'This content is content-heavy or intricate (lots of cards, calculated fields, or dependencies) - plan for more time and careful review.'
    };

    const complexityInfo = complexityCopy[String(complexity).toLowerCase()] || complexityCopy.low;

    const cards = [
        {
            label: 'Migration Readiness',
            value: readiness,
            color: readinessInfo.color,
            bg: readinessInfo.bg,
            explain: readinessInfo.text
        },
        {
            label: 'Overall Complexity',
            value: complexity,
            color: complexity.toLowerCase() === 'high' ? '#d91f28' : complexity.toLowerCase() === 'medium' ? '#b27700' : '#078653',
            bg: complexity.toLowerCase() === 'high' ? '#fff0f1' : complexity.toLowerCase() === 'medium' ? '#fff5d9' : '#e8fbf2',
            explain: complexityInfo
        }
    ];

    return (
        <div>

            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
                What this assessment tells you
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '16px' }}>
                We scanned your connected Domo environment (apps, pages, cards, datasets, and their calculations) and
                summarized how much content there is, how ready it is to move, and how complex each piece is to migrate.
                No formulas here - just what it means for your migration plan.
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: '12px',
                    marginBottom: '20px'
                }}
            >
                {cards.map(card => (
                    <div
                        key={card.label}
                        style={{
                            border: '1px solid var(--border-color)',
                            borderRadius: '10px',
                            padding: '14px',
                            background: card.bg
                        }}
                    >
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                            {card.label}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: card.color, marginBottom: '8px' }}>
                            {card.value}
                        </div>
                        <div style={{ fontSize: '12px', lineHeight: 1.55, color: 'var(--text-primary)' }}>
                            {card.explain}
                        </div>
                    </div>
                ))}
            </div>

            <div
                style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '10px',
                    padding: '14px',
                    marginBottom: '20px'
                }}
            >
                <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                    Assessment Score: {score}
                </div>
                <div style={{ fontSize: '12px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                    Think of this as a "size and complexity" score for everything we found - it goes up with more
                    dashboards, cards, datasets, calculated fields, and other content. A higher number simply means
                    there is more to migrate, not that something is wrong.
                    {issueCount > 0 && (
                        <> We also found <strong style={{ color: 'var(--text-primary)' }}>{issueCount} issue{issueCount === 1 ? '' : 's'}</strong> worth a look - see the Issues tab for details.</>
                    )}
                </div>
            </div>

            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '10px' }}>
                What each area of the app tells you
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: '10px'
                }}
            >
                {[
                    { title: 'Overall Status', text: 'Compares every dashboard to every other dashboard and flags which ones look similar - useful for spotting duplicates.' },
                    { title: 'Merge Sets', text: 'Groups dashboards that are similar enough that they could likely be combined into one, saving migration effort.' },
                    { title: 'Migration Waves', text: 'Sorts dashboards into "easy", "medium", and "complex" groups so you can migrate the quick wins first.' },
                    { title: 'Dependencies', text: 'Shows how apps, pages, cards, and datasets connect to each other, so you know what depends on what before you move anything.' },
                    { title: 'Issues', text: 'Lists anything we found that needs attention before or during migration.' },
                    { title: 'Metrics', text: 'A quick count of everything discovered - datasets, cards, filters, and more - so you know the scale of the migration.' }
                ].map(item => (
                    <div
                        key={item.title}
                        style={{
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            padding: '10px 12px',
                            background: 'var(--hover-bg)'
                        }}
                    >
                        <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>{item.title}</div>
                        <div style={{ fontSize: '11px', lineHeight: 1.5, color: 'var(--text-secondary)' }}>{item.text}</div>
                    </div>
                ))}
            </div>

        </div>
    );

}


function AssessmentInfoModal({ summary = {}, pageComplexityConfig, onPageComplexityConfigChange, onClose }) {
    const [section, setSection] = useState('tabs');

    /*
     * Page Complexity factor weights + High/Medium thresholds, editable
     * from this modal (same pattern as the Pairwise weights below):
     * saved via PUT /assessment/page-complexity-config, and immediately
     * reflected across the app (Pages tab, Overall Complexity, Migration
     * Waves) via onPageComplexityConfigChange, which updates the shared
     * pageComplexityConfig state in the parent Assess component.
     */
    const pageComplexityFactorMeta = [
        { key: 'cards', label: 'Cards', description: 'Weight per unique card used on the page.' },
        { key: 'datasets', label: 'Datasets', description: 'Weight per unique dataset used on the page.' },
        { key: 'variables', label: 'Variables', description: 'Weight per variable defined across the page\'s cards.' },
        { key: 'sets', label: 'Sets', description: 'Weight per set/filter-group used across the page\'s cards.' },
        { key: 'groups', label: 'Groups', description: 'Weight per grouping definition used across the page\'s cards.' },
        { key: 'hierarchy', label: 'Hierarchy', description: 'Weight per hierarchy definition used across the page\'s cards.' },
        { key: 'appLinkedPages', label: 'Other pages in same app', description: 'Weight per other page that shares the same Domo app as this page.' }
    ];

    const [pageComplexityDraft, setPageComplexityDraft] = useState(
        pageComplexityConfig || DEFAULT_PAGE_COMPLEXITY_CONFIG
    );
    const [editingPageComplexity, setEditingPageComplexity] = useState(false);
    const [savingPageComplexity, setSavingPageComplexity] = useState(false);

    const handlePageComplexityWeightChange = (key, value) => {
        const numeric = value === '' ? '' : Number(value);
        setPageComplexityDraft(prev => ({
            ...prev,
            weights: { ...prev.weights, [key]: numeric }
        }));
    };

    const handlePageComplexityThresholdChange = (key, value) => {
        const numeric = value === '' ? '' : Number(value);
        setPageComplexityDraft(prev => ({
            ...prev,
            thresholds: { ...prev.thresholds, [key]: numeric }
        }));
    };

    const handleStartEditingPageComplexity = () => {
        setPageComplexityDraft(pageComplexityConfig || DEFAULT_PAGE_COMPLEXITY_CONFIG);
        setEditingPageComplexity(true);
    };

    const handleCancelEditingPageComplexity = () => {
        setPageComplexityDraft(pageComplexityConfig || DEFAULT_PAGE_COMPLEXITY_CONFIG);
        setEditingPageComplexity(false);
    };

    const handleSavePageComplexity = async () => {

        const weights = {};
        for (const factor of pageComplexityFactorMeta) {
            const value = Number(pageComplexityDraft.weights?.[factor.key]);
            if (Number.isNaN(value) || value < 0) {
                toast.error(`${factor.label} weight must be a non-negative number.`);
                return;
            }
            weights[factor.key] = value;
        }

        const highThreshold = Number(pageComplexityDraft.thresholds?.high);
        const mediumThreshold = Number(pageComplexityDraft.thresholds?.medium);

        if (Number.isNaN(highThreshold) || Number.isNaN(mediumThreshold) || highThreshold < 0 || mediumThreshold < 0) {
            toast.error('High and Medium thresholds must be non-negative numbers.');
            return;
        }

        if (mediumThreshold >= highThreshold) {
            toast.error('The Medium threshold must be lower than the High threshold.');
            return;
        }

        const normalized = { weights, thresholds: { high: highThreshold, medium: mediumThreshold } };

        setSavingPageComplexity(true);
        try {
            await assessmentApi.updatePageComplexityConfig(normalized);
            onPageComplexityConfigChange?.(normalized);
            setPageComplexityDraft(normalized);
            setEditingPageComplexity(false);
            toast.success('Page complexity settings updated.');
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Failed to save page complexity settings.');
        } finally {
            setSavingPageComplexity(false);
        }

    };

    const activePageComplexityConfig = pageComplexityConfig || DEFAULT_PAGE_COMPLEXITY_CONFIG;

    /*
     * Pairwise dimension weights used to be hardcoded in the UI. They now
     * come from the backend (GET /assessment/weights) so the table always
     * reflects whatever the scoring engine is actually using, and can be
     * edited + saved back (PUT /assessment/weights) from here.
     */
    const dimensionMeta = [
        { key: 'datasources', label: 'Datasources', description: 'Jaccard similarity of datasource IDs shared between two pages. Strongest merge signal.' },
        { key: 'sourceTables', label: 'Source tables', description: 'Jaccard similarity of physical table names from Domo stream configuration.' },
        { key: 'connectorType', label: 'Connector type', description: 'Jaccard similarity of data provider/driver type (e.g., PostgreSQL, Snowflake).' },
        { key: 'worksheets', label: 'Worksheets / Cards', description: 'Jaccard similarity of normalized card/worksheet names on each page.' },
        { key: 'chartTypes', label: 'Chart types', description: 'Jaccard similarity of visualization types (bar, line, pie, etc.).' },
        { key: 'fieldBindings', label: 'Field bindings', description: 'Jaccard similarity of axis column bindings (category + value fields).' },
        { key: 'columns', label: 'Columns', description: 'Jaccard similarity of column names from datasets used by each page.' },
        { key: 'columnTypes', label: 'Column types', description: 'Jaccard similarity of data types (STRING, DECIMAL, DATETIME, etc.).' },
        { key: 'calculatedFields', label: 'Calculated fields', description: 'Jaccard similarity of Beast Mode / calculated field names.' },
        { key: 'formulaSignatures', label: 'Formula signatures', description: 'Jaccard similarity of function calls extracted from formulas (SUM, AVG, etc.).' },
        { key: 'dataflows', label: 'Dataflows', description: 'Jaccard similarity of associated dataflows / ETL pipelines.' },
        { key: 'variables', label: 'Variables', description: 'Jaccard similarity of variable names defined on each page.' },
        { key: 'sets', label: 'Sets', description: 'Jaccard similarity of set/filter-group names used on each page.' },
        { key: 'groups', label: 'Groups', description: 'Jaccard similarity of grouping definitions used on each page.' },
        { key: 'hierarchy', label: 'Hierarchy', description: 'Jaccard similarity of hierarchy definitions used on each page.' }
    ];

    // Fallback defaults (mirrors the previous hardcoded values) used only
    // while the backend value hasn't loaded yet or if the request fails.
    const defaultWeights = {
        datasources: 0.20,
        sourceTables: 0.13,
        connectorType: 0.05,
        worksheets: 0.14,
        chartTypes: 0.05,
        fieldBindings: 0.07,
        columns: 0.12,
        columnTypes: 0.04,
        calculatedFields: 0.08,
        formulaSignatures: 0.04,
        dataflows: 0.04,
        variables: 0.01,
        sets: 0.01,
        groups: 0.01,
        hierarchy: 0.01
    };

    const [weights, setWeights] = useState(defaultWeights);
    const [draftWeights, setDraftWeights] = useState(defaultWeights);
    const [weightsLoading, setWeightsLoading] = useState(true);
    const [weightsError, setWeightsError] = useState(null);
    const [editingWeights, setEditingWeights] = useState(false);
    const [savingWeights, setSavingWeights] = useState(false);

    useEffect(() => {

        let cancelled = false;

        (async () => {
            setWeightsLoading(true);
            setWeightsError(null);
            try {
                const resp = await assessmentApi.getPairwiseWeights();
                const data = resp.data?.data || resp.data || {};
                if (cancelled) return;
                const merged = { ...defaultWeights, ...data };
                setWeights(merged);
                setDraftWeights(merged);
            } catch (err) {
                if (!cancelled) {
                    setWeightsError(err.response?.data?.error || err.message || 'Failed to load weights, showing defaults.');
                }
            } finally {
                if (!cancelled) setWeightsLoading(false);
            }
        })();

        return () => { cancelled = true; };

    }, []);

    const weightsSum = Object.values(draftWeights).reduce((sum, v) => sum + (Number(v) || 0), 0);
    const weightsSumRounded = Math.round(weightsSum * 1000) / 1000;
    const weightsExceedOne = weightsSumRounded > 1;

    const handleWeightChange = (key, value) => {
        const numeric = value === '' ? '' : Number(value);
        setDraftWeights(prev => ({ ...prev, [key]: numeric }));
    };

    const handleStartEditingWeights = () => {
        setDraftWeights(weights);
        setEditingWeights(true);
    };

    const handleCancelEditingWeights = () => {
        setDraftWeights(weights);
        setEditingWeights(false);
    };

    const handleSaveWeights = async () => {

        const normalized = {};
        for (const dim of dimensionMeta) {
            const value = Number(draftWeights[dim.key]);
            if (Number.isNaN(value) || value < 0) {
                toast.error(`${dim.label} weight must be a non-negative number.`);
                return;
            }
            normalized[dim.key] = value;
        }

        const total = Math.round(Object.values(normalized).reduce((sum, v) => sum + v, 0) * 1000) / 1000;

        if (total > 1) {
            toast.error(`Weights sum to ${total}, which exceeds 1. Please reduce one or more values.`);
            return;
        }

        setSavingWeights(true);
        try {
            await assessmentApi.updatePairwiseWeights(normalized);
            setWeights(normalized);
            setDraftWeights(normalized);
            setEditingWeights(false);
            toast.success('Pairwise weights updated.');
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Failed to save weights.');
        } finally {
            setSavingWeights(false);
        }

    };

    const scoreRows = dimensionMeta.map(dim => [
        dim.label,
        `Weight: ${Number(weights[dim.key] ?? 0).toFixed(2)}`,
        dim.description
    ]);

    const tabs = [
        { key: 'tabs', label: 'Tab Guide' },
        { key: 'page-complexity', label: 'Page Complexity' },
        { key: 'pairwise-detail', label: 'Pairwise Detail' },
        { key: 'migration-waves', label: 'Migration Wave Logic' }
    ];

    const tabGuide = [
        ['Overall Status', 'Compares every page to every other page and shows how similar they are, so you can spot duplicate or near-duplicate dashboards.'],
        ['Merge Sets', 'Groups pages that are similar enough to likely be combined into one, to reduce migration work.'],
        ['Migration Waves', 'Sorts pages into Easy, Medium, and Complex groups, so you can migrate the quick wins first.'],
        ['Cards', 'Every card (chart/visualization) found, with its readiness and complexity.'],
        ['Datasets', 'Every dataset found, with its schema, size, and how many cards/dashboards use it.'],
        ['Metrics', 'A quick count of everything discovered - datasets, cards, filters, and more - to gauge migration scope.'],
        ['Columns', 'Every column across all datasets, with its data type.'],
        ['Pages', 'Every dashboard/page found, along with its cards, datasets, and complexity.'],
        ['DataFlows', 'Every Domo DataFlow (data transformation pipeline) found, and what it needs for migration.'],
        ['Beast Mode', 'Every calculated field (Beast Mode) found, with its formula.'],
        ['Data Model', 'Shows how datasets, columns, cards, and pages connect to each other.'],
        ['Users & Groups', 'Every user and group found, and who belongs to which group.'],
        ['Dependencies', 'Shows how apps, pages, cards, and datasets depend on each other, so you know what to check before moving anything.'],
        ['Security & Access', 'Permission and access information Domo returned for your content.'],
        ['Governance', 'Ownership, certification, and tagging information for your content.'],
        ['Issues', 'Anything found that needs attention before or during migration.'],
        ['API Coverage', 'Shows which types of information Domo was able to provide, and where data may be incomplete.']
    ];

    const assessmentScoreFormula =
        'Columns x 1 + Cards x 2 + Datasets x 2 + DataFlows x 3 + Apps x 1 + Pages x 1 + Variables x 1 + Sets x 2 + Groups x 2 + Hierarchy x 2';

    const overallComplexityFormula =
        'Rolls up from each page\'s own complexity (cards, datasets, variables, sets, groups, hierarchy, and pages sharing the same app - see Page Complexity below). If any page is High, overall is High; else if any page is Medium, overall is Medium; otherwise Low.';

    const datasetComplexityFormula =
        'Dataset complexity = Columns + (Cards x 2) + (Beast Modes x 3).  >30 = High, >10 = Medium, otherwise Low.';

    const readinessFormula =
        'Overall backend readiness: any HIGH issue, Blocked; otherwise any issue, Review Required; otherwise Ready.';

    const waveFormula =
        'Wave 3 if blocked/high complexity; Wave 1 if Ready + Low complexity + 0 issues; otherwise Wave 2.';

    const planningEffortFormula =
        'If no explicit effort is supplied: 0.75 + Datasetsx0.5 + Columnsx0.05 + Cardsx0.25 + Pagesx0.35 + Beast Modesx0.35 + Issuesx0.5 hours.';

    const methodologyCards = [
        { title: 'Assessment Score', value: summary.assessmentScore ?? 0, text: assessmentScoreFormula },
        { title: 'Readiness', value: summary.migrationReadiness || 'Ready', text: readinessFormula },
        { title: 'Overall Complexity', value: summary.overallComplexity || 'Low', text: overallComplexityFormula },
        { title: 'Complexity Score / Slab', value: summary.assessmentScore ?? 0, text: 'Overall slab: High when score >100; Medium when score >40; Low when score 40. Dataset-level complexity has its own formula described below.' }
    ];

    return (
        <div
            onMouseDown={event => {
                if (event.target === event.currentTarget) onClose();
            }}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.48)',
                zIndex: 10050,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                boxSizing: 'border-box'
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Domo assessment methodology"
                style={{
                    width: 'min(1180px, 96vw)',
                    maxHeight: '92vh',
                    background: 'var(--card-bg, #fff)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--border-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                            <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#1e447e' }} />
                            <div style={{ fontSize: '18px', fontWeight: 700 }}>Algorithm & Scoring Details</div>
                        </div>
                        <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                            Methodology, scoring rules, readiness, complexity and purpose of every assessment tab.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close assessment information"
                        style={{
                            width: '34px',
                            height: '34px',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            background: 'var(--card-bg, #fff)',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)'
                        }}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)' }}>
                    {tabs.map(item => (
                        <button
                            key={item.key}
                            type="button"
                            onClick={() => setSection(item.key)}
                            style={{
                                border: 'none',
                                borderBottom: section === item.key ? '2px solid #1e447e' : '2px solid transparent',
                                background: section === item.key ? 'var(--hover-bg)' : 'transparent',
                                color: section === item.key ? '#1e447e' : 'var(--text-secondary)',
                                padding: '11px 16px',
                                fontSize: '12px',
                                fontWeight: section === item.key ? 700 : 500,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                <div style={{ overflowY: 'auto', padding: '18px 20px' }}>
                    {section === 'simple' ? (
                        <AssessmentPlainEnglishSummary summary={summary} />
                    ) : section === 'page-complexity' ? (
                        <>
                            
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px', gap: '10px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 700 }}>Page Complexity — Factor Weights &amp; Thresholds</div>

                                    {editingPageComplexity ? (
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <button
                                                type="button"
                                                onClick={handleCancelEditingPageComplexity}
                                                disabled={savingPageComplexity}
                                                style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', fontWeight: 500, background: 'var(--card-bg, #fff)', color: 'var(--text-secondary)', cursor: 'pointer' }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleSavePageComplexity}
                                                disabled={savingPageComplexity}
                                                style={{
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '5px 10px',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    background: '#1e447e',
                                                    color: '#fff',
                                                    cursor: savingPageComplexity ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {savingPageComplexity ? 'Saving...' : 'Save'}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleStartEditingPageComplexity}
                                            style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', fontWeight: 600, background: 'var(--card-bg, #fff)', color: '#1e447e', cursor: 'pointer' }}
                                        >
                                            Edit Complexity
                                        </button>
                                    )}
                                </div>

                                <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '7px', marginBottom: '10px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                        <thead>
                                            <tr>{['Factor', 'Weight', 'How it works'].map(h => <th key={h} style={{ ...tableHeaderStyle, textAlign: 'left' }}>{h}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            {editingPageComplexity ? (
                                                pageComplexityFactorMeta.map(factor => (
                                                    <tr key={factor.key}>
                                                        <td style={tableCellStyle}>{factor.label}</td>
                                                        <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                step={0.1}
                                                                value={pageComplexityDraft.weights?.[factor.key]}
                                                                onChange={e => handlePageComplexityWeightChange(factor.key, e.target.value)}
                                                                style={{
                                                                    width: '70px',
                                                                    padding: '4px 6px',
                                                                    border: '1px solid var(--border-color)',
                                                                    borderRadius: '5px',
                                                                    fontSize: '11px',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--text-primary)'
                                                                }}
                                                            />
                                                        </td>
                                                        <td style={tableCellStyle}>{factor.description}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                pageComplexityFactorMeta.map(factor => (
                                                    <tr key={factor.key}>
                                                        <td style={tableCellStyle}>{factor.label}</td>
                                                        <td style={{ ...tableCellStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                            {Number(activePageComplexityConfig.weights?.[factor.key] ?? 0)}
                                                        </td>
                                                        <td style={tableCellStyle}>{factor.description}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                                    <div style={{ flex: '1 1 160px', border: '1px solid var(--border-color)', borderRadius: '7px', padding: '8px 10px' }}>
                                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '4px' }}>High threshold (score &ge;)</div>
                                        {editingPageComplexity ? (
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={pageComplexityDraft.thresholds?.high}
                                                onChange={e => handlePageComplexityThresholdChange('high', e.target.value)}
                                                style={{
                                                    width: '80px',
                                                    padding: '4px 6px',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '5px',
                                                    fontSize: '12px',
                                                    background: 'var(--input-bg)',
                                                    color: 'var(--text-primary)'
                                                }}
                                            />
                                        ) : (
                                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#d91f28' }}>
                                                {activePageComplexityConfig.thresholds?.high}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ flex: '1 1 160px', border: '1px solid var(--border-color)', borderRadius: '7px', padding: '8px 10px' }}>
                                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Medium threshold (score &ge;)</div>
                                        {editingPageComplexity ? (
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={pageComplexityDraft.thresholds?.medium}
                                                onChange={e => handlePageComplexityThresholdChange('medium', e.target.value)}
                                                style={{
                                                    width: '80px',
                                                    padding: '4px 6px',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '5px',
                                                    fontSize: '12px',
                                                    background: 'var(--input-bg)',
                                                    color: 'var(--text-primary)'
                                                }}
                                            />
                                        ) : (
                                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#b27700' }}>
                                                {activePageComplexityConfig.thresholds?.medium}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ flex: '1 1 160px', border: '1px solid var(--border-color)', borderRadius: '7px', padding: '8px 10px' }}>
                                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{`Low threshold (score <)`}</div>
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'green' }}>
                                            {activePageComplexityConfig.thresholds?.medium}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ fontSize: '11px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                                    If score &ge; High threshold, the page is <strong>High</strong> complexity;
                                    if score &ge; Medium threshold, it's <strong>Medium</strong>; otherwise <strong>Low</strong>.
                                </div>
                            </div>
                        </>
                    ) : section === 'pairwise-detail' ? (
                        <>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px', gap: '10px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 700 }}>Pairwise Detail — Algorithm (MinHash + LSH + Weighted Jaccard)</div>

                                    {!weightsLoading && (
                                        editingWeights ? (
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <span style={{ fontSize: '11px', fontWeight: 600, color: weightsExceedOne ? '#dc2626' : 'var(--text-secondary)' }}>
                                                    Sum: {weightsSumRounded.toFixed(2)} / 1.00
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={handleCancelEditingWeights}
                                                    disabled={savingWeights}
                                                    style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', fontWeight: 500, background: 'var(--card-bg, #fff)', color: 'var(--text-secondary)', cursor: 'pointer' }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSaveWeights}
                                                    disabled={savingWeights || weightsExceedOne}
                                                    style={{
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '5px 10px',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        background: weightsExceedOne ? '#94a3b8' : '#1e447e',
                                                        color: '#fff',
                                                        cursor: weightsExceedOne || savingWeights ? 'not-allowed' : 'pointer'
                                                    }}
                                                >
                                                    {savingWeights ? 'Saving...' : 'Save'}
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleStartEditingWeights}
                                                style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', fontWeight: 600, background: 'var(--card-bg, #fff)', color: '#1e447e', cursor: 'pointer' }}
                                            >
                                                Edit Weights
                                            </button>
                                        )
                                    )}
                                </div>

                                {weightsError && (
                                    <div style={{ fontSize: '11px', color: '#dc2626', marginBottom: '6px' }}>
                                        {weightsError}
                                    </div>
                                )}

                                <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '7px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                        <thead>
                                            <tr>{['Dimension', 'Weight', 'How it works'].map(h => <th key={h} style={{ ...tableHeaderStyle, textAlign: 'left' }}>{h}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            {weightsLoading ? (
                                                <tr>
                                                    <td colSpan={3} style={{ ...tableCellStyle, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                                                        Loading weights...
                                                    </td>
                                                </tr>
                                            ) : editingWeights ? (
                                                dimensionMeta.map(dim => (
                                                    <tr key={dim.key}>
                                                        <td style={tableCellStyle}>{dim.label}</td>
                                                        <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={1}
                                                                step={0.01}
                                                                value={draftWeights[dim.key]}
                                                                onChange={e => handleWeightChange(dim.key, e.target.value)}
                                                                style={{
                                                                    width: '70px',
                                                                    padding: '4px 6px',
                                                                    border: '1px solid var(--border-color)',
                                                                    borderRadius: '5px',
                                                                    fontSize: '11px',
                                                                    background: 'var(--input-bg)',
                                                                    color: 'var(--text-primary)'
                                                                }}
                                                            />
                                                        </td>
                                                        <td style={tableCellStyle}>{dim.description}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                scoreRows.map(row => (
                                                    <tr key={row[0]}>
                                                        <td style={tableCellStyle}>{row[0]}</td>
                                                        <td style={{ ...tableCellStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>{row[1]}</td>
                                                        <td style={tableCellStyle}>{row[2]}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {!weightsLoading && !editingWeights && (
                                    <div style={{ marginTop: '6px', fontSize: '11px', color: weightsExceedOne ? '#dc2626' : 'var(--text-secondary)' }}>
                                        Current sum of weights: {Math.round(Object.values(weights).reduce((sum, v) => sum + (Number(v) || 0), 0) * 1000) / 1000} / 1.00
                                    </div>
                                )}

                                <div style={{ marginTop: '7px', fontSize: '11px', lineHeight: 1.6 }}>
                                    <strong>Recommendation:</strong> score >= 85 = MERGE, score >= 70 = REVIEW, below 70 = SEPARATE.<br/>
                                    <strong>Algorithm:</strong> MinHash (128 hash functions) + LSH (16 bands x 8 rows) for candidate pair discovery, then Weighted Jaccard across the metadata dimensions. Score is capped at 0-100 for display.<br/>
                                    <strong>Clustering:</strong> MERGE pairs are grouped into clusters using Union-Find (disjoint set). Transitive merges are automatic (A+B merge, B+C merge = A+B+C cluster).<br/>
                                    <strong>Weights:</strong> loaded dynamically from the backend and must sum to 1.00 or less.
                                </div>

                                <div style={{ marginTop: '10px', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '7px', background: 'var(--hover-bg)', fontSize: '11px', lineHeight: 1.6 }}>
                                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>Score breakdown &amp; "available weight"</div>
                                    Each dimension only counts toward a pair's score if at least one of the two pages has data for it (e.g. a page with no
                                    Filters at all doesn't get penalized for a "0% Filters match"). The sum of weights across dimensions that actually had
                                    data on either page is called <strong>available weight</strong>. The final score is <code>(sum of dimension scores / available weight) x 100</code>,
                                    and each dimension's contribution ("points") shown in the Page Comparison Details -&gt; Score Breakdown tab is
                                    <code> (dimension score / available weight) x 100</code> - the same divisor used for the overall score, so all dimensions'
                                    points add up to the final score. This is why a single strongly-matching dimension can contribute more points than its raw
                                    weight suggests: it's a bigger share of a smaller comparable pool, not the full 100-point weight total. You can see the exact
                                    available weight used for any pair (e.g. "48 / 100") at the top of that Score Breakdown tab.
                                </div>

                                {/* <div style={{ marginTop: '14px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '7px' }}>Actual scoring weights (full 100-point pool)</div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px', lineHeight: 1.6 }}>
                                        These are the exact weights the scoring engine uses when comparing two pages (this is what the Score Breakdown panel
                                        on any pair - e.g. "How the score of 86 was calculated" - is built from). They're a separate, fixed set of numbers from
                                        the editable table above, which is a config surface not currently wired into scoring.
                                    </div>
                                    {(() => {
                                        const scoringDimensionKeys = Object.keys(PAIRWISE_DIMENSION_WEIGHTS);
                                        const totalPoints = scoringDimensionKeys.reduce((sum, key) => sum + (Number(PAIRWISE_DIMENSION_WEIGHTS[key]) || 0), 0);

                                        return (
                                            <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '7px' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                                    <thead>
                                                        <tr>{['Dimension', 'Points (of 100)'].map(h => <th key={h} style={{ ...tableHeaderStyle, textAlign: 'left' }}>{h}</th>)}</tr>
                                                    </thead>
                                                    <tbody>
                                                        {scoringDimensionKeys.map(key => (
                                                            <tr key={key}>
                                                                <td style={tableCellStyle}>{PAIRWISE_DIMENSION_LABELS[key] || key}</td>
                                                                <td style={{ ...tableCellStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>{Number(PAIRWISE_DIMENSION_WEIGHTS[key]) || 0}</td>
                                                            </tr>
                                                        ))}
                                                        <tr>
                                                            <td style={{ ...tableCellStyle, fontWeight: 700 }}>Total</td>
                                                            <td style={{ ...tableCellStyle, fontWeight: 700, color: totalPoints === 100 ? '#078653' : '#d91f28' }}>
                                                                {totalPoints}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        );
                                    })()}
                                </div> */}
                            </div>
                        </>
                    ) : section === 'migration-waves' ? (
                        <>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '7px' }}>Migration wave logic</div>
                                <div style={{ padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '7px', fontSize: '11px', lineHeight: 1.6 }}>
                                    {/* {waveFormula}
                                    <br /><br /> */}
                                    <strong>Planning effort:</strong> {planningEffortFormula}
                                    <br />
                                    The effort value is a planning estimate and does not replace the backend assessment score.
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '4px' }}>Assessment tab guide</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                                Each tab answers a different migration question. The inventory tabs use the metadata discovered from Domo; the analysis tabs derive recommendations from that discovered metadata.
                            </div>
                            <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...tableHeaderStyle, textAlign: 'left', width: '180px' }}>TAB</th>
                                            <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>WHAT IT DOES</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tabGuide.map(([name, description]) => (
                                            <tr key={name}>
                                                <td style={{ ...tableCellStyle, fontWeight: 700, whiteSpace: 'nowrap' }}>{name}</td>
                                                <td style={tableCellStyle}>{description}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}


/*
|--------------------------------------------------------------------------
| PAGE DETAILS MODAL
|--------------------------------------------------------------------------
*/

function PageDetailsModal({
    page,
    datasets = [],
    cards = [],
    beastmodes = [],
    onClose
}) {
    console.log("page", page)
    const pageName =
        page?.pageName ||
        page?.name ||
        page?.title ||
        'Page';


    const pageId =
        page?.pageId ||
        page?.id ||
        '-';


    const rows =
        buildPageDetailsRows(
            page,
            datasets,
            cards,
            beastmodes
        );


    return (

        <div
            onMouseDown={event => {

                if (
                    event.target === event.currentTarget
                ) {
                    onClose();
                }

            }}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.45)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '30px',
                boxSizing: 'border-box'
            }}
        >

            <div
                style={{
                    width: 'min(1250px, 96vw)',
                    maxHeight: '90vh',
                    background:
                        'var(--card-bg, #fff)',
                    border:
                        '1px solid var(--border-color)',
                    borderRadius: '12px',
                    boxShadow:
                        '0 20px 60px rgba(0,0,0,0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >

                {/* MODAL HEADER */}

                <div
                    style={{
                        padding: '18px 20px',
                        borderBottom:
                            '1px solid var(--border-color)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >

                    <div>

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '9px'
                            }}
                        >

                            <div
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    background: '#eef3ff',
                                    color: '#1e447e',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >

                                <FontAwesomeIcon
                                    icon={faTable}
                                />

                            </div>

                            <div>

                                <div
                                    style={{
                                        fontSize: '16px',
                                        fontWeight: 600
                                    }}
                                >
                                    {pageName}
                                </div>

                                <div
                                    style={{
                                        fontSize: '11px',
                                        color:
                                            'var(--text-secondary)',
                                        marginTop: '3px'
                                    }}
                                >
                                    Page ID: {pageId}
                                </div>

                            </div>

                        </div>

                    </div>


                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            width: '32px',
                            height: '32px',
                            border: 'none',
                            borderRadius: '6px',
                            background:
                                'var(--hover-bg)',
                            color:
                                'var(--text-secondary)',
                            cursor: 'pointer'
                        }}
                    >

                        <FontAwesomeIcon
                            icon={faXmark}
                        />

                    </button>

                </div>


                {/* SUMMARY */}

                <div
                    style={{
                        display: 'flex',
                        gap: '10px',
                        padding: '14px 20px',
                        borderBottom:
                            '1px solid var(--border-color)',
                        flexWrap: 'wrap'
                    }}
                >

                    <DetailsSummary
                        icon={faChartBar}
                        label="Cards"
                        value={
                            new Set(
                                rows.map(
                                    row =>
                                        row.cardKey
                                )
                            ).size
                        }
                    />

                    <DetailsSummary
                        icon={faDatabase}
                        label="Datasets"
                        value={
                            new Set(
                                rows
                                    .map(
                                        row =>
                                            row.datasetKey
                                    )
                                    .filter(Boolean)
                            ).size
                        }
                    />

                    <DetailsSummary
                        icon={faColumns}
                        label="Columns"
                        value={
                            rows.filter(
                                row =>
                                    row.columnName
                            ).length
                        }
                    />

                    <DetailsSummary
                        icon={faCalculator}
                        label="Calculated Fields"
                        value={
                            rows.filter(
                                row =>
                                    row.calculatedField
                            ).length
                        }
                    />

                    <DetailsSummary
                        icon={faMagnifyingGlassChart}
                        label="Filters"
                        value={
                            rows.reduce(
                                (total, row) =>
                                    total + (row.cardFilters?.length || 0),
                                0
                            )
                        }
                    />

                    <DetailsSummary
                        icon={faMagnifyingGlassChart}
                        label="Variables"
                        value={
                            rows.reduce(
                                (total, row) =>
                                    total + (row.cardVariables?.length || 0),
                                0
                            )
                        }
                    />

                    <DetailsSummary
                        icon={faMagnifyingGlassChart}
                        label="Sets"
                        value={
                            rows.reduce(
                                (total, row) =>
                                    total + (row.cardSets?.length || 0),
                                0
                            )
                        }
                    />

                    <DetailsSummary
                        icon={faMagnifyingGlassChart}
                        label="Groups"
                        value={
                            rows.reduce(
                                (total, row) =>
                                    total + (row.cardGroups?.length || 0),
                                0
                            )
                        }
                    />

                    <DetailsSummary
                        icon={faMagnifyingGlassChart}
                        label="Hierarchy"
                        value={
                            rows.reduce(
                                (total, row) =>
                                    total + (row.cardHierarchy?.length || 0),
                                0
                            )
                        }
                    />

                </div>


                {/* TABLE */}

                <div
                    style={{
                        overflow: 'auto',
                        flex: 1
                    }}
                >

                    {rows.length === 0 ? (

                        <EmptyState
                            message="No page dependency details found."
                        />

                    ) : (

                        <PageDetailsTable
                            rows={rows}
                        />

                    )}

                </div>


                {/* FOOTER */}

                <div
                    style={{
                        padding: '12px 20px',
                        borderTop:
                            '1px solid var(--border-color)',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}
                >

                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            border:
                                '1px solid var(--border-color)',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            background:
                                'var(--card-bg, #fff)',
                            color:
                                'var(--text-primary)',
                            fontSize: '12px',
                            cursor: 'pointer'
                        }}
                    >
                        Close
                    </button>

                </div>

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Page Details Summary
|--------------------------------------------------------------------------
*/

function DetailsSummary({
    icon,
    label,
    value
}) {

    return (

        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '7px 11px',
                border:
                    '1px solid var(--border-color)',
                borderRadius: '7px'
            }}
        >

            <FontAwesomeIcon
                icon={icon}
                style={{
                    color: '#1e447e',
                    fontSize: '12px'
                }}
            />

            <span
                style={{
                    fontSize: '11px',
                    color:
                        'var(--text-secondary)'
                }}
            >
                {label}
            </span>

            <strong
                style={{
                    fontSize: '12px'
                }}
            >
                {value}
            </strong>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Build Page Details
|--------------------------------------------------------------------------
|
| Creates:
|
| Page
|   Card
|     Dataset
|       Column
|       Calculated Field
|
|--------------------------------------------------------------------------
*/

function buildPageDetailsRows(
    page,
    datasets = [],
    allCards = [],
    beastmodes = []
) {

    const pageCards =
        Array.isArray(page?.cards)
            ? page.cards
            : [];


    /*
    |--------------------------------------------------------------------------
    | If page.cards isn't populated, find cards from global cards collection.
    |--------------------------------------------------------------------------
    */

    let cardsForPage =
        pageCards;


    if (
        cardsForPage.length === 0 &&
        Array.isArray(allCards)
    ) {

        const pageId =
            String(
                page?.pageId ||
                page?.id ||
                ''
            );


        cardsForPage =
            allCards.filter(card => {

                const cardPageId =
                    String(
                        card?.pageId ||
                        card?.page?.id ||
                        ''
                    );

                return (
                    cardPageId &&
                    pageId &&
                    cardPageId === pageId
                );

            });

    }


    const rows = [];


    /*
    |--------------------------------------------------------------------------
    | No cards
    |--------------------------------------------------------------------------
    */

    if (cardsForPage.length === 0) {

        return [{
            pageName:
                page?.pageName ||
                page?.name ||
                page?.title ||
                '-',

            pageKey:
                page?.id ||
                page?.pageId ||
                '-',

            cardName: '-',
            cardKey: null,
            cardId: null,

            datasetName: '-',
            datasetKey: null,

            columnName: null,

            calculatedField: null,

            cardCalculatedFields: [],
            cardFilters: [],
            cardVariables: [],
            cardSets: [],
            cardGroups: [],
            cardHierarchy: [],

            isFirstPage: true,
            isFirstCard: true,
            isFirstDataset: true
        }];

    }


    /*
    |--------------------------------------------------------------------------
    | Process Cards
    |--------------------------------------------------------------------------
    */

    cardsForPage.forEach(
        (
            card,
            cardIndex
        ) => {

            const cardName =
                card?.cardName ||
                card?.name ||
                card?.title ||
                '-';


            const cardId =
                card?.cardId ||
                card?.id ||
                card?.urn ||
                `${cardIndex}`;


            /*
            |--------------------------------------------------------------------------
            | Find datasets used by this card
            |--------------------------------------------------------------------------
            */

            const datasetIds =
                getCardDatasetIds(
                    card
                );


            /*
            |--------------------------------------------------------------------------
            | If dataset IDs are unavailable, use dataset name directly
            |--------------------------------------------------------------------------
            */

            if (datasetIds.length === 0) {

                const datasetName =
                    card?.datasetName ||
                    card?.dataSetName ||
                    card?.datasourceName ||
                    card?.dataset?.name ||
                    '-';


                const datasetId =
                    card?.dataSetId ||
                    card?.datasetId ||
                    card?.datasourceId ||
                    card?.dataSourceId ||
                    null;


                const dataset =
                    findDataset(
                        datasets,
                        datasetId,
                        datasetName
                    );


                const columns =
                    getCardColumns(
                        card,
                        dataset
                    );


                const calculatedFields =
                    card?.calc || [];

                const cardFilters = Array.from(
                    new Map(
                        [
                            ...(card?.chartBody?.filters || []),
                            ...(card?.chartBody?.filters || [])
                        ].map(filter => [
                            JSON.stringify(filter),
                            filter
                        ])
                    ).values()
                );

                const cardVariables = card?.variables || [];
                const cardSets = card?.sets || [];
                const cardGroups = card?.groups || [];
                const cardHierarchy = card?.hierarchy || [];

                addDatasetRows(
                    rows,
                    page,
                    card,
                    {
                        cardName,
                        cardId,
                        dataset,
                        datasetId,
                        datasetName,
                        columns,
                        calculatedFields,
                        cardFilters,
                        cardVariables,
                        cardSets,
                        cardGroups,
                        cardHierarchy,
                        cardIndex,
                        datasetIndex: 0
                    }
                );

                return;
            }


            /*
            |--------------------------------------------------------------------------
            | Multiple datasets
            |--------------------------------------------------------------------------
            */

            datasetIds.forEach(
                (
                    datasetId,
                    datasetIndex
                ) => {

                    const dataset =
                        findDataset(
                            datasets,
                            datasetId
                        );


                    const datasetName =
                        dataset?.datasetName ||
                        dataset?.name ||
                        dataset?.title ||
                        card?.datasetName ||
                        card?.dataSetName ||
                        String(datasetId);


                    const columns =
                        getCardColumns(
                            card,
                            dataset,
                            datasetId
                        );


                    const calculatedFields =
                        card?.calc || [];

                    const cardFilters = Array.from(
                        new Map(
                            [
                                ...(card?.chartBody?.filters || []),
                                ...(card?.chartBody?.filters || [])
                            ].map(filter => [
                                JSON.stringify(filter),
                                filter
                            ])
                        ).values()
                    );

                    const cardVariables = card?.variables || [];
                    const cardSets = card?.sets || [];
                    const cardGroups = card?.groups || [];
                    const cardHierarchy = card?.hierarchy || [];

                    addDatasetRows(
                        rows,
                        page,
                        card,
                        {
                            cardName,
                            cardId,
                            dataset,
                            datasetId,
                            datasetName,
                            columns,
                            calculatedFields,
                            cardFilters,
                            cardVariables,
                            cardSets,
                            cardGroups,
                            cardHierarchy,
                            cardIndex,
                            datasetIndex
                        }
                    );

                }
            );

        }
    );


    return rows;
}


/*
|--------------------------------------------------------------------------
| Add Dataset Rows
|--------------------------------------------------------------------------
*/

function addDatasetRows(
    rows,
    page,
    card,
    options
) {

    const {
        cardName,
        cardId,
        dataset,
        datasetId,
        datasetName,
        columns = [],
        calculatedFields = [],
        cardFilters = [],
        cardVariables = [],
        cardSets = [],
        cardGroups = [],
        cardHierarchy = [],
        cardIndex,
        datasetIndex
    } = options;


    const pageName =
        page?.pageName ||
        page?.name ||
        page?.title ||
        '-';


    const pageId =
        page?.pageId ||
        page?.id ||
        '-';


    /*
    |--------------------------------------------------------------------------
    | Make sure calculated fields are also represented when there are
    | no physical columns.
    |--------------------------------------------------------------------------
    */

    const maxRows =
        Math.max(
            columns.length,
            calculatedFields.length,
            1
        );


    for (
        let index = 0;
        index < maxRows;
        index++
    ) {

        rows.push({

            pageName,

            pageKey:
                String(pageId),

            cardName:
                index === 0
                    ? cardName
                    : '',

            cardKey:
                `${pageId}_${cardId}`,
            
            cardId: cardId,

            datasetName:
                index === 0
                    ? datasetName
                    : '',

            datasetKey:
                datasetId
                    ? String(datasetId)
                    : (
                        datasetName
                            ? String(datasetName)
                            : null
                    ),

            columnName:
                columns[index]?.name ||
                columns[index]?.column ||
                columns[index]?.columnName ||
                columns[index]?.alias ||
                null,

            calculatedField:
                calculatedFields[index]?.name ||
                calculatedFields[index]?.formula ||
                calculatedFields[index]?.expression ||
                null,

            calculatedFormula:
                calculatedFields[index]?.formula ||
                calculatedFields[index]?.expression ||
                calculatedFields[index]?.sql ||
                null,

            cardCalculatedFields:
                index === 0
                    ? calculatedFields
                    : [],

            cardFilters:
                index === 0
                    ? cardFilters
                    : [],

            cardVariables:
                index === 0
                    ? cardVariables
                    : [],

            cardSets:
                index === 0
                    ? cardSets
                    : [],

            cardGroups:
                index === 0
                    ? cardGroups
                    : [],

            cardHierarchy:
                index === 0
                    ? cardHierarchy
                    : [],

            cardIndex,

            datasetIndex

        });

    }
}


/*
|--------------------------------------------------------------------------
| Get Card Dataset IDs
|--------------------------------------------------------------------------
*/

function getCardDatasetIds(card) {

    const ids = [];


    const add =
        value => {

            if (
                value !== null &&
                value !== undefined &&
                value !== ''
            ) {

                ids.push(
                    String(value)
                );

            }

        };


    add(card?.dataSetId);
    add(card?.datasetId);
    add(card?.datasourceId);
    add(card?.dataSourceId);


    /*
    |--------------------------------------------------------------------------
    | Dataset object
    |--------------------------------------------------------------------------
    */

    add(
        card?.dataset?.id
    );

    add(
        card?.dataSet?.id
    );


    /*
    |--------------------------------------------------------------------------
    | Multiple datasets
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            card?.datasets
        )
    ) {

        card.datasets.forEach(
            dataset => {

                add(
                    dataset?.id ||
                    dataset?.datasetId ||
                    dataset?.dataSetId
                );

            }
        );

    }


    if (
        Array.isArray(
            card?.dataSources
        )
    ) {

        card.dataSources.forEach(
            source => {

                add(
                    source?.id ||
                    source?.datasetId ||
                    source?.dataSetId
                );

            }
        );

    }


    return [
        ...new Set(ids)
    ];
}


/*
|--------------------------------------------------------------------------
| Find Dataset
|--------------------------------------------------------------------------
*/

function findDataset(
    datasets,
    datasetId,
    datasetName
) {

    if (!Array.isArray(datasets)) {
        return null;
    }


    if (datasetId) {

        const found =
            datasets.find(
                dataset =>
                    String(
                        dataset?.datasetId ||
                        dataset?.id ||
                        dataset?.dataSetId
                    ) ===
                    String(datasetId)
            );


        if (found) {
            return found;
        }

    }


    if (datasetName) {

        const found =
            datasets.find(
                dataset =>
                    String(
                        dataset?.datasetName ||
                        dataset?.name ||
                        dataset?.title ||
                        ''
                    ).toLowerCase() ===
                    String(datasetName).toLowerCase()
            );


        if (found) {
            return found;
        }

    }


    return null;
}


/*
|--------------------------------------------------------------------------
| Get Card Columns
|--------------------------------------------------------------------------
*/

function getCardColumns(
    card,
    dataset,
    datasetId
) {

    const result = [];


    /*
    |--------------------------------------------------------------------------
    | Dataset schema / columns
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            dataset?.schema
        )
    ) {

        dataset.schema.forEach(
            column => {

                result.push(
                    normalizeColumn(
                        column
                    )
                );

            }
        );

    }


    if (
        Array.isArray(
            dataset?.columns
        )
    ) {

        dataset.columns.forEach(
            column => {

                result.push(
                    normalizeColumn(
                        column
                    )
                );

            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Card columns
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            card?.columns
        )
    ) {

        card.columns.forEach(
            column => {

                result.push(
                    normalizeColumn(
                        column
                    )
                );

            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | chartBody.columns
    |--------------------------------------------------------------------------
    */

    const chartColumns =
        card?.chartBody?.columns;


    if (
        Array.isArray(chartColumns)
    ) {

        chartColumns.forEach(
            column => {

                result.push(
                    normalizeColumn(
                        column
                    )
                );

            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | chartBody.dataColumns
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            card?.chartBody?.dataColumns
        )
    ) {

        card.chartBody.dataColumns.forEach(
            column => {

                result.push(
                    normalizeColumn(
                        column
                    )
                );

            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | card.columns used for a specific dataset
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            card?.chartBody?.column
        )
    ) {

        card.chartBody.column.forEach(
            column => {

                result.push(
                    normalizeColumn(
                        column
                    )
                );

            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Remove duplicates
    |--------------------------------------------------------------------------
    */

    const unique = [];


    result.forEach(
        column => {

            if (
                !column?.name
            ) {
                return;
            }


            /*
            |------------------------------------------------------------------
            | Ignore calculated fields here.
            |------------------------------------------------------------------
            */

            if (
                column.isCalculated ||
                column.calculated ||
                column.type === 'beastmode'
            ) {

                return;
            }


            const key =
                String(
                    column.name
                ).toLowerCase();


            if (
                !unique.some(
                    item =>
                        String(
                            item.name
                        ).toLowerCase() ===
                        key
                )
            ) {

                unique.push(
                    column
                );

            }

        }
    );


    return unique;
}


/*
|--------------------------------------------------------------------------
| Normalize Column
|--------------------------------------------------------------------------
*/

function normalizeColumn(column) {

    if (
        typeof column === 'string'
    ) {

        return {
            name: column
        };

    }


    if (!column) {
        return null;
    }


    return {

        name:
            column.name ||
            column.column ||
            column.columnName ||
            column.alias ||
            column.field ||
            column.dataField ||
            null,

        isCalculated:
            column.isCalculated ||
            column.calculated ||
            column.type === 'beastmode'

    };
}


/*
|--------------------------------------------------------------------------
| Get Calculated Fields
|--------------------------------------------------------------------------
*/

function getCalculatedFields(
    card,
    beastmodes = [],
    datasetId
) {

    const fields = [];


    /*
    |--------------------------------------------------------------------------
    | Card calculatedFields
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            card?.calculatedFields
        )
    ) {

        card.calculatedFields.forEach(
            field => {

                fields.push(
                    normalizeCalculatedField(
                        field
                    )
                );

            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Card calculated fields alternative names
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            card?.calculated_fields
        )
    ) {

        card.calculated_fields.forEach(
            field => {

                fields.push(
                    normalizeCalculatedField(
                        field
                    )
                );

            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Chart body calculated fields
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            card?.chartBody?.calculatedFields
        )
    ) {

        card.chartBody.calculatedFields.forEach(
            field => {

                fields.push(
                    normalizeCalculatedField(
                        field
                    )
                );

            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Beast Modes associated with dataset
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(beastmodes) &&
        datasetId
    ) {

        beastmodes
            .filter(
                beastmode => {

                    const beastDatasetId =
                        beastmode?.datasetId ||
                        beastmode?.dataSetId ||
                        beastmode?.datasourceId;


                    return (
                        beastDatasetId &&
                        String(beastDatasetId) ===
                        String(datasetId)
                    );

                }
            )
            .forEach(
                beastmode => {

                    fields.push({

                        name:
                            beastmode?.beastmodeName ||
                            beastmode?.name ||
                            beastmode?.title ||
                            'Calculated Field',

                        formula:
                            beastmode?.expression ||
                            beastmode?.formula ||
                            beastmode?.sql ||
                            ''

                    });

                }
            );

    }


    /*
    |--------------------------------------------------------------------------
    | Remove duplicate fields
    |--------------------------------------------------------------------------
    */

    const unique = [];


    fields.forEach(
        field => {

            if (!field) {
                return;
            }


            const key =
                `${field.name || ''}|${field.formula || ''}`
                    .toLowerCase();


            if (
                !unique.some(
                    item =>
                        `${item.name || ''}|${item.formula || ''}`
                            .toLowerCase() ===
                        key
                )
            ) {

                unique.push(
                    field
                );

            }

        }
    );


    return unique;
}


/*
|--------------------------------------------------------------------------
| Normalize Calculated Field
|--------------------------------------------------------------------------
*/

function normalizeCalculatedField(field) {

    if (
        typeof field === 'string'
    ) {

        return {

            name:
                'Calculated Field',

            formula:
                field

        };

    }


    return {

        name:
            field?.name ||
            field?.fieldName ||
            field?.beastmodeName ||
            field?.title ||
            'Calculated Field',

        formula:
            field?.formula ||
            field?.expression ||
            field?.sql ||
            field?.value ||
            ''

    };
}


/*
|--------------------------------------------------------------------------
| Page Details Table
|--------------------------------------------------------------------------
*/

function PageDetailsTable({
    rows
}) {

    const [filterRow, setFilterRow] = useState(null);
    const [variableRow, setVariableRow] = useState(null);
    const [setsRow, setSetsRow] = useState(null);
    const [groupsRow, setGroupsRow] = useState(null);
    const [hierarchyRow, setHierarchyRow] = useState(null);
    const [calcRow, setCalcRow] = useState(null);

    return (

        <>

        <table
            style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '12px'
            }}
        >

            <thead>

                <tr>

                    <th style={detailsHeaderStyle}>
                        Page
                    </th>

                    <th style={detailsHeaderStyle}>
                        Card
                    </th>

                    <th style={detailsHeaderStyle}>
                        Dataset
                    </th>

                    <th style={detailsHeaderStyle}>
                        Column
                    </th>

                    <th style={detailsHeaderStyle}>
                        Calculative Field
                    </th>

                    <th style={detailsHeaderStyle}>
                        Filters
                    </th>

                    <th style={detailsHeaderStyle}>
                        Variables
                    </th>

                    <th style={detailsHeaderStyle}>
                        Sets
                    </th>

                    <th style={detailsHeaderStyle}>
                        Groups
                    </th>

                    <th style={detailsHeaderStyle}>
                        Hierarchy
                    </th>

                </tr>

            </thead>


            <tbody>

                {rows.map(
                    (
                        row,
                        index
                    ) => (
                        
                        <tr
                            key={index}
                            style={{
                                borderBottom:
                                    '1px solid var(--border-color)'
                            }}
                        >
                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.pageName || ''}

                            </td>


                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.cardName || ''}

                            </td>


                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.datasetName || ''}

                            </td>


                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.columnName ? (

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '7px'
                                        }}
                                    >

                                        <FontAwesomeIcon
                                            icon={faColumns}
                                            style={{
                                                color:
                                                    '#1e447e',
                                                fontSize:
                                                    '11px'
                                            }}
                                        />

                                        <span>
                                            {row.columnName}
                                        </span>

                                    </div>

                                ) : ''}

                            </td>


                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.cardCalculatedFields?.length ? (

                                    <button
                                        type="button"
                                        title="View calculated field values"
                                        onClick={() =>
                                            setCalcRow({
                                                cardName: row.cardName,
                                                calc: row.cardCalculatedFields
                                            })
                                        }
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            padding: '2px 6px',
                                            color: '#1e447e',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            fontSize: '11px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {row.cardCalculatedFields.length}
                                        <FontAwesomeIcon icon={faCircleInfo} />
                                    </button>

                                ) : ''}

                            </td>

                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.cardFilters?.length ? (

                                    <button
                                        type="button"
                                        title="View filter values"
                                        onClick={() =>
                                            setFilterRow({
                                                cardName: row.cardName,
                                                filters: row.cardFilters
                                            })
                                        }
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            padding: '2px 6px',
                                            color: '#1e447e',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            fontSize: '11px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {row.cardFilters.length}
                                        <FontAwesomeIcon icon={faCircleInfo} />
                                    </button>

                                ) : ''}

                            </td>

                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.cardVariables?.length ? (

                                    <button
                                        type="button"
                                        title="View variable values"
                                        onClick={() =>
                                            setVariableRow({
                                                cardName: row.cardName,
                                                variables: row.cardVariables
                                            })
                                        }
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            padding: '2px 6px',
                                            color: '#1e447e',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            fontSize: '11px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {row.cardVariables.length}
                                        <FontAwesomeIcon icon={faCircleInfo} />
                                    </button>

                                ) : ''}

                            </td>

                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.cardSets?.length ? (

                                    <button
                                        type="button"
                                        title="View set values"
                                        onClick={() =>
                                            setSetsRow({
                                                cardName: row.cardName,
                                                sets: row.cardSets
                                            })
                                        }
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            padding: '2px 6px',
                                            color: '#1e447e',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            fontSize: '11px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {row.cardSets.length}
                                        <FontAwesomeIcon icon={faCircleInfo} />
                                    </button>

                                ) : ''}

                            </td>

                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.cardGroups?.length ? (

                                    <button
                                        type="button"
                                        title="View group values"
                                        onClick={() =>
                                            setGroupsRow({
                                                cardName: row.cardName,
                                                groups: row.cardGroups
                                            })
                                        }
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            padding: '2px 6px',
                                            color: '#1e447e',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            fontSize: '11px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {row.cardGroups.length}
                                        <FontAwesomeIcon icon={faCircleInfo} />
                                    </button>

                                ) : ''}

                            </td>

                            <td
                                style={
                                    detailsCellStyle
                                }
                            >

                                {row.cardHierarchy?.length ? (

                                    <button
                                        type="button"
                                        title="View hierarchy values"
                                        onClick={() =>
                                            setHierarchyRow({
                                                cardName: row.cardName,
                                                hierarchy: row.cardHierarchy
                                            })
                                        }
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            padding: '2px 6px',
                                            color: '#1e447e',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            fontSize: '11px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {row.cardHierarchy.length}
                                        <FontAwesomeIcon icon={faCircleInfo} />
                                    </button>

                                ) : ''}

                            </td>

                        </tr>

                    )
                )}

            </tbody>

        </table>

        {filterRow && (
            <CardFiltersModal
                cardName={filterRow.cardName}
                filters={filterRow.filters}
                onClose={() => setFilterRow(null)}
            />
        )}

        {variableRow && (
            <CardVariablesModal
                cardName={variableRow.cardName}
                filters={variableRow.variables}
                onClose={() => setVariableRow(null)}
            />
        )}

        {setsRow && (
            <CardSetsModal
                cardName={setsRow.cardName}
                filters={setsRow.sets}
                onClose={() => setSetsRow(null)}
            />
        )}

        {groupsRow && (
            <CardGroupsModal
                cardName={groupsRow.cardName}
                filters={groupsRow.groups}
                onClose={() => setGroupsRow(null)}
            />
        )}

        {hierarchyRow && (
            <CardHierarchyModal
                cardName={hierarchyRow.cardName}
                filters={hierarchyRow.hierarchy}
                onClose={() => setHierarchyRow(null)}
            />
        )}

        {calcRow && (
            <CardCalcModal
                cardName={calcRow.cardName}
                filters={calcRow.calc}
                onClose={() => setCalcRow(null)}
            />
        )}

        </>
    );
}


/*
|--------------------------------------------------------------------------
| Details Styles
|--------------------------------------------------------------------------
*/

const detailsHeaderStyle = {

    textAlign: 'left',

    padding: '12px 14px',

    borderBottom:
        '1px solid var(--border-color)',

    color:
        'var(--text-secondary)',

    fontWeight: 600,

    whiteSpace: 'nowrap',

    background:
        '#d1d6e4',

    position: 'sticky',

    top: 0,

    zIndex: 1
};


const detailsCellStyle = {

    padding: '11px 14px',

    color:
        'var(--text-primary)',

    verticalAlign: 'top',

    minWidth: '150px'
};


/*
|--------------------------------------------------------------------------
| Readiness Card
|--------------------------------------------------------------------------
*/

function ReadinessCard({
    readinessCounts,
    overallReadiness,
    resourceCount
}) {

    const total =
        readinessCounts.ready +
        readinessCounts.review +
        readinessCounts.blocked;

    return (

        <div
            style={{
                background:
                    'var(--card-bg, #fff)',
                border:
                    '1px solid var(--border-color)',
                borderRadius: '10px',
                padding: '20px'
            }}
        >

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >

                <div>

                    <div
                        style={{
                            fontSize: '12px',
                            color:
                                'var(--text-secondary)',
                            marginBottom: '8px'
                        }}
                    >
                        Migration Readiness
                    </div>

                    {/* <div
                        style={{
                            fontSize: '26px',
                            fontWeight: 700,
                            color:
                                getStatusColor(
                                    overallReadiness
                                )
                        }}
                    >
                        {overallReadiness}
                    </div> */}
{/* 
                    <div
                        style={{
                            fontSize: '11px',
                            color:
                                'var(--text-secondary)',
                            marginTop: '6px'
                        }}
                    >
                        {resourceCount} resources analyzed
                    </div> */}

                </div>

{/* 
                <div
                    style={{
                        width: '46px',
                        height: '46px',
                        borderRadius: '10px',
                        background:
                            getStatusBackground(
                                overallReadiness
                            ),
                        color:
                            getStatusColor(
                                overallReadiness
                            ),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >

                    <FontAwesomeIcon
                        icon={
                            overallReadiness === 'Blocked'
                                ? faCircleXmark
                                : overallReadiness ===
                                    'Review Required'
                                    ? faTriangleExclamation
                                    : faCircleCheck
                        }
                    />

                </div> */}

            </div>


            {/* <div
                style={{
                    // display: 'flex',
                    gap: '18px',
                    marginTop: '0px'
                }}
            >

                <MetricCount
                    label="Ready"
                    value={readinessCounts.ready}
                    status="ready"
                />

                <MetricCount
                    label="Review"
                    value={readinessCounts.review}
                    status="review"
                />

                <MetricCount
                    label="Blocked"
                    value={readinessCounts.blocked}
                    status="blocked"
                />

            </div> */}

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '7px'
                }}
            >

                <ComplexityRow
                    label="Ready"
                    value={readinessCounts.ready}
                    status="low"
                />

                <ComplexityRow
                    label="Review"
                    value={readinessCounts.review}
                    status="medium"
                />

                <ComplexityRow
                    label="Blocked"
                    value={readinessCounts.blocked}
                    status="high"
                />

            </div>


        </div>
    );
}

function MetricCount({
    label,
    value,
    status
}) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
            }}
        >
            <span
                style={{
                    fontSize: '11px',
                    color: 'var(--text-secondary)'
                }}
            >
                {label}:
            </span>

            <strong
                style={{
                    fontSize: '14px',
                    color: getStatusColor(status)
                }}
            >
                {value || 0}
            </strong>
        </div>
    );
}

/*
|--------------------------------------------------------------------------
| Overall Status Tab
|--------------------------------------------------------------------------
|
| Page-to-page comparison.
|
| Comparison is based on:
|   1. Worksheets / Cards
|   2. Datasources / Datasets
|   3. Columns
|   4. Column Types
|   5. Calculated Fields / Beast Modes
|
| Only PAGES are shown as resources in the matrix.
|--------------------------------------------------------------------------
*/

function OverallStatusTab({
    pages = [],
    cards = [],
    datasets = [],
    beastmodes = []
}) {

    const [viewMode, setViewMode] = useState('matrix');

    const pageResources = useMemo(() => {

        return pages.map((page, index) => {

            const pageId =
                page.pageId ||
                page.id ||
                page.urn ||
                `page-${index}`;

            const pageName =
                page.pageName ||
                page.name ||
                page.title ||
                `Page ${index + 1}`;

            return {
                key: String(pageId),
                id: pageId,
                name: pageName,
                type: 'Page'
            };

        });

    }, [pages]);


    /*
    |--------------------------------------------------------------------------
    | Build page comparison data
    |--------------------------------------------------------------------------
    */

    const comparisonData = useMemo(() => {

        return pageResources.map(page => {

            const originalPage =
                pages.find(
                    item =>
                        String(
                            item.pageId ||
                            item.id ||
                            item.urn
                        ) === String(page.id)
                );


            const pageCards =
                getPageCardsForOverallStatus(
                    originalPage,
                    cards
                );


            const worksheets =
                pageCards.map(card =>
                    normalizeWorksheet(card)
                );


            const datasourceIds = [
                ...new Set(
                    pageCards
                        .map(
                            card =>
                                getCardDatasetId(card)
                        )
                        .filter(Boolean)
                        .map(String)
                )
            ];


            const datasourceNames = [
                ...new Set(
                    pageCards
                        .map(
                            card =>
                                getCardDatasetName(
                                    card,
                                    datasets
                                )
                        )
                        .filter(Boolean)
                )
            ];


            const columnInfo =
                getPageColumnInfo(
                    pageCards,
                    datasets
                );


            const calculatedFields =
                getPageCalculatedFields(
                    originalPage,
                    pageCards,
                    beastmodes
                );


            return {

                ...page,

                page:
                    originalPage,

                cards:
                    pageCards,

                worksheets,

                datasourceIds,

                datasourceNames,

                columns:
                    columnInfo.columns,

                columnTypes:
                    columnInfo.types,

                columnDetails:
                    columnInfo.columnDetails,

                calculatedFields,

                calculatedFieldNames:
                    calculatedFields.map(
                        field =>
                            field.name
                    )

            };

        });

    }, [
        pages,
        cards,
        datasets,
        beastmodes,
        pageResources
    ]);


    /*
    |--------------------------------------------------------------------------
    | Empty state
    |--------------------------------------------------------------------------
    */

    if (!pageResources.length) {

        return (
            <EmptyState
                message="No pages available for overall status."
            />
        );

    }


    return (

        <div
            style={{
                padding: '20px'
            }}
        >

            {/* =========================================================
                HEADER
            ========================================================== */}

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '18px',
                    gap: '12px'
                }}
            >

                <div>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '15px',
                            fontWeight: 600,
                            marginBottom: '5px'
                        }}
                    >

                        <FontAwesomeIcon
                            icon={faTable}
                            style={{
                                color: '#1e447e'
                            }}
                        />

                        Overall Status
                    </div>


                    <div
                        style={{
                            fontSize: '12px',
                            color:
                                'var(--text-secondary)'
                        }}
                    >
                        Page-to-page comparison based on worksheets,
                        datasources, columns, column types and calculated fields.
                    </div>

                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <ExportTabButton
                        rows={buildOverallStatusExportRows(comparisonData)}
                        sheetName="Overall Status"
                    />
                    <ViewModeToggle
                        viewMode={viewMode}
                        onChange={setViewMode}
                        options={[
                            { key: 'matrix', label: 'Table', icon: faTable },
                            { key: 'network', label: 'Diagram', icon: faSitemap }
                        ]}
                    />
                </div>

            </div>


            {/* =========================================================
                INSIGHT SUMMARY CARDS
            ========================================================== */}

            <OverallStatusInsights
                resources={
                    comparisonData
                }
            />


            {viewMode === 'network' ? (

                /* =========================================================
                    SIMILARITY NETWORK DIAGRAM
                ========================================================== */

                <OverallStatusNetwork
                    resources={
                        comparisonData
                    }
                />

            ) : (

                <>

                    {/* =========================================================
                        LEGEND
                    ========================================================== */}

                    {/* <OverallStatusLegend /> */}


                    {/* =========================================================
                        MATRIX
                    ========================================================== */}

                    <OverallStatusMatrix
                        resources={
                            comparisonData
                        }
                    />

                </>

            )}


            {/* =========================================================
                PAIRWISE DETAIL / MATCHED DATA / RECOMMENDATION
            ========================================================== */}

            <PairwiseDetail
                resources={
                    comparisonData
                }
            />

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Overall Status Insights
|--------------------------------------------------------------------------
| Enriched summary cards above the matrix/network: total pages compared,
| how many pairs are strong merge candidates (score >= 85), how many need
| review (score 70-84), and the single strongest pair found, so the reader
| gets the headline takeaway before digging into the full pairwise table.
|--------------------------------------------------------------------------
*/

function OverallStatusInsights({ resources = [] }) {

    const stats = useMemo(() => {

        let mergeCount = 0;
        let reviewCount = 0;
        let separateCount = 0;
        let totalScore = 0;
        let pairCount = 0;
        let strongest = null;

        for (let i = 0; i < resources.length; i++) {
            for (let j = i + 1; j < resources.length; j++) {

                const first = resources[i];
                const second = resources[j];
                const score = getPageComparisonScore(first, second);

                pairCount += 1;
                totalScore += score;

                if (score >= 85) mergeCount += 1;
                else if (score >= 70) reviewCount += 1;
                else separateCount += 1;

                if (!strongest || score > strongest.score) {
                    strongest = { first, second, score };
                }

            }
        }

        return {
            pageCount: resources.length,
            pairCount,
            mergeCount,
            reviewCount,
            separateCount,
            avgScore: pairCount ? Math.round(totalScore / pairCount) : 0,
            strongest
        };

    }, [resources]);

    const cards = [
        {
            title: 'Pages Compared',
            value: stats.pageCount,
            icon: faTable,
            color: '#1e447e'
        },
        {
            title: 'Merge Candidates',
            value: stats.mergeCount,
            subtitle: 'score >= 85',
            icon: faObjectGroup,
            color: '#198754'
        },
        {
            title: 'Needs Review',
            value: stats.reviewCount,
            subtitle: 'score 70-84',
            icon: faTriangleExclamation,
            color: '#d97706'
        },
        {
            title: 'Separate',
            value: stats.separateCount,
            subtitle: 'score < 70',
            icon: faGaugeHigh,
            color: '#6b7280'
        }
    ];

    return (

        <div style={{ marginBottom: '18px' }}>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '10px',
                    marginBottom: stats.strongest ? '10px' : 0
                }}
            >

                {cards.map(card => (
                    <div
                        key={card.title}
                        style={{
                            background: 'var(--card-bg, #fff)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            padding: '12px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '11px'
                        }}
                    >
                        <div
                            style={{
                                width: '34px',
                                height: '34px',
                                borderRadius: '8px',
                                background: `${card.color}1a`,
                                color: card.color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <FontAwesomeIcon icon={card.icon} />
                        </div>
                        <div>
                            <div style={smallLabelStyle}>{card.title}</div>
                            <div style={{ fontSize: '17px', fontWeight: 600 }}>{card.value}</div>
                            {card.subtitle && (
                                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{card.subtitle}</div>
                            )}
                        </div>
                    </div>
                ))}

            </div>

            <div style={{paddingTop: 10}}>
                <OverallStatusLegend />
            </div>

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| Overall Status Network Diagram
|--------------------------------------------------------------------------
| Alternative to the N x N matrix: pages are laid out as nodes around a
| circle, with an edge drawn between any two pages whose similarity score
| clears a visibility threshold. Edge thickness/color encodes the score, so
| clusters of strongly related pages are visible at a glance instead of
| having to scan a grid of numbers.
|--------------------------------------------------------------------------
*/

function OverallStatusNetwork({ resources = [] }) {

    /*
     * With larger page counts (dozens to ~100+), a low default threshold
     * draws nearly every pairwise connection at once, which collapses into
     * an unreadable "hairball" (all lines converging, labels overlapping).
     * Defaulting higher (only show strong/likely-merge connections) keeps
     * the initial view readable; the slider still lets you lower it.
     */
    const [threshold, setThreshold] = useState(70);
    const [hoveredKey, setHoveredKey] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);

    // Clicking a node pins the highlight so it survives moving the mouse
    // away (previously, hovering elsewhere reset the highlight entirely).
    // Hover still previews highlighting when nothing is pinned.
    const activeKey = selectedKey || hoveredKey;

    // Once there are enough nodes, permanent labels on every isolated
    // (unconnected) page overlap each other with no real payoff - those
    // dots aren't part of any relationship worth reading a name for right
    // now. Connected nodes (part of at least one visible edge) always keep
    // their label though, since those are exactly the pages the diagram is
    // meant to highlight.
    const isDense = resources.length > 20;

    const { nodes, edges } = useMemo(() => {

        const count = resources.length;
        const radius = Math.max(140, count * 22);
        const centerX = radius + 60;
        const centerY = radius + 60;

        const nodeList = resources.map((resource, index) => {
            const angle = (2 * Math.PI * index) / Math.max(1, count);
            return {
                key: resource.key,
                name: resource.name,
                x: centerX + radius * Math.cos(angle),
                y: centerY + radius * Math.sin(angle)
            };
        });

        const edgeList = [];
        for (let i = 0; i < resources.length; i++) {
            for (let j = i + 1; j < resources.length; j++) {
                const score = getPageComparisonScore(resources[i], resources[j]);
                if (score >= threshold) {
                    edgeList.push({
                        sourceKey: resources[i].key,
                        targetKey: resources[j].key,
                        score
                    });
                }
            }
        }

        return {
            nodes: nodeList,
            edges: edgeList,
            width: centerX + radius + 60,
            height: centerY + radius + 60
        };

    }, [resources, threshold]);

    const svgSize = Math.max(
        400,
        2 * (Math.max(140, resources.length * 22) + 60) + 20
    );

    // Cap the rendered size and let the SVG viewBox scale everything down
    // to fit the available width, instead of rendering at full pixel size
    // (which overflowed the container and forced side-scrolling through a
    // wall of crossing lines, as seen with ~100 pages).
    const displaySize = Math.min(svgSize, 900);

    // The viewBox scales the whole coordinate space down to fit
    // displaySize, which also shrinks text/strokes defined in those same
    // units - that's why labels looked "small" with many pages. Scale font
    // size and node radius up by the inverse of that shrink factor so they
    // render at a consistent, readable size on screen regardless of page
    // count.
    const scaleCompensation = svgSize / displaySize;
    const labelFontSize = 11 * scaleCompensation;
    const nodeRadius = 9 * scaleCompensation;

    const positions = new Map(nodes.map(n => [n.key, n]));

    const isNodeDimmed = key => {
        if (!activeKey) return false;
        if (key === activeKey) return false;
        return !edges.some(e =>
            (e.sourceKey === activeKey && e.targetKey === key) ||
            (e.targetKey === activeKey && e.sourceKey === key)
        );
    };

    const isEdgeDimmed = edge => {
        if (!activeKey) return false;
        return edge.sourceKey !== activeKey && edge.targetKey !== activeKey;
    };

    const connectedKeys = useMemo(() => {
        const set = new Set();
        edges.forEach(e => {
            set.add(e.sourceKey);
            set.add(e.targetKey);
        });
        return set;
    }, [edges]);

    return (

        <div style={{ marginBottom: '28px' }}>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px',
                    fontSize: '11px',
                    color: 'var(--text-secondary)'
                }}
            >
                <span>Show connections with score &ge;</span>
                <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={threshold}
                    onChange={e => setThreshold(Number(e.target.value))}
                    style={{ width: '160px' }}
                />
                <strong style={{ color: 'var(--text-primary)' }}>{threshold}</strong>
                <span style={{ marginLeft: 'auto' }}>
                    {edges.length} connection{edges.length === 1 ? '' : 's'} shown
                    {isDense ? ' \u00b7 hover an unconnected dot to see its name' : ''}
                    {' \u00b7 click a node to pin its highlight'}
                </span>
            </div>

            <div
                style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    overflow: 'auto',
                    background: 'var(--card-bg, #fff)',
                    padding: '10px'
                }}
            >

                {!resources.length ? (
                    <EmptyState message="No pages available to diagram." />
                ) : (
                    <svg
                        width={displaySize}
                        height={displaySize}
                        viewBox={`0 0 ${svgSize} ${svgSize}`}
                        style={{ display: 'block', margin: '0 auto' }}
                        onClick={() => setSelectedKey(null)}
                    >

                        {/* EDGES */}
                        {edges.map((edge, i) => {

                            const from = positions.get(edge.sourceKey);
                            const to = positions.get(edge.targetKey);

                            if (!from || !to) return null;

                            const dimmed = isEdgeDimmed(edge);
                            const strong = edge.score >= 85;
                            const medium = edge.score >= 70;

                            // Only label edges touching the active
                            // (hovered/selected) node - showing every
                            // score at once would recreate the original
                            // clutter problem.
                            const showScore = !!activeKey && !dimmed;
                            const midX = (from.x + to.x) / 2;
                            const midY = (from.y + to.y) / 2;

                            return (
                                <g key={i}>
                                    <line
                                        x1={from.x}
                                        y1={from.y}
                                        x2={to.x}
                                        y2={to.y}
                                        stroke={
                                            dimmed
                                                ? '#e2e8f0'
                                                : strong
                                                    ? '#198754'
                                                    : medium
                                                        ? '#d97706'
                                                        : '#94a3b8'
                                        }
                                        strokeWidth={(dimmed ? 1 : Math.max(1, edge.score / 30)) * scaleCompensation}
                                        opacity={dimmed ? 0.35 : 0.85}
                                    />
                                    {showScore && (
                                        <text
                                            x={midX}
                                            y={midY}
                                            textAnchor="middle"
                                            fontSize={labelFontSize * 0.85}
                                            fontWeight={700}
                                            fill={
                                                strong
                                                    ? '#198754'
                                                    : medium
                                                        ? '#d97706'
                                                        : '#6b7280'
                                            }
                                            style={{ paintOrder: 'stroke', stroke: 'var(--card-bg, #fff)', strokeWidth: 4 * scaleCompensation }}
                                        >
                                            {edge.score}
                                        </text>
                                    )}
                                </g>
                            );

                        })}

                        {/* NODES */}
                        {nodes.map(node => {

                            const dimmed = isNodeDimmed(node.key);
                            const isSelected = node.key === selectedKey;
                            const showLabel = !isDense || connectedKeys.has(node.key) || activeKey === node.key;

                            return (
                                <g
                                    key={node.key}
                                    transform={`translate(${node.x}, ${node.y})`}
                                    onMouseEnter={() => setHoveredKey(node.key)}
                                    onMouseLeave={() => setHoveredKey(null)}
                                    onClick={event => {
                                        event.stopPropagation();
                                        setSelectedKey(prev => prev === node.key ? null : node.key);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                    opacity={dimmed ? 0.35 : 1}
                                >
                                    <circle
                                        r={isSelected ? nodeRadius * 1.35 : nodeRadius}
                                        fill={isSelected ? '#1e447e' : '#eef3ff'}
                                        stroke="#1e447e"
                                        strokeWidth={(isSelected ? 2.5 : 1.5) * scaleCompensation}
                                    />
                                    {showLabel && (
                                        <text
                                            x={nodeRadius + 5}
                                            y={nodeRadius * 0.45}
                                            fontSize={labelFontSize}
                                            fontWeight={isSelected ? 700 : 600}
                                            fill={isSelected ? '#1e447e' : 'var(--text-primary)'}
                                            style={{ paintOrder: 'stroke', stroke: 'var(--card-bg, #fff)', strokeWidth: 3 * scaleCompensation }}
                                        >
                                            {node.name.length > 26 ? `${node.name.slice(0, 26)}...` : node.name}
                                        </text>
                                    )}
                                    <title>{node.name}</title>
                                </g>
                            );

                        })}

                    </svg>
                )}

            </div>

            <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '16px', height: '2px', background: '#198754', display: 'inline-block' }} />
                    Score &ge; 85 (merge)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '16px', height: '2px', background: '#d97706', display: 'inline-block' }} />
                    Score 70-84 (review)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '16px', height: '2px', background: '#94a3b8', display: 'inline-block' }} />
                    Below 70
                </div>
            </div>

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| Overall Status Legend
|--------------------------------------------------------------------------
*/

function OverallStatusLegend() {

    return (

        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '18px',
                marginBottom: '16px',
                fontSize: '11px',
                color:
                    'var(--text-secondary)'
            }}
        >

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}
            >

                <span
                    style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '3px',
                        background:
                            getMatrixCellBackground(90),
                        display: 'inline-block'
                    }}
                />

                <strong>85-100</strong>

                <span>
                    Merge
                </span>

            </div>


            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}
            >

                <span
                    style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '3px',
                        background:
                            getMatrixCellBackground(75),
                        display: 'inline-block'
                    }}
                />

                <strong>70-84</strong>

                <span>
                    Review
                </span>

            </div>


            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}
            >

                <span
                    style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '3px',
                        background:
                            getMatrixCellBackground(0),
                        display: 'inline-block'
                    }}
                />

                <strong>0-69</strong>

                <span>
                    Separate
                </span>

            </div>


            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}
            >

                <strong>-</strong>

                <span>
                    Same page
                </span>

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Overall Status Matrix
|--------------------------------------------------------------------------
*/

function OverallStatusMatrix({
    resources = []
}) {

    return (

        <div
            style={{
                border:
                    '1px solid var(--border-color)',
                borderRadius: '8px',
                overflow: 'auto',
                maxHeight: '650px',
                marginBottom: '28px'
            }}
        >

            <table
                style={{
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    minWidth:
                        `${Math.max(
                            850,
                            220 +
                            resources.length * 125
                        )}px`,
                    width: '100%',
                    fontSize: '11px'
                }}
            >

                <thead>

                    <tr>

                        <th
                            style={{
                                ...overallStatusHeaderStyle,
                                position: 'sticky',
                                left: 0,
                                top: 0,
                                zIndex: 4,
                                minWidth: '220px',
                                textAlign: 'left'
                            }}
                        >
                            Page
                        </th>


                        {resources.map(
                            resource => (

                                <th
                                    key={
                                        resource.key
                                    }
                                    style={{
                                        ...overallStatusHeaderStyle,
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 3,
                                        minWidth: '125px',
                                        maxWidth: '160px'
                                    }}
                                    title={
                                        resource.name
                                    }
                                >

                                    <div
                                        style={{
                                            overflow:
                                                'hidden',
                                            textOverflow:
                                                'ellipsis',
                                            whiteSpace:
                                                'nowrap'
                                        }}
                                    >
                                        {resource.name}
                                    </div>

                                </th>

                            )
                        )}

                    </tr>

                </thead>


                <tbody>

                    {resources.map(
                        rowResource => (

                            <tr
                                key={
                                    rowResource.key
                                }
                            >

                                <td
                                    style={{
                                        ...overallStatusRowHeaderStyle,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 2,
                                        background:
                                            'var(--card-bg, #fff)'
                                    }}
                                    title={
                                        rowResource.name
                                    }
                                >

                                    <div
                                        style={{
                                            fontWeight: 600,
                                            overflow:
                                                'hidden',
                                            textOverflow:
                                                'ellipsis',
                                            whiteSpace:
                                                'nowrap'
                                        }}
                                    >
                                        {rowResource.name}
                                    </div>

                                </td>


                                {resources.map(
                                    columnResource => {

                                        const same =
                                            rowResource.key ===
                                            columnResource.key;


                                        const score =
                                            same
                                                ? null
                                                : getPageComparisonScore(
                                                    rowResource,
                                                    columnResource
                                                );


                                        return (

                                            <td
                                                key={
                                                    columnResource.key
                                                }
                                                style={{
                                                    ...overallStatusCellStyle,
                                                    background:
                                                        same
                                                            ? 'var(--hover-bg)'
                                                            : getMatrixCellBackground(
                                                                score
                                                            ),
                                                    color:
                                                        same
                                                            ? 'var(--text-tertiary)'
                                                            : getMatrixCellColor(
                                                                score
                                                            ),
                                                    fontWeight:
                                                        same
                                                            ? 400
                                                            : 600
                                                }}
                                            >

                                                {same
                                                    ? '-'
                                                    : score}

                                            </td>

                                        );

                                    }
                                )}

                            </tr>

                        )
                    )}

                </tbody>

            </table>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Pairwise Detail
|--------------------------------------------------------------------------
*/
/*
|--------------------------------------------------------------------------
| Pairwise Detail
|--------------------------------------------------------------------------
*/

function PairwiseDetail({
    resources = []
}) {

    const [selectedPair, setSelectedPair] = useState(null);

    const pairs = useMemo(() => {

        const result = [];

        for (let i = 0; i < resources.length; i++) {

            for (let j = i + 1; j < resources.length; j++) {

                const first = resources[i];
                const second = resources[j];

                const breakdown = getPageComparisonBreakdown(
                    first,
                    second
                );

                const score = breakdown.score;

                /*
                |--------------------------------------------------------------------------
                | Get comparison details
                |--------------------------------------------------------------------------
                */

                const detail = getPairwiseDetails(
                    first,
                    second,
                    score
                );

                const matchedDetails =
                    getMatchedPairDetails(first, second);

                const unmatchedDetails =
                    getUnmatchedPairDetails(first, second);

                result.push({

                    first,
                    second,

                    score,
                    scoreBreakdown: breakdown.dimensions,
                    availableWeight: breakdown.availableWeight,
                    totalWeight: breakdown.totalWeight,

                    matched:
                        detail?.matched ?? 0,

                    recommendation:
                        detail?.recommendation ||
                        getPairRecommendation(score),

                    /*
                    | Keep the categorized matched details with the pair.
                    | The popup reads pair.matchedDetails directly.
                    */
                    matchedDetails,

                    unmatchedDetails,

                    details:
                        matchedDetails

                });

            }

        }

        /*
        |--------------------------------------------------------------------------
        | Strongest relationships first
        |--------------------------------------------------------------------------
        */

        return result.sort(
            (a, b) =>
                b.score - a.score
        );

    }, [resources]);


    if (!pairs.length) {
        return null;
    }


    return (

        <div>

            <div
                style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '10px'
                }}
            >
                Pairwise Detail - Matched Data & Recommendation
            </div>

            {/* <div
                style={{
                    fontSize: '11px',
                    color: 'var(--text-secondary)',
                    marginBottom: '10px'
                }}
            >
                Click the matched count to see the exact worksheets, datasources, columns,
                compatible types and calculated fields that contributed to the score.
            </div> */}


            <div
                style={{
                    border:
                        '1px solid var(--border-color)',
                    borderRadius: '8px',
                    overflow: 'auto'
                }}
            >

                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '12px'
                    }}
                >

                    <thead>

                        <tr>

                            <th
                                style={tableHeaderStyle}
                            >
                                Page
                            </th>

                            <th
                                style={tableHeaderStyle}
                            >
                                Page
                            </th>

                            <th
                                style={{
                                    ...tableHeaderStyle,
                                    textAlign: 'center'
                                }}
                            >
                                Score
                            </th>

                            <th
                                style={{
                                    ...tableHeaderStyle,
                                    textAlign: 'center'
                                }}
                            >
                                Matched
                            </th>

                            <th
                                style={tableHeaderStyle}
                            >
                                Matched Data
                            </th>

                            <th
                                style={tableHeaderStyle}
                            >
                                Recommendation
                            </th>

                        </tr>

                    </thead>


                    <tbody>

                        {pairs.map(
                            pair => (

                                <tr
                                    key={
                                        `${pair.first.key}-${pair.second.key}`
                                    }
                                    style={{
                                        borderBottom:
                                            '1px solid var(--border-color)'
                                    }}
                                >

                                    <td
                                        style={tableCellStyle}
                                    >
                                        {pair.first.name}
                                    </td>


                                    <td
                                        style={tableCellStyle}
                                    >
                                        {pair.second.name}
                                    </td>


                                    <td
                                        style={{
                                            ...tableCellStyle,
                                            textAlign: 'center'
                                        }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setSelectedPair(pair)}
                                            title="View score breakdown"
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                padding: '4px 8px',
                                                borderRadius: '5px',
                                                cursor: 'pointer',
                                                fontWeight: 700,
                                                textDecoration: 'underline',
                                                color: getMatrixCellColor(pair.score)
                                            }}
                                        >
                                            {pair.score}
                                        </button>
                                    </td>


                                    {/* =====================================================
                                        MATCHED COUNT
                                    ====================================================== */}

                                    <td
                                        style={{
                                            ...tableCellStyle,
                                            textAlign: 'center'
                                        }}
                                    >

                                        <button
                                            type="button"
                                            onClick={() => {

                                                if (
                                                    !pair.matched ||
                                                    pair.matched <= 0
                                                ) {
                                                    return;
                                                }

                                                setSelectedPair(pair);

                                            }}
                                            disabled={
                                                !pair.matched ||
                                                pair.matched <= 0
                                            }
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                padding: '4px 8px',
                                                borderRadius: '5px',
                                                cursor:
                                                    pair.matched > 0
                                                        ? 'pointer'
                                                        : 'default',
                                                fontWeight: 700,
                                                color:
                                                    pair.matched > 0
                                                        ? 'var(--primary-color, #2563eb)'
                                                        : 'var(--text-secondary)',
                                                textDecoration:
                                                    pair.matched > 0
                                                        ? 'underline'
                                                        : 'none'
                                            }}
                                            title={
                                                pair.matched > 0
                                                    ? 'View matched details'
                                                    : 'No matched items'
                                            }
                                        >
                                            {pair.matched}
                                        </button>

                                    </td>


                                    <td
                                        style={{
                                            ...tableCellStyle,
                                            minWidth: '260px'
                                        }}
                                    >
                                        <MatchedBreakdown
                                            matchedDetails={pair.details}
                                        />
                                    </td>


                                    <td
                                        style={tableCellStyle}
                                    >

                                        <RecommendationBadge
                                            value={
                                                pair.recommendation
                                            }
                                        />

                                    </td>

                                </tr>

                            )
                        )}

                    </tbody>

                </table>

            </div>


            {/* =====================================================
                MATCHED DETAILS POPUP
            ====================================================== */}

            {selectedPair && (

                <MatchedDetailsModal
                    pair={selectedPair}
                    onClose={() =>
                        setSelectedPair(null)
                    }
                />

            )}

        </div>
    );
}

function getMatchedItemLabel(item) {

    if (item === null || item === undefined) {
        return '-';
    }

    if (typeof item === 'string' || typeof item === 'number') {
        return String(item);
    }

    if (typeof item === 'object') {
        return (
            item.firstValue ||
            item.name ||
            item.title ||
            item.caption ||
            item.column ||
            item.columnName ||
            item.worksheet ||
            item.worksheetName ||
            item.datasource ||
            item.datasourceName ||
            item.expression ||
            item.formula ||
            item.id ||
            '-'
        );
    }

    return String(item);
}


/*
|--------------------------------------------------------------------------
| Matched Breakdown
|--------------------------------------------------------------------------
| Shows the actual matched categories directly in the Overall Status table.
*/

function MatchedBreakdown({ matchedDetails = {} }) {

    const categories = [
        ['Worksheets', matchedDetails.worksheets],
        ['Datasources', matchedDetails.datasources],
        ['Columns', matchedDetails.columns],
        ['Types', matchedDetails.types],
        ['Calculated Fields', matchedDetails.calculatedFields]
    ];

    const active = categories.filter(([, items]) => Array.isArray(items) && items.length);

    if (!active.length) {
        return (
            <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>
                No matched data
            </span>
        );
    }

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {active.map(([label, items]) => (
                <span
                    key={label}
                    title={items.map(item => getMatchedItemLabel(item)).join(', ')}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'var(--hover-bg)',
                        color: 'var(--text-secondary)',
                        fontSize: '10px'
                    }}
                >
                    <strong>{items.length}</strong> {label}
                </span>
            ))}
        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Matched Details Modal
|--------------------------------------------------------------------------
*/

function MatchedDetailsModal({
    pair,
    onClose
}) {

    const [activeDetailTab, setActiveDetailTab] = useState('breakdown');

    if (!pair) return null;

    const matched = pair.matchedDetails || {};
    const unmatched = pair.unmatchedDetails || {};

    const categories = [
        ['Worksheets / Cards', 'worksheets'],
        ['Datasources', 'datasources'],
        ['Columns', 'columns'],
        ['Calculated Fields / Beast Modes', 'calculatedFields']
    ];

    const activeDetails = activeDetailTab === 'matched' ? matched : unmatched;
    const rows = categories.flatMap(([label, key]) =>
        (Array.isArray(activeDetails[key]) ? activeDetails[key] : []).map(item => ({
            ...item,
            category: label
        }))
    );

    return (
        <div
            onClick={event => {
                if (event.target === event.currentTarget) onClose();
            }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 9999, padding: '20px'
            }}
        >
            <div style={{
                width: 'min(1100px, 100%)', maxHeight: '88vh',
                background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color)',
                borderRadius: '10px', overflow: 'hidden',
                boxShadow: '0 15px 50px rgba(0,0,0,0.20)',
                display: 'flex', flexDirection: 'column'
            }}>

                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 18px', borderBottom: '1px solid var(--border-color)'
                }}>
                    <div>
                        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '5px' }}>
                            Page Comparison Details
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                            <strong>{pair.first.name}</strong><span>-</span><strong>{pair.second.name}</strong>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        width: '32px', height: '32px', border: 'none', borderRadius: '6px',
                        background: 'var(--hover-bg)', color: 'var(--text-secondary)', fontSize: '18px', cursor: 'pointer'
                    }}>x</button>
                </div>

                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px',
                    padding: '14px 18px', borderBottom: '1px solid var(--border-color)'
                }}>
                    <MatchedSummary label="Score" value={pair.score} />
                    <MatchedSummary label="Matched" value={pair.matched} />
                    <MatchedSummary label="Recommendation" value={pair.recommendation} />
                </div>

                <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', padding: '0 18px' }}>
                    {[
                        ['breakdown', 'Score Breakdown'],
                        ['matched', 'Matched Details'],
                        ['unmatched', 'Unmatched Details']
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setActiveDetailTab(key)}
                            style={{
                                border: 'none', background: 'transparent', padding: '11px 14px',
                                fontSize: '12px', fontWeight: activeDetailTab === key ? 700 : 500,
                                color: activeDetailTab === key ? 'var(--primary-color, #2563eb)' : 'var(--text-secondary)',
                                borderBottom: activeDetailTab === key ? '2px solid var(--primary-color, #2563eb)' : '2px solid transparent',
                                cursor: 'pointer'
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div style={{ overflowY: 'auto', padding: '18px' }}>
                    {activeDetailTab === 'breakdown' ? (

                        <ScoreBreakdownPanel pair={pair} />

                    ) : (
                    <>
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '5px' }}>
                        {activeDetailTab === 'matched' ? 'What is matched?' : 'What is unmatched?'}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        {activeDetailTab === 'matched'
                            ? 'These rows have the same comparison key on both pages.'
                            : 'These rows exist on only one page and were not found on the other page.'}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '12px' }}>
                        Datatype is shown directly with each Column. The previous Types score represents column datatype compatibility and is no longer displayed as a separate detail category.
                    </div>

                    {rows.length ? (
                        <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...tableHeaderStyle, width: '16%' }}>Match Type</th>
                                        <th style={{ ...tableHeaderStyle, width: '31%' }}>{pair.first.name}</th>
                                        <th style={{ ...tableHeaderStyle, width: '31%' }}>{pair.second.name}</th>
                                        <th style={{ ...tableHeaderStyle, width: '12%' }}>Datatype A</th>
                                        <th style={{ ...tableHeaderStyle, width: '12%' }}>Datatype B</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <tr key={`${row.category}-${index}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={tableCellStyle}>
                                                <strong>{row.category}</strong>
                                            </td>
                                            <td style={{ ...tableCellStyle, verticalAlign: 'top' }}>
                                                <div style={{ fontWeight: 600 }}>{row.firstValue || '-'}</div>
                                                {row.firstDetail && <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', marginTop: '3px' }}>{row.firstDetail}</div>}
                                            </td>
                                            <td style={{ ...tableCellStyle, verticalAlign: 'top' }}>
                                                <div style={{ fontWeight: 600 }}>{row.secondValue || '-'}</div>
                                                {row.secondDetail && <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', marginTop: '3px' }}>{row.secondDetail}</div>}
                                            </td>
                                            <td style={tableCellStyle}>{row.firstDataType || (row.category === 'Columns' ? 'Unknown' : '-')}</td>
                                            <td style={tableCellStyle}>{row.secondDataType || (row.category === 'Columns' ? 'Unknown' : '-')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{ padding: '14px', border: '1px dashed var(--border-color)', borderRadius: '7px', color: 'var(--text-tertiary)', fontSize: '11px' }}>
                            {activeDetailTab === 'matched' ? 'No matched details found.' : 'No unmatched details found.'}
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>
        </div>
    );
}

/*
|--------------------------------------------------------------------------
| Score Breakdown Panel
|--------------------------------------------------------------------------
| Shows exactly which comparison dimensions (datasources, worksheets,
| columns, etc.) drove the overall Score up or down, so it's not just a
| single opaque number. Each dimension shows its similarity % between the
| two pages, how many points it contributed to the final 0-100 score, and
| whether either page even had data to compare for that dimension.
|--------------------------------------------------------------------------
*/

function ScoreBreakdownPanel({ pair }) {

    const dimensions = pair.scoreBreakdown || [];
    const maxPoints = Math.max(1, ...dimensions.map(d => d.points || 0));

    return (
        <div>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '5px' }}>
                How the score of {pair.score} was calculated
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                Each dimension is compared using Jaccard similarity (how much overlap exists between the two pages)
                and contributes points toward the final score based on its weight. Points across all dimensions add up to the Score above.
            </div>

            {pair.totalWeight != null && (() => {

                const includedDims = dimensions.filter(d => d.hasData);
                const excludedDims = dimensions.filter(d => !d.hasData);

                return (
                    <div style={{ marginBottom: '14px', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '7px', background: 'var(--hover-bg)' }}>

                        <div style={{ fontSize: '11px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                            <strong>Comparable weight used: {pair.availableWeight} / {pair.totalWeight}</strong> - only dimensions
                            where at least one page has data count toward the score. Dimensions empty on both pages are excluded, which is why a single strong match
                            can contribute more points than its raw weight suggests.
                        </div>

                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '10px' }}>

                            <div style={{ flex: '1 1 200px' }}>
                                <div style={{ fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                    Included ({includedDims.length}) - has data on at least one page
                                </div>
                                {includedDims.length ? (
                                    <div style={{ color: 'var(--text-primary)' }}>
                                        {includedDims.map(d => `${d.label} (${d.weight})`).join(' + ')}
                                        {' '}= <strong>{pair.availableWeight}</strong>
                                    </div>
                                ) : (
                                    <div style={{ color: 'var(--text-tertiary)' }}>None</div>
                                )}
                            </div>

                            <div style={{ flex: '1 1 200px' }}>
                                <div style={{ fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                    Excluded ({excludedDims.length}) - empty on both pages
                                </div>
                                {excludedDims.length ? (
                                    <div style={{ color: 'var(--text-tertiary)' }}>
                                        {excludedDims.map(d => `${d.label} (${d.weight})`).join(' + ')}
                                        {' '}= {pair.totalWeight - pair.availableWeight}
                                    </div>
                                ) : (
                                    <div style={{ color: 'var(--text-tertiary)' }}>None - every dimension had data</div>
                                )}
                            </div>

                        </div>

                    </div>
                );

            })()}

            {!dimensions.length ? (
                <div style={{ padding: '14px', border: '1px dashed var(--border-color)', borderRadius: '7px', color: 'var(--text-tertiary)', fontSize: '11px' }}>
                    No breakdown available for this pair.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {dimensions.map(dimension => (
                        <div
                            key={dimension.key}
                            style={{
                                border: '1px solid var(--border-color)',
                                borderRadius: '7px',
                                padding: '10px 12px',
                                opacity: dimension.hasData ? 1 : 0.55
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px', gap: '10px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 600 }}>
                                    {dimension.label}
                                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: 400, marginLeft: '6px' }}>
                                        weight {dimension.weight}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', whiteSpace: 'nowrap', alignItems: 'baseline' }}>
                                    <span style={{ color: 'var(--text-secondary)' }}>
                                        {dimension.hasData ? `${dimension.similarity}% similar` : 'No data on either page'}
                                    </span>
                                    {dimension.hasData && (
                                        <span
                                            style={{ color: 'var(--text-tertiary)', fontFamily: 'monospace' }}
                                            title="Raw contribution divided by the comparable weight, times 100, rounded"
                                        >
                                            ({dimension.rawScore} / {pair.availableWeight}) x 100
                                        </span>
                                    )}
                                    <strong style={{ color: !dimension.hasData ? 'var(--text-tertiary)' : dimension.similarity >= 60 ? '#198754' : dimension.similarity >= 30 ? '#d97706' : '#94a3b8' }}>
                                        +{dimension.points} pts
                                    </strong>
                                </div>
                            </div>
                            <div style={{ height: '6px', borderRadius: '4px', background: 'var(--hover-bg)', overflow: 'hidden' }}>
                                <div
                                    style={{
                                        height: '100%',
                                        width: `${Math.min(100, (dimension.points / maxPoints) * 100)}%`,
                                        background: dimension.hasData ? '#1e447e' : 'var(--border-color)',
                                        borderRadius: '4px'
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

}

/*
|--------------------------------------------------------------------------
| Matched Summary
|--------------------------------------------------------------------------
*/

function MatchedSummary({
    label,
    value
}) {

    return (

        <div
            style={{
                border:
                    '1px solid var(--border-color)',
                borderRadius: '7px',
                padding: '10px 12px'
            }}
        >

            <div
                style={{
                    fontSize: '10px',
                    color:
                        'var(--text-secondary)',
                    marginBottom: '4px'
                }}
            >
                {label}
            </div>


            <div
                style={{
                    fontSize: '15px',
                    fontWeight: 700,
                    color:
                        label ===
                        'Recommendation'
                            ? (
                                value ===
                                'MERGE'
                                    ? '#198754'
                                    : '#dc3545'
                            )
                            : '#1e447e'
                }}
            >
                {value}
            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Matched Modal Section
|--------------------------------------------------------------------------
*/

function MatchedModalSection({
    title,
    items = [],
    icon
}) {

    return (

        <div
            style={{
                marginBottom: '18px'
            }}
        >

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    marginBottom: '8px'
                }}
            >

                <FontAwesomeIcon
                    icon={icon}
                    style={{
                        color: '#1e447e',
                        fontSize: '12px'
                    }}
                />


                <span
                    style={{
                        fontSize: '12px',
                        fontWeight: 600
                    }}
                >
                    {title}
                </span>


                <span
                    style={{
                        background:
                            'var(--hover-bg)',
                        color:
                            'var(--text-secondary)',
                        borderRadius:
                            '10px',
                        padding:
                            '2px 7px',
                        fontSize:
                            '10px'
                    }}
                >
                    {items.length}
                </span>

            </div>


            {items.length ? (

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px'
                    }}
                >

                    {items.map(
                        (item, index) => (

                            <div
                                key={
                                    index
                                }
                                style={{
                                    padding:
                                        '8px 10px',
                                    border:
                                        '1px solid var(--border-color)',
                                    borderRadius:
                                        '5px',
                                    fontSize:
                                        '11px',
                                    background:
                                        'var(--card-bg, #fff)'
                                }}
                            >
                                {getMatchedItemLabel(
                                    item
                                )}
                            </div>

                        )
                    )}

                </div>

            ) : (

                <div
                    style={{
                        padding:
                            '8px 10px',
                        border:
                            '1px dashed var(--border-color)',
                        borderRadius:
                            '5px',
                        color:
                            'var(--text-tertiary)',
                        fontSize:
                            '11px'
                    }}
                >
                    No matches found.
                </div>

            )}

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Calculated Fields
|--------------------------------------------------------------------------
*/

function MatchedCalculatedFields({
    items = []
}) {

    return (

        <div
            style={{
                marginBottom: '10px'
            }}
        >

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    marginBottom: '8px'
                }}
            >

                <FontAwesomeIcon
                    icon={faCode}
                    style={{
                        color: '#1e447e',
                        fontSize: '12px'
                    }}
                />


                <span
                    style={{
                        fontSize: '12px',
                        fontWeight: 600
                    }}
                >
                    Calculated Fields
                </span>


                <span
                    style={{
                        background:
                            'var(--hover-bg)',
                        color:
                            'var(--text-secondary)',
                        borderRadius:
                            '10px',
                        padding:
                            '2px 7px',
                        fontSize:
                            '10px'
                    }}
                >
                    {items.length}
                </span>

            </div>


            {items.length ? (

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '7px'
                    }}
                >

                    {items.map(
                        (field, index) => {

                            const name =
                                field.name ||
                                field.title ||
                                field.beastmodeName ||
                                'Calculated Field';


                            const formula =
                                field.formula ||
                                field.expression ||
                                field.formulaText ||
                                '-';


                            return (

                                <div
                                    key={
                                        index
                                    }
                                    style={{
                                        border:
                                            '1px solid var(--border-color)',
                                        borderRadius:
                                            '6px',
                                        padding:
                                            '9px 11px'
                                    }}
                                >

                                    <div
                                        style={{
                                            fontSize:
                                                '11px',
                                            fontWeight:
                                                600,
                                            marginBottom:
                                                '5px'
                                        }}
                                    >
                                        {name}
                                    </div>


                                    <code
                                        style={{
                                            display:
                                                'block',
                                            fontSize:
                                                '10px',
                                            color:
                                                '#1e447e',
                                            background:
                                                'var(--hover-bg)',
                                            padding:
                                                '7px',
                                            borderRadius:
                                                '4px',
                                            whiteSpace:
                                                'pre-wrap',
                                            wordBreak:
                                                'break-word'
                                        }}
                                    >
                                        {formula}
                                    </code>

                                </div>

                            );

                        }
                    )}

                </div>

            ) : (

                <div
                    style={{
                        padding:
                            '8px 10px',
                        border:
                            '1px dashed var(--border-color)',
                        borderRadius:
                            '5px',
                        color:
                            'var(--text-tertiary)',
                        fontSize:
                            '11px'
                    }}
                >
                    No calculated-field matches found.
                </div>

            )}

        </div>
    );
}

function getPairwiseDetails(
    first,
    second,
    score,
    pages = [],
    cards = [],
    datasets = [],
    beastmodes = []
) {

    const matchedDetails =
        getMatchedPairDetails(
            first,
            second,
            pages,
            cards,
            datasets,
            beastmodes
        );


    const matched =
        Object.values(
            matchedDetails
        ).reduce(
            (total, items) =>
                total +
                (
                    Array.isArray(items)
                        ? items.length
                        : 0
                ),
            0
        );


    return {

        matched,

        matchedDetails,

        recommendation:
            getPairRecommendation(score)

    };
}

function toArray(value) {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined) return [];
    if (value instanceof Set) return Array.from(value);
    if (typeof value === 'object') {
        if (Array.isArray(value.items)) return value.items;
        if (Array.isArray(value.data)) return value.data;
        if (Array.isArray(value.results)) return value.results;
        if (Array.isArray(value.values)) return value.values;
        return [value];
    }
    return [value];
}


function getPairRecommendation(score) {

    const value = Number(score || 0);

    if (value >= 85) {
        return 'MERGE';
    }

    if (value >= 70) {
        return 'REVIEW';
    }

    return 'SEPARATE';
}


function getMatchedPairDetails(first, second) {
    const details = {
        worksheets: [],
        datasources: [],
        columns: [],
        calculatedFields: []
    };

    compareMatchedValues(details.worksheets, 'Worksheet',
        toArray(first?.worksheets || first?.worksheet || first?.sheets || first?.cards),
        toArray(second?.worksheets || second?.worksheet || second?.sheets || second?.cards)
    );

    compareMatchedValues(details.datasources, 'Datasource',
        toArray(first?.datasources || first?.dataSources || first?.datasource || first?.dataSource || first?.datasourceNames || first?.datasourceIds),
        toArray(second?.datasources || second?.dataSources || second?.datasource || second?.dataSource || second?.datasourceNames || second?.datasourceIds)
    );

    compareMatchedValues(details.columns, 'Column',
        getPageColumnEntries(first),
        getPageColumnEntries(second)
    );

    compareMatchedValues(details.calculatedFields, 'Calculated Field',
        toArray(first?.calculatedFields || first?.calculativeFields || first?.beastmodes || first?.beastModes),
        toArray(second?.calculatedFields || second?.calculativeFields || second?.beastmodes || second?.beastModes)
    );

    return details;
}

function getUnmatchedPairDetails(first, second) {
    const details = {
        worksheets: [],
        datasources: [],
        columns: [],
        calculatedFields: []
    };

    compareUnmatchedValues(details.worksheets, 'Worksheet',
        toArray(first?.worksheets || first?.worksheet || first?.sheets || first?.cards),
        toArray(second?.worksheets || second?.worksheet || second?.sheets || second?.cards)
    );

    compareUnmatchedValues(details.datasources, 'Datasource',
        toArray(first?.datasources || first?.dataSources || first?.datasource || first?.dataSource || first?.datasourceNames || first?.datasourceIds),
        toArray(second?.datasources || second?.dataSources || second?.datasource || second?.dataSource || second?.datasourceNames || second?.datasourceIds)
    );

    compareUnmatchedValues(details.columns, 'Column',
        getPageColumnEntries(first),
        getPageColumnEntries(second)
    );

    compareUnmatchedValues(details.calculatedFields, 'Calculated Field',
        toArray(first?.calculatedFields || first?.calculativeFields || first?.beastmodes || first?.beastModes),
        toArray(second?.calculatedFields || second?.calculativeFields || second?.beastmodes || second?.beastModes)
    );

    return details;
}

function getPageColumnEntries(page) {
    if (Array.isArray(page?.columnDetails) && page.columnDetails.length) {
        return page.columnDetails;
    }

    const names = toArray(page?.columns);
    const types = toArray(page?.columnTypes);
    return names.map((name, index) => ({
        name: getDisplayValue(name),
        dataType: types[index] ? getDisplayValue(types[index]) : ''
    }));
}

function compareMatchedValues(result, type, firstValues, secondValues) {
    if (!Array.isArray(firstValues) || !Array.isArray(secondValues)) return;

    const secondMap = new Map();
    secondValues.forEach(value => {
        const key = getComparisonKey(value);
        if (key && !secondMap.has(key)) secondMap.set(key, value);
    });

    const seen = new Set();
    firstValues.forEach(value => {
        const key = getComparisonKey(value);
        if (!key || seen.has(key)) return;
        seen.add(key);
        const matchingValue = secondMap.get(key);
        if (matchingValue !== undefined) {
            result.push({
                type,
                firstValue: getDisplayValue(value),
                secondValue: getDisplayValue(matchingValue),
                firstDataType: getDataType(value),
                secondDataType: getDataType(matchingValue),
                matched: true,
                match: 'MATCH',
                details: `Same ${type.toLowerCase()}`,
                firstDetail: getComparisonDetail(value),
                secondDetail: getComparisonDetail(matchingValue)
            });
        }
    });
}

function compareUnmatchedValues(result, type, firstValues, secondValues) {
    if (!Array.isArray(firstValues) || !Array.isArray(secondValues)) return;

    const firstMap = new Map();
    const secondMap = new Map();
    firstValues.forEach(value => {
        const key = getComparisonKey(value);
        if (key && !firstMap.has(key)) firstMap.set(key, value);
    });
    secondValues.forEach(value => {
        const key = getComparisonKey(value);
        if (key && !secondMap.has(key)) secondMap.set(key, value);
    });

    for (const [key, value] of firstMap) {
        if (!secondMap.has(key)) {
            result.push({
                type,
                firstValue: getDisplayValue(value),
                secondValue: 'Not present',
                firstDataType: getDataType(value),
                secondDataType: '',
                matched: false,
                match: 'ONLY_A',
                firstDetail: getComparisonDetail(value),
                secondDetail: ''
            });
        }
    }

    for (const [key, value] of secondMap) {
        if (!firstMap.has(key)) {
            result.push({
                type,
                firstValue: 'Not present',
                secondValue: getDisplayValue(value),
                firstDataType: '',
                secondDataType: getDataType(value),
                matched: false,
                match: 'ONLY_B',
                firstDetail: '',
                secondDetail: getComparisonDetail(value)
            });
        }
    }
}

function getDataType(value) {
    if (!value || typeof value !== 'object') return '';
    return String(
        value.dataType || value.data_type || value.datatype ||
        value.columnType || value.type || ''
    );
}

function getComparisonDetail(value) {
    if (!value || typeof value !== 'object') return '';
    const formula = value.formula || value.expression || value.sql || value.description;
    if (formula) return String(formula);
    const query = value.query || value.sqlQuery;
    if (query) return `SQL: ${String(query)}`;
    const metadata = [value.connectorName || value.driver, value.schema, value.tableName, value.queryType, value.updateMode].filter(Boolean).join(' - ');
    return metadata;
}

function getComparisonKey(value) {

    if (
        value === null ||
        value === undefined
    ) {
        return '';
    }


    if (typeof value === 'string') {

        return value
            .trim()
            .toLowerCase();

    }


    if (typeof value === 'number') {

        return String(value);

    }


    if (typeof value === 'object') {

        /*
        |--------------------------------------------------------------------------
        | Try common Domo fields first
        |--------------------------------------------------------------------------
        */

        const name =
            value.name ||
            value.column ||
            value.columnName ||
            value.worksheet ||
            value.worksheetName ||
            value.datasource ||
            value.datasourceName ||
            value.type ||
            value.expression ||
            value.formula ||
            value.id;


        if (name !== undefined) {

            return String(name)
                .trim()
                .toLowerCase();

        }


        /*
        |--------------------------------------------------------------------------
        | Fallback
        |--------------------------------------------------------------------------
        */

        try {

            return JSON.stringify(value)
                .trim()
                .toLowerCase();

        } catch (error) {

            return '';

        }

    }


    return String(value)
        .trim()
        .toLowerCase();
}

function getDisplayValue(value) {

    if (
        value === null ||
        value === undefined
    ) {
        return '-';
    }


    if (
        typeof value === 'string' ||
        typeof value === 'number'
    ) {
        return String(value);
    }


    if (typeof value === 'object') {

        return (
            value.name ||
            value.column ||
            value.columnName ||
            value.worksheet ||
            value.worksheetName ||
            value.datasource ||
            value.datasourceName ||
            value.type ||
            value.expression ||
            value.formula ||
            value.id ||
            '-'
        );

    }


    return String(value);
}

/*
|--------------------------------------------------------------------------
| Recommendation Badge
|--------------------------------------------------------------------------
*/

function RecommendationBadge({
    value
}) {

    const normalized =
        String(value || 'SEPARATE').toUpperCase();

    const merge = normalized === 'MERGE';
    const review = normalized === 'REVIEW';


    return (

        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 9px',
                borderRadius: '12px',
                fontSize: '10px',
                fontWeight: 700,
                background:
                    merge
                        ? '#e8f7ee'
                        : review
                            ? '#fff4d6'
                            : '#fde8e8',
                color:
                    merge
                        ? '#198754'
                        : review
                            ? '#b7791f'
                            : '#dc3545'
            }}
        >

            {normalized}

        </span>
    );
}


/*
|--------------------------------------------------------------------------
| Page Comparison Score
|--------------------------------------------------------------------------
|
| Base:
|   20
|
| Additional points:
|
|   Same datasource     +20
|   Matching worksheet  +15
|   Matching column     +10 each
|   Matching type       +3 each
|   Matching calc field +5 each
|
| Maximum is capped at 97.
|
| This gives the common baseline of 20 and allows pages
| with significant structural overlap to reach 97.
|--------------------------------------------------------------------------
*/

// function getPageComparisonScore(
//     first,
//     second
// ) {

//     if (!first || !second) {
//         return 0;
//     }


//     let score = 20;


//     /*
//     |--------------------------------------------------------------------------
//     | Datasource comparison
//     |--------------------------------------------------------------------------
//     */

//     const firstDatasets =
//         new Set(
//             first.datasourceIds
//                 .map(String)
//         );


//     const secondDatasets =
//         new Set(
//             second.datasourceIds
//                 .map(String)
//         );


//     const sharedDatasets =
//         intersectionSize(
//             firstDatasets,
//             secondDatasets
//         );


//     if (sharedDatasets > 0) {

//         score +=
//             Math.min(
//                 20,
//                 sharedDatasets * 20
//             );

//     }


//     /*
//     |--------------------------------------------------------------------------
//     | Worksheet comparison
//     |--------------------------------------------------------------------------
//     */

//     const firstWorksheets =
//         new Set(
//             first.worksheets
//                 .map(normalizeName)
//                 .filter(Boolean)
//         );


//     const secondWorksheets =
//         new Set(
//             second.worksheets
//                 .map(normalizeName)
//                 .filter(Boolean)
//         );


//     const sharedWorksheets =
//         intersectionSize(
//             firstWorksheets,
//             secondWorksheets
//         );


//     score +=
//         Math.min(
//             15,
//             sharedWorksheets * 15
//         );


//     /*
//     |--------------------------------------------------------------------------
//     | Column comparison
//     |--------------------------------------------------------------------------
//     */

//     const firstColumns =
//         new Set(
//             first.columns
//                 .map(normalizeName)
//                 .filter(Boolean)
//         );


//     const secondColumns =
//         new Set(
//             second.columns
//                 .map(normalizeName)
//                 .filter(Boolean)
//         );


//     const sharedColumns =
//         intersectionSize(
//             firstColumns,
//             secondColumns
//         );


//     score +=
//         Math.min(
//             20,
//             sharedColumns * 10
//         );


//     /*
//     |--------------------------------------------------------------------------
//     | Column type comparison
//     |--------------------------------------------------------------------------
//     */

//     const firstTypes =
//         new Set(
//             first.columnTypes
//                 .map(normalizeName)
//                 .filter(Boolean)
//         );


//     const secondTypes =
//         new Set(
//             second.columnTypes
//                 .map(normalizeName)
//                 .filter(Boolean)
//         );


//     const sharedTypes =
//         intersectionSize(
//             firstTypes,
//             secondTypes
//         );


//     score +=
//         Math.min(
//             10,
//             sharedTypes * 3
//         );


//     /*
//     |--------------------------------------------------------------------------
//     | Calculated fields
//     |--------------------------------------------------------------------------
//     */

//     const firstCalcs =
//         new Set(
//             first.calculatedFieldNames
//                 .map(normalizeName)
//                 .filter(Boolean)
//         );


//     const secondCalcs =
//         new Set(
//             second.calculatedFieldNames
//                 .map(normalizeName)
//                 .filter(Boolean)
//         );


//     const sharedCalcs =
//         intersectionSize(
//             firstCalcs,
//             secondCalcs
//         );


//     score +=
//         Math.min(
//             12,
//             sharedCalcs * 5
//         );


//     /*
//     |--------------------------------------------------------------------------
//     | Cap score
//     |--------------------------------------------------------------------------
//     */

//     return Math.min(
//         97,
//         Math.max(
//             20,
//             score
//         )
//     );
// }

function getPageComparisonScore(first, second) {
    return getPageComparisonBreakdown(first, second).score;
}

/*
|--------------------------------------------------------------------------
| Export Rows: Overall Status
|--------------------------------------------------------------------------
| Flattens the Overall Status tab's pairwise comparisons (every page vs.
| every other page) into one row per pair, for the "Export Tab" button.
|--------------------------------------------------------------------------
*/

function buildOverallStatusExportRows(resources = []) {

    const rows = [];

    for (let i = 0; i < resources.length; i++) {
        for (let j = i + 1; j < resources.length; j++) {
            const score = getPageComparisonScore(resources[i], resources[j]);
            rows.push({
                'Page A': resources[i]?.name || '-',
                'Page B': resources[j]?.name || '-',
                'Similarity Score': score,
                'Recommendation': getPairRecommendation(score)
            });
        }
    }

    return rows.sort((a, b) => b['Similarity Score'] - a['Similarity Score']);
}

/*
|--------------------------------------------------------------------------
| Page Comparison Breakdown
|--------------------------------------------------------------------------
|
| Same weighted-Jaccard calculation as getPageComparisonScore, but returns
| the per-dimension detail (label, weight, similarity %, contributed
| points) alongside the final score, so the Page Comparison Details modal
| can show exactly which dimensions drove the score up or down instead of
| just the final number.
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| Pairwise Dimension Weights (actual scoring engine)
|--------------------------------------------------------------------------
| The real weights used by getPageComparisonBreakdown below to compute
| every pair's score (Overall Status, Merge Sets, Score Breakdown panel).
| These already sum to 100 (i.e. plain points, not a 0-1 fraction) - this
| is a SEPARATE set of weights/keys from the editable "Pairwise Detail"
| weights in AssessmentInfoModal (loaded from /assessment/weights, 0-1
| scale, different key names). Do not confuse the two: the numbers here
| are what actually drives scoring; the /assessment/weights ones are a
| user-facing config surface that isn't wired into this calculation.
|--------------------------------------------------------------------------
*/
const PAIRWISE_DIMENSION_LABELS = {
    datasources: 'Datasources',
    worksheets: 'Worksheets / Cards',
    columns: 'Columns',
    types: 'Column Types',
    calculatedFields: 'Calculated Fields / Beast Modes',
    formulaSignatures: 'Formula Signatures',
    variables: 'Variables',
    sets: 'Sets',
    groups: 'Groups',
    hierarchy: 'Hierarchy',
    filters: 'Filters'
};

const PAIRWISE_DIMENSION_WEIGHTS = {
    datasources: 15,
    worksheets: 10,
    columns: 15,
    types: 8,
    calculatedFields: 6,
    formulaSignatures: 4,
    variables: 8,
    sets: 8,
    groups: 8,
    hierarchy: 8,
    filters: 10
};

function getPageComparisonBreakdown(first, second) {

    const dimensionLabels = PAIRWISE_DIMENSION_LABELS;

    if (!first || !second) {
        return {
            score: 0,
            availableWeight: 0,
            totalWeight: 0,
            dimensions: Object.keys(dimensionLabels).map(key => ({
                key,
                label: dimensionLabels[key],
                weight: 0,
                rawScore: 0,
                similarity: 0,
                points: 0,
                intersection: 0,
                hasData: false
            }))
        };
    }

    /*
    |--------------------------------------------------------------------------
    | Helpers
    |--------------------------------------------------------------------------
    */

    const toNormalizedSet = (value) => {
        if (!Array.isArray(value)) {
            return new Set();
        }

        return new Set(
            value
                .map(item => {
                    if (item === null || item === undefined) {
                        return '';
                    }

                    if (typeof item === 'object') {
                        return normalizeName(
                            item.name ||
                            item.title ||
                            item.label ||
                            item.key ||
                            item.id ||
                            item.expression ||
                            item.formula ||
                            item.column ||
                            item.columnName ||
                            ''
                        );
                    }

                    return normalizeName(item);
                })
                .filter(Boolean)
        );
    };

    /*
    |--------------------------------------------------------------------------
    | Jaccard Similarity
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    | - Both empty = 0
    | - One empty = 0
    | - Both populated = intersection / union
    |
    | We don't give a 100% match to an empty dimension.
    |--------------------------------------------------------------------------
    */

    const jaccardSimilarity = (firstSet, secondSet) => {
        // Nothing to compare
        if (
            firstSet.size === 0 &&
            secondSet.size === 0
        ) {
            return 0;
        }

        // One side has data and the other doesn't
        if (
            firstSet.size === 0 ||
            secondSet.size === 0
        ) {
            return 0;
        }

        const intersection = intersectionSize(
            firstSet,
            secondSet
        );

        const union = new Set([
            ...firstSet,
            ...secondSet
        ]).size;

        if (union === 0) {
            return 0;
        }

        return intersection / union;
    };

    /*
    |--------------------------------------------------------------------------
    | Dimension Weights
    |--------------------------------------------------------------------------
    */

    const weights = PAIRWISE_DIMENSION_WEIGHTS;

    /*
    |--------------------------------------------------------------------------
    | Dimension Score Helper
    |--------------------------------------------------------------------------
    |
    | A dimension only contributes to the final score when at least one
    | side contains data.
    |
    | Example:
    |
    | A columns = []
    | B columns = []
    |
    | This dimension contributes:
    | score  = 0
    | weight = 0
    |
    | But:
    |
    | A columns = ["sales"]
    | B columns = ["sales"]
    |
    | score  = 15
    | weight = 15
    | => 100%
    |--------------------------------------------------------------------------
    */

    const calculateDimension = (
        firstSet,
        secondSet,
        weight
    ) => {
        const firstHasData = firstSet.size > 0;
        const secondHasData = secondSet.size > 0;

        // Neither side has anything to compare
        if (
            !firstHasData &&
            !secondHasData
        ) {
            return {
                score: 0,
                weight: 0,
                similarity: 0,
                intersection: 0,
                hasData: false
            };
        }

        const similarity = jaccardSimilarity(
            firstSet,
            secondSet
        );

        const intersection =
            firstHasData && secondHasData
                ? intersectionSize(
                    firstSet,
                    secondSet
                )
                : 0;

        return {
            score: similarity * weight,
            weight,
            similarity,
            intersection,
            hasData: true
        };
    };

    /*
    |--------------------------------------------------------------------------
    | Datasources
    |--------------------------------------------------------------------------
    */

    const firstDatasources = toNormalizedSet(
        first.datasourceIds ||
        first.datasources ||
        first.dataSources ||
        []
    );

    const secondDatasources = toNormalizedSet(
        second.datasourceIds ||
        second.datasources ||
        second.dataSources ||
        []
    );

    /*
    |--------------------------------------------------------------------------
    | Worksheets
    |--------------------------------------------------------------------------
    */

    const firstWorksheets = toNormalizedSet(
        first.worksheets ||
        first.worksheet ||
        first.sheets ||
        first.cards ||
        []
    );

    const secondWorksheets = toNormalizedSet(
        second.worksheets ||
        second.worksheet ||
        second.sheets ||
        second.cards ||
        []
    );

    /*
    |--------------------------------------------------------------------------
    | Columns
    |--------------------------------------------------------------------------
    */

    const firstColumns = toNormalizedSet(
        first.columns || []
    );

    const secondColumns = toNormalizedSet(
        second.columns || []
    );

    /*
    |--------------------------------------------------------------------------
    | Column Types
    |--------------------------------------------------------------------------
    */

    const firstTypes = toNormalizedSet(
        first.columnTypes || []
    );

    const secondTypes = toNormalizedSet(
        second.columnTypes || []
    );

    /*
    |--------------------------------------------------------------------------
    | Calculated Fields / Beast Modes
    |--------------------------------------------------------------------------
    */

    const firstCalculatedFields = toNormalizedSet(
        first.calculatedFieldNames ||
        first.calculatedFields ||
        first.calculativeFields ||
        first.beastmodes ||
        first.beastModes ||
        []
    );

    const secondCalculatedFields = toNormalizedSet(
        second.calculatedFieldNames ||
        second.calculatedFields ||
        second.calculativeFields ||
        second.beastmodes ||
        second.beastModes ||
        []
    );

    /*
    |--------------------------------------------------------------------------
    | Formula Signatures
    |--------------------------------------------------------------------------
    |
    | Two calculated fields can have completely different names but the
    | exact same underlying logic (e.g. "Total Sales" vs "Revenue", both
    | SUM(Sales_Amount)). Calculated Fields above only compares names, so
    | this dimension separately compares normalized formula text, catching
    | renamed-but-identical calculations that name-matching alone misses.
    |--------------------------------------------------------------------------
    */

    const toFormulaSet = (value) => {
        const items = Array.isArray(value) ? value : [];

        return new Set(
            items
                .map(item => {
                    if (!item || typeof item !== 'object') {
                        return '';
                    }

                    const formula =
                        item.formula ||
                        item.expression ||
                        item.formulaText ||
                        item.sql ||
                        item.calculation ||
                        '';

                    return normalizeFormula(formula);
                })
                .filter(Boolean)
        );
    };

    const firstFormulaSignatures = toFormulaSet(
        first.calculatedFields ||
        first.calculativeFields ||
        first.beastmodes ||
        first.beastModes ||
        []
    );

    const secondFormulaSignatures = toFormulaSet(
        second.calculatedFields ||
        second.calculativeFields ||
        second.beastmodes ||
        second.beastModes ||
        []
    );

    /*
    |--------------------------------------------------------------------------
    | Variables
    |--------------------------------------------------------------------------
    */

    const firstVariables = toNormalizedSet(
        first.variables ||
        first.variable ||
        first.calculations?.variables ||
        []
    );

    const secondVariables = toNormalizedSet(
        second.variables ||
        second.variable ||
        second.calculations?.variables ||
        []
    );

    /*
    |--------------------------------------------------------------------------
    | Sets
    |--------------------------------------------------------------------------
    */

    const firstSets = toNormalizedSet(
        first.sets ||
        first.calculations?.sets ||
        []
    );

    const secondSets = toNormalizedSet(
        second.sets ||
        second.calculations?.sets ||
        []
    );

    /*
    |--------------------------------------------------------------------------
    | Groups
    |--------------------------------------------------------------------------
    */

    const firstGroups = toNormalizedSet(
        first.groups ||
        first.calculations?.groups ||
        []
    );

    const secondGroups = toNormalizedSet(
        second.groups ||
        second.calculations?.groups ||
        []
    );

    /*
    |--------------------------------------------------------------------------
    | Hierarchy / Drill Down
    |--------------------------------------------------------------------------
    */

    const firstHierarchy = toNormalizedSet(
        first.hierarchy ||
        first.hierarchies ||
        first.drillDown ||
        first.drilldown ||
        first.drillDownHierarchy ||
        []
    );

    const secondHierarchy = toNormalizedSet(
        second.hierarchy ||
        second.hierarchies ||
        second.drillDown ||
        second.drilldown ||
        second.drillDownHierarchy ||
        []
    );

    /*
    |--------------------------------------------------------------------------
    | Filters
    |--------------------------------------------------------------------------
    */

    const firstFilters = toNormalizedSet(
        first.filters ||
        first.filter ||
        first.cardFilters ||
        first.pageFilters ||
        []
    );

    const secondFilters = toNormalizedSet(
        second.filters ||
        second.filter ||
        second.cardFilters ||
        second.pageFilters ||
        []
    );

    /*
    |--------------------------------------------------------------------------
    | Calculate Each Dimension
    |--------------------------------------------------------------------------
    */

    const dimensions = [
        {
            key: 'datasources',
            ...calculateDimension(
                firstDatasources,
                secondDatasources,
                weights.datasources
            )
        },

        {
            key: 'worksheets',
            ...calculateDimension(
                firstWorksheets,
                secondWorksheets,
                weights.worksheets
            )
        },

        {
            key: 'columns',
            ...calculateDimension(
                firstColumns,
                secondColumns,
                weights.columns
            )
        },

        {
            key: 'types',
            ...calculateDimension(
                firstTypes,
                secondTypes,
                weights.types
            )
        },

        {
            key: 'calculatedFields',
            ...calculateDimension(
                firstCalculatedFields,
                secondCalculatedFields,
                weights.calculatedFields
            )
        },

        {
            key: 'formulaSignatures',
            ...calculateDimension(
                firstFormulaSignatures,
                secondFormulaSignatures,
                weights.formulaSignatures
            )
        },

        {
            key: 'variables',
            ...calculateDimension(
                firstVariables,
                secondVariables,
                weights.variables
            )
        },

        {
            key: 'sets',
            ...calculateDimension(
                firstSets,
                secondSets,
                weights.sets
            )
        },

        {
            key: 'groups',
            ...calculateDimension(
                firstGroups,
                secondGroups,
                weights.groups
            )
        },

        {
            key: 'hierarchy',
            ...calculateDimension(
                firstHierarchy,
                secondHierarchy,
                weights.hierarchy
            )
        },

        {
            key: 'filters',
            ...calculateDimension(
                firstFilters,
                secondFilters,
                weights.filters
            )
        }
    ];

    /*
    |--------------------------------------------------------------------------
    | Total Weighted Score
    |--------------------------------------------------------------------------
    */

    const totalScore = dimensions.reduce(
        (total, dimension) =>
            total + dimension.score,
        0
    );

    /*
    |--------------------------------------------------------------------------
    | Available Weight
    |--------------------------------------------------------------------------
    |
    | Only dimensions where at least one page has data are included.
    |--------------------------------------------------------------------------
    */

    const availableWeight = dimensions.reduce(
        (total, dimension) =>
            total + dimension.weight,
        0
    );

    /*
    |--------------------------------------------------------------------------
    | No Comparable Data
    |--------------------------------------------------------------------------
    |
    | If both pages have absolutely no data in any dimension,
    | there is no basis for similarity.
    |--------------------------------------------------------------------------
    */

    const totalWeight = Object.values(weights).reduce(
        (total, weight) => total + weight,
        0
    );

    if (availableWeight === 0) {
        return {
            score: 0,
            availableWeight,
            totalWeight,
            dimensions: dimensions.map(dimension => ({
                key: dimension.key,
                label: dimensionLabels[dimension.key],
                weight: dimension.weight,
                rawScore: 0,
                similarity: 0,
                points: 0,
                intersection: dimension.intersection,
                hasData: dimension.hasData
            }))
        };
    }

    /*
    |--------------------------------------------------------------------------
    | Final Normalized Score
    |--------------------------------------------------------------------------
    */

    const finalScore =
        (totalScore / availableWeight) * 100;

    const score = Math.round(
        Math.min(
            100,
            Math.max(
                0,
                finalScore
            )
        )
    );

    /*
    |--------------------------------------------------------------------------
    | Per-Dimension Breakdown
    |--------------------------------------------------------------------------
    |
    | "points" is how much of the final 0-100 score this dimension is
    | responsible for (normalized against availableWeight, same divisor
    | used for the overall score), so the breakdown adds up to `score`.
    |--------------------------------------------------------------------------
    */

    const breakdownDimensions = dimensions.map(dimension => ({
        key: dimension.key,
        label: dimensionLabels[dimension.key],
        weight: dimension.weight,
        // Raw contribution before normalization (similarity x weight), so
        // the UI can show "15 raw -> 31 pts" side by side instead of just
        // the final normalized points, which is what caused the confusion
        // about how a weight of 15 became +31 pts.
        rawScore: Math.round(dimension.score * 100) / 100,
        similarity: dimension.hasData
            ? Math.round(dimension.similarity * 100)
            : 0,
        points: Math.round(
            (dimension.score / availableWeight) * 100
        ),
        intersection: dimension.intersection,
        hasData: dimension.hasData
    }));

    return {
        score,
        availableWeight,
        totalWeight,
        dimensions: breakdownDimensions
    };
}


/*
|--------------------------------------------------------------------------
| Pairwise Details
|--------------------------------------------------------------------------
*/














/*
|--------------------------------------------------------------------------
| Get Page Cards
|--------------------------------------------------------------------------
*/

function getPageCardsForOverallStatus(
    page,
    cards
) {

    if (!page) {
        return [];
    }


    /*
    |--------------------------------------------------------------------------
    | Prefer cards already inside page
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(page.cards) &&
        page.cards.length
    ) {

        return page.cards;

    }


    /*
    |--------------------------------------------------------------------------
    | Otherwise use global cards
    |--------------------------------------------------------------------------
    */

    const pageId =
        page.pageId ||
        page.id ||
        page.urn;


    return cards.filter(
        card => {

            const cardPageId =
                card.pageId ||
                card.page_id ||
                card.page ||
                card.pageUrn;


            return (
                cardPageId !== undefined &&
                String(cardPageId) ===
                    String(pageId)
            );

        }
    );
}


/*
|--------------------------------------------------------------------------
| Normalize Worksheet
|--------------------------------------------------------------------------
*/

function normalizeWorksheet(card) {

    return (
        card.worksheetName ||
        card.worksheet ||
        card.cardName ||
        card.name ||
        card.title ||
        card.id ||
        ''
    );
}


/*
|--------------------------------------------------------------------------
| Dataset ID
|--------------------------------------------------------------------------
*/

function getCardDatasetId(card) {

    return (
        card.dataSetId ||
        card.datasetId ||
        card.datasourceId ||
        card.dataSourceId ||
        card.dataSource?.id ||
        card.dataset?.id ||
        card.dataSet?.id ||
        ''
    );
}


/*
|--------------------------------------------------------------------------
| Dataset Name
|--------------------------------------------------------------------------
*/

function getCardDatasetName(
    card,
    datasets
) {

    const directName =
        card.datasetName ||
        card.dataSetName ||
        card.datasourceName ||
        card.dataSourceName;


    if (directName) {
        return directName;
    }


    const id =
        getCardDatasetId(card);


    if (!id) {
        return '';
    }


    const dataset =
        datasets.find(
            item =>
                String(
                    item.datasetId ||
                    item.id
                ) === String(id)
        );


    return (
        dataset?.datasetName ||
        dataset?.name ||
        dataset?.title ||
        ''
    );
}


/*
|--------------------------------------------------------------------------
| Page Columns
|--------------------------------------------------------------------------
*/

function getPageColumnInfo(
    pageCards,
    datasets
) {

    const columns = [];
    const types = [];
    const columnDetails = [];

    function addColumnDetail(column, datasetId = '') {
        const rawName = typeof column === 'string'
            ? column
            : (
                column?.name ||
                column?.column ||
                column?.columnName ||
                column?.alias ||
                ''
            );

        const name = String(rawName || '').trim();
        if (!name) return;

        const dataType = typeof column === 'object' && column
            ? (
                column.type ||
                column.dataType ||
                column.columnType ||
                column.datatype ||
                column.data_type ||
                ''
            )
            : '';

        columns.push(name);
        if (dataType) types.push(String(dataType));

        const key = `${name.toLowerCase()}|${String(dataType || '').toLowerCase()}|${String(datasetId || '')}`;
        if (!columnDetails.some(item => item._key === key)) {
            columnDetails.push({
                _key: key,
                name,
                dataType: dataType ? String(dataType) : '',
                datasetId: datasetId ? String(datasetId) : ''
            });
        }
    }

    pageCards.forEach(card => {
        const chartColumns = Array.isArray(card?.chartBody?.columns)
            ? card.chartBody.columns : [];
        chartColumns.forEach(column => addColumnDetail(column, getCardDatasetId(card)));

        const cardColumns = Array.isArray(card?.columns)
            ? card.columns : [];
        cardColumns.forEach(column => addColumnDetail(column, getCardDatasetId(card)));

        const schema = Array.isArray(card?.schema) ? card.schema : [];
        schema.forEach(column => addColumnDetail(column, getCardDatasetId(card)));

        const datasetId = getCardDatasetId(card);
        if (datasetId) {
            const dataset = datasets.find(item =>
                String(item?.datasetId || item?.dataSetId || item?.id) === String(datasetId)
            );
            const datasetSchema = dataset?.schema || dataset?.columns || [];
            if (Array.isArray(datasetSchema)) {
                datasetSchema.forEach(column => addColumnDetail(column, datasetId));
            }
        }
    });

    return {
        columns: [...new Set(columns.filter(Boolean).map(String))],
        types: [...new Set(types.filter(Boolean).map(String))],
        columnDetails
    };
}


/*
|--------------------------------------------------------------------------
| Calculated Fields
|--------------------------------------------------------------------------
*/




















































/*
|--------------------------------------------------------------------------
| Intersection Size
|--------------------------------------------------------------------------
*/

function intersectionSize(
    first,
    second
) {

    let count = 0;


    first.forEach(
        value => {

            if (
                second.has(value)
            ) {

                count++;

            }

        }
    );


    return count;
}


/*
|--------------------------------------------------------------------------
| Normalize Name
|--------------------------------------------------------------------------
*/








/*
|--------------------------------------------------------------------------
| Matrix Background
|--------------------------------------------------------------------------
*/

function getMatrixCellBackground(
    score
) {

    if (
        score === null ||
        score === undefined
    ) {

        return 'var(--hover-bg)';

    }

    // Aligned with getPairRecommendation's MERGE/REVIEW/SEPARATE cutoffs
    // (85 / 70) so the matrix coloring and the legend agree with the
    // actual recommendation shown in Pairwise Detail.
    if (score >= 85) {

        return '#d9f2e3';

    }


    if (score >= 70) {

        return '#fff0d9';

    }


    return '#f3f4f6';
}


/*
|--------------------------------------------------------------------------
| Matrix Text Color
|--------------------------------------------------------------------------
*/

function getMatrixCellColor(
    score
) {

    if (
        score === null ||
        score === undefined
    ) {

        return 'var(--text-tertiary)';

    }

    // Aligned with getPairRecommendation's MERGE/REVIEW/SEPARATE cutoffs
    // (85 / 70) so the matrix coloring and the legend agree with the
    // actual recommendation shown in Pairwise Detail.
    if (score >= 85) {

        return '#198754';

    }


    if (score >= 70) {

        return '#d97706';

    }


    return '#6b7280';
}


/*
|--------------------------------------------------------------------------
| Overall Status Styles
|--------------------------------------------------------------------------
*/


























/*
|--------------------------------------------------------------------------
| Get Cards For Page
|--------------------------------------------------------------------------
*/

function getCardsForPage(
    page,
    cards = []
) {

    const pageId =
        String(
            page?.pageId ||
            page?.id ||
            page?.pageID ||
            ''
        );


    /*
    |--------------------------------------------------------------------------
    | If page already contains cards
    |--------------------------------------------------------------------------
    */

    if (
        Array.isArray(
            page?.cards
        ) &&
        page.cards.length
    ) {

        return page.cards;

    }


    /*
    |--------------------------------------------------------------------------
    | Otherwise find cards from global card collection
    |--------------------------------------------------------------------------
    */

    return cards.filter(
        card => {

            const cardPageId =
                String(
                    card?.pageId ||
                    card?.pageID ||
                    card?.page?.id ||
                    ''
                );


            return (
                cardPageId &&
                pageId &&
                cardPageId === pageId
            );

        }
    );

}


/*
|--------------------------------------------------------------------------
| Get Datasets For Page
|--------------------------------------------------------------------------
*/

function getDatasetsForPage(
    page,
    pageCards = [],
    datasets = []
) {

    const ids = new Set();


    /*
    |--------------------------------------------------------------------------
    | Dataset IDs directly from page
    |--------------------------------------------------------------------------
    */

    const pageDatasetIds =
        page?.datasetIds ||
        page?.dataSetIds ||
        page?.datasets ||
        [];


    if (
        Array.isArray(
            pageDatasetIds
        )
    ) {

        pageDatasetIds.forEach(
            dataset => {

                const id =
                    typeof dataset === 'object'
                        ? (
                            dataset.datasetId ||
                            dataset.dataSetId ||
                            dataset.id
                        )
                        : dataset;


                if (id !== undefined &&
                    id !== null
                ) {

                    ids.add(
                        String(id)
                    );

                }

            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Dataset IDs from cards / worksheets
    |--------------------------------------------------------------------------
    */

    pageCards.forEach(
        card => {

            const datasetId =
                card?.dataSetId ||
                card?.datasetId ||
                card?.dataSourceId ||
                card?.datasourceId;


            if (
                datasetId !== undefined &&
                datasetId !== null
            ) {

                ids.add(
                    String(datasetId)
                );

            }

        }
    );


    /*
    |--------------------------------------------------------------------------
    | Convert IDs to dataset objects
    |--------------------------------------------------------------------------
    */

    const result = [];


    ids.forEach(
        id => {

            const dataset =
                datasets.find(
                    item =>
                        String(
                            item?.datasetId ||
                            item?.dataSetId ||
                            item?.id
                        ) === id
                );


            if (dataset) {

                result.push(
                    dataset
                );

            } else {

                result.push({
                    id,
                    datasetId: id,
                    name: id
                });

            }

        }
    );


    return result;

}


/*
|--------------------------------------------------------------------------
| Get Columns For Page
|--------------------------------------------------------------------------
*/

function getColumnsForPage(
    page,
    pageCards = [],
    pageDatasets = [],
    allDatasets = []
) {

    const columns = [];


    /*
    |--------------------------------------------------------------------------
    | 1. Columns from cards
    |--------------------------------------------------------------------------
    */

    pageCards.forEach(
        card => {

            /*
            | chartBody.columns
            */

            if (
                Array.isArray(
                    card?.chartBody?.columns
                )
            ) {

                card.chartBody.columns.forEach(
                    column => {

                        addColumn(
                            columns,
                            column
                        );

                    }
                );

            }


            /*
            | card.columns
            */

            if (
                Array.isArray(
                    card?.columns
                )
            ) {

                card.columns.forEach(
                    column => {

                        addColumn(
                            columns,
                            column
                        );

                    }
                );

            }


            /*
            | card.schema
            */

            if (
                Array.isArray(
                    card?.schema
                )
            ) {

                card.schema.forEach(
                    column => {

                        addColumn(
                            columns,
                            column
                        );

                    }
                );

            }

        }
    );


    /*
    |--------------------------------------------------------------------------
    | 2. Columns from datasets
    |--------------------------------------------------------------------------
    */

    pageDatasets.forEach(
        pageDataset => {

            const datasetId =
                pageDataset?.datasetId ||
                pageDataset?.dataSetId ||
                pageDataset?.id;


            const fullDataset =
                allDatasets.find(
                    dataset =>
                        String(
                            dataset?.datasetId ||
                            dataset?.dataSetId ||
                            dataset?.id
                        ) ===
                        String(datasetId)
                ) ||
                pageDataset;


            const schema =
                fullDataset?.schema ||
                fullDataset?.columns ||
                [];


            if (
                Array.isArray(schema)
            ) {

                schema.forEach(
                    column => {

                        addColumn(
                            columns,
                            column,
                            datasetId
                        );

                    }
                );

            }

        }
    );


    return columns;

}


/*
|--------------------------------------------------------------------------
| Add Column
|--------------------------------------------------------------------------
*/

function addColumn(
    result,
    column,
    datasetId
) {

    if (
        column === null ||
        column === undefined
    ) {

        return;

    }


    /*
    |--------------------------------------------------------------------------
    | String column
    |--------------------------------------------------------------------------
    */

    if (
        typeof column === 'string'
    ) {

        const name =
            column.trim();


        if (!name) {
            return;
        }


        result.push({
            name,
            type: '',
            datasetId
        });

        return;

    }


    /*
    |--------------------------------------------------------------------------
    | Object column
    |--------------------------------------------------------------------------
    */

    const name =
        column.column ||
        column.name ||
        column.alias ||
        column.field ||
        column.columnName;


    if (!name) {
        return;
    }


    const type =
        column.type ||
        column.dataType ||
        column.columnType ||
        column.data_type ||
        '';


    result.push({

        name:
            String(name),

        type:
            String(type),

        datasetId:
            column.datasetId ||
            column.dataSetId ||
            datasetId

    });

}


/*
|--------------------------------------------------------------------------
| Get Calculative Fields For Page
|--------------------------------------------------------------------------
*/

function getCalculativeFieldsForPage(
    page,
    pageCards = [],
    beastmodes = []
) {

    const result = [];


    /*
    |--------------------------------------------------------------------------
    | Beast Modes from page cards
    |--------------------------------------------------------------------------
    */

    pageCards.forEach(
        card => {

            /*
            | calculatedFields
            */

            if (
                Array.isArray(
                    card?.calculatedFields
                )
            ) {

                card.calculatedFields.forEach(
                    field => {

                        addCalculativeField(
                            result,
                            field
                        );

                    }
                );

            }


            /*
            | calculativeFields
            */

            if (
                Array.isArray(
                    card?.calculativeFields
                )
            ) {

                card.calculativeFields.forEach(
                    field => {

                        addCalculativeField(
                            result,
                            field
                        );

                    }
                );

            }


            /*
            | beastmodes
            */

            if (
                Array.isArray(
                    card?.beastmodes
                )
            ) {

                card.beastmodes.forEach(
                    field => {

                        addCalculativeField(
                            result,
                            field
                        );

                    }
                );

            }

        }
    );


    /*
    |--------------------------------------------------------------------------
    | Match global Beast Modes by dataset
    |--------------------------------------------------------------------------
    */

    const datasetIds =
        new Set(
            pageCards
                .map(
                    card =>
                        card?.dataSetId ||
                        card?.datasetId ||
                        card?.dataSourceId ||
                        card?.datasourceId
                )
                .filter(Boolean)
                .map(
                    id =>
                        String(id)
                )
        );


    beastmodes.forEach(
        beastmode => {

            const datasetId =
                beastmode?.datasetId ||
                beastmode?.dataSetId ||
                beastmode?.datasourceId ||
                beastmode?.dataSourceId;


            if (
                datasetId &&
                datasetIds.has(
                    String(datasetId)
                )
            ) {

                addCalculativeField(
                    result,
                    beastmode
                );

            }

        }
    );


    return result;

}


/*
|--------------------------------------------------------------------------
| Add Calculative Field
|--------------------------------------------------------------------------
*/

function addCalculativeField(
    result,
    field
) {

    if (
        !field
    ) {

        return;

    }


    if (
        typeof field === 'string'
    ) {

        result.push({

            name:
                field,

            formula:
                field

        });

        return;

    }


    const name =
        field.name ||
        field.beastmodeName ||
        field.title ||
        field.alias ||
        field.fieldName;


    const formula =
        field.formula ||
        field.expression ||
        field.sql ||
        field.calculation ||
        field.value ||
        '';


    if (
        name ||
        formula
    ) {

        result.push({

            name:
                name ||
                formula,

            formula:
                formula ||
                name

        });

    }

}


/*
|--------------------------------------------------------------------------
| Calculate Page Comparison
|--------------------------------------------------------------------------
*/

function calculatePageComparison(
    pageA,
    pageB
) {

    /*
    |--------------------------------------------------------------------------
    | Unique cards / worksheets
    |--------------------------------------------------------------------------
    */

    const cardsA =
        getUniqueNames(
            pageA.cards,
            getCardName
        );


    const cardsB =
        getUniqueNames(
            pageB.cards,
            getCardName
        );


    /*
    |--------------------------------------------------------------------------
    | Datasources
    |--------------------------------------------------------------------------
    */

    const datasetsA =
        getUniqueNames(
            pageA.datasets,
            getDatasetName
        );


    const datasetsB =
        getUniqueNames(
            pageB.datasets,
            getDatasetName
        );


    /*
    |--------------------------------------------------------------------------
    | Columns
    |--------------------------------------------------------------------------
    */

    const columnsA =
        getUniqueColumns(
            pageA.columns
        );


    const columnsB =
        getUniqueColumns(
            pageB.columns
        );


    /*
    |--------------------------------------------------------------------------
    | Calculative fields
    |--------------------------------------------------------------------------
    */

    const fieldsA =
        getUniqueNames(
            pageA.calculativeFields,
            getCalculativeFieldName
        );


    const fieldsB =
        getUniqueNames(
            pageB.calculativeFields,
            getCalculativeFieldName
        );


    /*
    |--------------------------------------------------------------------------
    | Overlap
    |--------------------------------------------------------------------------
    */

    const cardOverlap =
        getOverlapCount(
            cardsA,
            cardsB
        );


    const datasetOverlap =
        getOverlapCount(
            datasetsA,
            datasetsB
        );


    const columnOverlap =
        getOverlapCount(
            columnsA.names,
            columnsB.names
        );


    const typeOverlap =
        getColumnTypeOverlap(
            pageA.columns,
            pageB.columns
        );


    const calculativeOverlap =
        getOverlapCount(
            fieldsA,
            fieldsB
        );


    /*
    |--------------------------------------------------------------------------
    | Score
    |
    | Worksheet     = 20
    | Datasource    = 20
    | Column        = 20
    | Type          = 20
    | Calculative   = 20
    |
    | Maximum = 100
    |--------------------------------------------------------------------------
    */

    let score = 0;


    /*
    | Worksheet score
    */

    if (
        cardsA.size &&
        cardsB.size
    ) {

        const ratio =
            cardOverlap /
            Math.max(
                cardsA.size,
                cardsB.size
            );


        score +=
            Math.round(
                ratio * 20
            );

    }


    /*
    | Datasource score
    */

    if (
        datasetsA.size &&
        datasetsB.size
    ) {

        const ratio =
            datasetOverlap /
            Math.max(
                datasetsA.size,
                datasetsB.size
            );


        score +=
            Math.round(
                ratio * 20
            );

    }


    /*
    | Column score
    */

    if (
        columnsA.names.size &&
        columnsB.names.size
    ) {

        const ratio =
            columnOverlap /
            Math.max(
                columnsA.names.size,
                columnsB.names.size
            );


        score +=
            Math.round(
                ratio * 20
            );

    }


    /*
    | Column type score
    */

    if (
        typeOverlap.total > 0
    ) {

        const ratio =
            typeOverlap.match /
            typeOverlap.total;


        score +=
            Math.round(
                ratio * 20
            );

    }


    /*
    | Calculative field score
    */

    if (
        fieldsA.size &&
        fieldsB.size
    ) {

        const ratio =
            calculativeOverlap /
            Math.max(
                fieldsA.size,
                fieldsB.size
            );


        score +=
            Math.round(
                ratio * 20
            );

    }


    /*
    |--------------------------------------------------------------------------
    | Return details
    |--------------------------------------------------------------------------
    */

    return {

        score,

        cards:
            cardOverlap,

        datasets:
            datasetOverlap,

        columns:
            columnOverlap,

        types:
            typeOverlap.match,

        calculativeFields:
            calculativeOverlap

    };

}


/*
|--------------------------------------------------------------------------
| Unique Names
|--------------------------------------------------------------------------
*/

function getUniqueNames(
    items = [],
    getName
) {

    return new Set(
        items
            .map(
                item =>
                    normalizeName(
                        getName(item)
                    )
            )
            .filter(Boolean)
    );

}


/*
|--------------------------------------------------------------------------
| Unique Columns
|--------------------------------------------------------------------------
*/

function getUniqueColumns(
    columns = []
) {

    const names =
        new Set();

    const types =
        new Map();


    columns.forEach(
        column => {

            const name =
                normalizeName(
                    column?.name
                );


            if (!name) {
                return;
            }


            names.add(
                name
            );


            types.set(
                name,
                normalizeType(
                    column?.type
                )
            );

        }
    );


    return {
        names,
        types
    };

}


/*
|--------------------------------------------------------------------------
| Column Type Overlap
|--------------------------------------------------------------------------
*/

function getColumnTypeOverlap(
    columnsA = [],
    columnsB = []
) {

    const a =
        getUniqueColumns(
            columnsA
        );


    const b =
        getUniqueColumns(
            columnsB
        );


    let total = 0;
    let match = 0;


    a.names.forEach(
        name => {

            if (
                !b.names.has(name)
            ) {

                return;

            }


            total++;


            const typeA =
                a.types.get(name);


            const typeB =
                b.types.get(name);


            /*
            |--------------------------------------------------------------------------
            | If type is unavailable on either side,
            | consider column name as compatible.
            |--------------------------------------------------------------------------
            */

            if (
                !typeA ||
                !typeB ||
                typeA === typeB
            ) {

                match++;

            }

        }
    );


    return {
        total,
        match
    };

}


/*
|--------------------------------------------------------------------------
| Overlap Count
|--------------------------------------------------------------------------
*/

function getOverlapCount(
    setA,
    setB
) {

    let count = 0;


    setA.forEach(
        value => {

            if (
                setB.has(value)
            ) {

                count++;

            }

        }
    );


    return count;

}


/*
|--------------------------------------------------------------------------
| Name Helpers
|--------------------------------------------------------------------------
*/

function getCardName(
    card
) {

    return (
        card?.cardName ||
        card?.name ||
        card?.title ||
        card?.id ||
        card?.cardId ||
        ''
    );

}


function getDatasetName(
    dataset
) {

    return (
        dataset?.datasetName ||
        dataset?.name ||
        dataset?.title ||
        dataset?.datasetId ||
        dataset?.id ||
        ''
    );

}


function getCalculativeFieldName(
    field
) {

    return (
        field?.name ||
        field?.beastmodeName ||
        field?.title ||
        field?.formula ||
        field?.expression ||
        ''
    );

}


/*
|--------------------------------------------------------------------------
| Normalize Name
|--------------------------------------------------------------------------
*/





/*
|--------------------------------------------------------------------------
| Normalize Type
|--------------------------------------------------------------------------
*/

function normalizeType(
    value
) {

    return String(
        value || ''
    )
        .trim()
        .toLowerCase()
        .replace(
            /\s+/g,
            ''
        );

}


/*
|--------------------------------------------------------------------------
| Matrix Tooltip
|--------------------------------------------------------------------------
*/

function getComparisonTooltip(
    pageA,
    pageB,
    comparison
) {

    if (!comparison) {
        return '';
    }


    return [

        `${pageA.name}, ${pageB.name}`,

        `Score: ${comparison.score}`,

        `Worksheets: ${comparison.cards}`,

        `Datasources: ${comparison.datasets}`,

        `Columns: ${comparison.columns}`,

        `Compatible types: ${comparison.types}`,

        `Calculative fields: ${comparison.calculativeFields}`

    ].join('\n');

}


/*
|--------------------------------------------------------------------------
| Count Unique Resources
|--------------------------------------------------------------------------
*/

function countUniqueResources(
    pages,
    property
) {

    const set =
        new Set();


    pages.forEach(
        page => {

            (
                page[property] ||
                []
            ).forEach(
                item => {

                    let value;


                    if (
                        property ===
                        'columns'
                    ) {

                        value =
                            item?.name;

                    } else if (
                        property ===
                        'calculativeFields'
                    ) {

                        value =
                            item?.name ||
                            item?.formula;

                    } else {

                        value =
                            item?.datasetId ||
                            item?.id ||
                            item?.name;

                    }


                    if (value) {

                        set.add(
                            normalizeName(
                                value
                            )
                        );

                    }

                }
            );

        }
    );


    return set.size;

}


/*
|--------------------------------------------------------------------------
| Overall Status Tab
|--------------------------------------------------------------------------
|
| Only PAGES are displayed.
|
| Page-to-page comparison is based on:
|
|   1. Worksheets / Cards
|   2. Datasources / Datasets
|   3. Columns
|   4. Column Types
|   5. Calculated Fields / Beast Modes
|
|--------------------------------------------------------------------------
*/




















































































































































/*
|--------------------------------------------------------------------------
| Compare Two Pages
|--------------------------------------------------------------------------
*/

function comparePages(
    pageA,
    pageB,
    allCards = [],
    allDatasets = [],
    allBeastmodes = []
) {

    /*
    |--------------------------------------------------------------------------
    | Extract cards from pages
    |--------------------------------------------------------------------------
    */

    const cardsA =
        getPageCards(
            pageA,
            allCards
        );

    const cardsB =
        getPageCards(
            pageB,
            allCards
        );


    /*
    |--------------------------------------------------------------------------
    | Worksheet comparison
    |--------------------------------------------------------------------------
    */

    const worksheetMatches =
        countMatchingItems(
            cardsA,
            cardsB,
            getCardIdentity
        );


    /*
    |--------------------------------------------------------------------------
    | Datasource comparison
    |--------------------------------------------------------------------------
    */

    const datasourceA =
        getPageDatasources(
            pageA,
            cardsA,
            allDatasets
        );

    const datasourceB =
        getPageDatasources(
            pageB,
            cardsB,
            allDatasets
        );


    const datasourceMatches =
        countMatchingItems(
            datasourceA,
            datasourceB,
            getDatasetIdentity
        );


    /*
    |--------------------------------------------------------------------------
    | Column comparison
    |--------------------------------------------------------------------------
    */

    const columnsA =
        getPageColumns(
            pageA,
            cardsA
        );

    const columnsB =
        getPageColumns(
            pageB,
            cardsB
        );


    const columnMatches =
        countMatchingItems(
            columnsA,
            columnsB,
            getColumnIdentity
        );


    /*
    |--------------------------------------------------------------------------
    | Column type comparison
    |--------------------------------------------------------------------------
    */

    const typeMatches =
        countMatchingColumnTypes(
            columnsA,
            columnsB
        );


    /*
    |--------------------------------------------------------------------------
    | Calculated fields
    |--------------------------------------------------------------------------
    */

    const calculatedA =
        getPageCalculatedFields(
            pageA,
            cardsA,
            allBeastmodes
        );

    const calculatedB =
        getPageCalculatedFields(
            pageB,
            cardsB,
            allBeastmodes
        );


    const calculatedFieldMatches =
        countMatchingItems(
            calculatedA,
            calculatedB,
            getCalculatedFieldIdentity
        );


    /*
    |--------------------------------------------------------------------------
    | Total matches
    |--------------------------------------------------------------------------
    */

    const matches =
        worksheetMatches +
        datasourceMatches +
        columnMatches +
        typeMatches +
        calculatedFieldMatches;


    /*
    |--------------------------------------------------------------------------
    | Score
    |
    | Base score:
    |
    | 20 = pages are being compared
    |
    | Additional score is based on shared structures.
    |--------------------------------------------------------------------------
    */

    const score =
        calculatePagePairScore({
            worksheetMatches,
            datasourceMatches,
            columnMatches,
            typeMatches,
            calculatedFieldMatches
        });


    /*
    |--------------------------------------------------------------------------
    | Recommendation
    |--------------------------------------------------------------------------
    */

    const recommendation =
        score >= 80
            ? 'MERGE'
            : 'SEPARATE';


    return {

        score,

        matches,

        worksheetMatches,

        datasourceMatches,

        columnMatches,

        typeMatches,

        calculatedFieldMatches,

        recommendation

    };

}


/*
|--------------------------------------------------------------------------
| Get Page Cards
|--------------------------------------------------------------------------
*/

function getPageCards(
    page,
    allCards = []
) {

    if (
        Array.isArray(page?.cards)
    ) {

        return page.cards;

    }


    const pageId =
        String(
            page?.pageId ||
            page?.id ||
            ''
        );


    if (!pageId) {
        return [];
    }


    return allCards.filter(
        card =>
            String(
                card?.pageId ||
                card?.page?.id ||
                ''
            ) === pageId
    );

}


/*
|--------------------------------------------------------------------------
| Get Page Datasources
|--------------------------------------------------------------------------
*/

function getPageDatasources(
    page,
    pageCards = [],
    allDatasets = []
) {

    const ids = [];


    /*
    |----------------------------------------------------------------------
    | Page-level datasets
    |----------------------------------------------------------------------
    */

    if (
        Array.isArray(page?.datasets)
    ) {

        page.datasets.forEach(
            dataset => {

                const id =
                    dataset?.datasetId ||
                    dataset?.dataSetId ||
                    dataset?.id;

                if (id) {
                    ids.push(String(id));
                }

            }
        );

    }


    /*
    |----------------------------------------------------------------------
    | Card datasets
    |----------------------------------------------------------------------
    */

    pageCards.forEach(
        card => {

            const id =
                card?.dataSetId ||
                card?.datasetId ||
                card?.datasourceId ||
                card?.dataSourceId ||
                card?.dataset?.id ||
                card?.datasource?.id;

            if (id) {
                ids.push(String(id));
            }

        }
    );


    /*
    |----------------------------------------------------------------------
    | Return dataset objects when possible
    |----------------------------------------------------------------------
    */

    const uniqueIds =
        [...new Set(ids)];


    return uniqueIds.map(
        id => {

            const dataset =
                allDatasets.find(
                    item =>
                        String(
                            item?.datasetId ||
                            item?.dataSetId ||
                            item?.id
                        ) === id
                );


            return dataset || {
                id
            };

        }
    );

}


/*
|--------------------------------------------------------------------------
| Get Page Columns
|--------------------------------------------------------------------------
*/

function getPageColumns(
    page,
    pageCards = []
) {

    const result = [];


    /*
    |----------------------------------------------------------------------
    | Page columns
    |----------------------------------------------------------------------
    */

    if (
        Array.isArray(page?.columns)
    ) {

        result.push(
            ...page.columns
        );

    }


    /*
    |----------------------------------------------------------------------
    | Card columns
    |----------------------------------------------------------------------
    */

    pageCards.forEach(
        card => {

            const candidates = [

                card?.columns,

                card?.chartBody?.columns,

                card?.chartBody?.column,

                card?.groupBy,

                card?.filters,

                card?.dimensions,

                card?.measures

            ];


            candidates.forEach(
                value => {

                    if (
                        Array.isArray(value)
                    ) {

                        result.push(
                            ...value
                        );

                    }

                }
            );

        }
    );


    return deduplicateItems(
        result,
        getColumnIdentity
    );

}


/*
|--------------------------------------------------------------------------
| Get Calculated Fields
|--------------------------------------------------------------------------
*/

function getPageCalculatedFields(
    page,
    pageCards = [],
    allBeastmodes = []
) {

    const result = [];


    /*
    |----------------------------------------------------------------------
    | Page calculated fields
    |----------------------------------------------------------------------
    */

    if (
        Array.isArray(
            page?.calculatedFields
        )
    ) {

        result.push(
            ...page.calculatedFields
        );

    }


    /*
    |----------------------------------------------------------------------
    | Card calculated fields
    |----------------------------------------------------------------------
    */

    pageCards.forEach(
        card => {

            const fields = [

                card?.calculatedFields,

                card?.beastmodes,

                card?.beastModes,

                card?.calculativeFields

            ];


            fields.forEach(
                value => {

                    if (
                        Array.isArray(value)
                    ) {

                        result.push(
                            ...value
                        );

                    }

                }
            );

        }
    );


    /*
    |----------------------------------------------------------------------
    | Match global Beast Modes to page datasets
    |----------------------------------------------------------------------
    */

    const pageDatasetIds =
        new Set(
            getPageDatasources(
                page,
                pageCards,
                []
            )
                .map(
                    dataset =>
                        String(
                            dataset?.datasetId ||
                            dataset?.dataSetId ||
                            dataset?.id
                        )
                )
        );


    allBeastmodes.forEach(
        beastmode => {

            const datasetId =
                beastmode?.datasetId ||
                beastmode?.dataSetId ||
                beastmode?.datasourceId;


            if (
                datasetId &&
                pageDatasetIds.has(
                    String(datasetId)
                )
            ) {

                result.push(
                    beastmode
                );

            }

        }
    );


    return deduplicateItems(
        result,
        getCalculatedFieldIdentity
    );

}


/*
|--------------------------------------------------------------------------
| Generic Matching
|--------------------------------------------------------------------------
*/

function countMatchingItems(
    listA = [],
    listB = [],
    identityFunction
) {

    const keysB =
        new Set(
            listB
                .map(identityFunction)
                .filter(Boolean)
        );


    return listA.filter(
        item =>
            keysB.has(
                identityFunction(item)
            )
    ).length;

}


/*
|--------------------------------------------------------------------------
| Column Type Matching
|--------------------------------------------------------------------------
*/

function countMatchingColumnTypes(
    columnsA = [],
    columnsB = []
) {

    const mapB =
        new Map();


    columnsB.forEach(
        column => {

            const name =
                normalizeName(
                    column?.column ||
                    column?.name ||
                    column?.columnName
                );


            const type =
                normalizeName(
                    column?.type ||
                    column?.dataType ||
                    column?.columnType
                );


            if (
                name &&
                type
            ) {

                mapB.set(
                    name,
                    type
                );

            }

        }
    );


    return columnsA.filter(
        column => {

            const name =
                normalizeName(
                    column?.column ||
                    column?.name ||
                    column?.columnName
                );


            const type =
                normalizeName(
                    column?.type ||
                    column?.dataType ||
                    column?.columnType
                );


            return (
                name &&
                type &&
                mapB.get(name) === type
            );

        }
    ).length;

}


/*
|--------------------------------------------------------------------------
| Identity - Card / Worksheet
|--------------------------------------------------------------------------
*/

function getCardIdentity(card) {

    return normalizeName(
        card?.cardName ||
        card?.name ||
        card?.title ||
        card?.id ||
        card?.cardId
    );

}


/*
|--------------------------------------------------------------------------
| Identity - Dataset
|--------------------------------------------------------------------------
*/

function getDatasetIdentity(dataset) {

    return normalizeName(
        dataset?.datasetId ||
        dataset?.dataSetId ||
        dataset?.id ||
        dataset?.datasetName ||
        dataset?.name
    );

}


/*
|--------------------------------------------------------------------------
| Identity - Column
|--------------------------------------------------------------------------
*/

function getColumnIdentity(column) {

    return normalizeName(
        column?.column ||
        column?.columnName ||
        column?.name
    );

}


/*
|--------------------------------------------------------------------------
| Identity - Calculated Field
|--------------------------------------------------------------------------
*/

function getCalculatedFieldIdentity(
    field
) {

    const name =
        normalizeName(
            field?.name ||
            field?.fieldName ||
            field?.beastmodeName ||
            field?.title
        );


    const formula =
        normalizeFormula(
            field?.formula ||
            field?.expression ||
            field?.beastmodeFormula
        );


    /*
    |--------------------------------------------------------------------------
    | Prefer formula because two fields with different names but the same
    | calculation are still effectively the same calculation.
    |--------------------------------------------------------------------------
    */

    return (
        formula
            ? `${name}|${formula}`
            : name
    );

}


/*
|--------------------------------------------------------------------------
| Deduplicate
|--------------------------------------------------------------------------
*/

function deduplicateItems(
    items = [],
    identityFunction
) {

    const seen =
        new Set();


    return items.filter(
        item => {

            const key =
                identityFunction(item);


            if (!key) {
                return false;
            }


            if (
                seen.has(key)
            ) {

                return false;

            }


            seen.add(key);

            return true;

        }
    );

}


/*
|--------------------------------------------------------------------------
| Normalize Name
|--------------------------------------------------------------------------
*/

function normalizeName(
    value
) {

    if (
        value === null ||
        value === undefined
    ) {

        return '';

    }


    return String(value)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

}

/*
|--------------------------------------------------------------------------
| Normalize Formula
|--------------------------------------------------------------------------
|
| Used by the Formula Signatures comparison dimension (and elsewhere calc
| field formulas are displayed/compared) so two calculated fields with
| different names but equivalent logic (e.g. "Total Sales" vs "Revenue",
| both SUM(Sales_Amount)) are recognized as a match. Strips
| whitespace/case/quoting differences and bracket styles that don't change
| the underlying calculation, without attempting a full expression parse.
|--------------------------------------------------------------------------
*/

function normalizeFormula(
    value
) {

    if (
        value === null ||
        value === undefined
    ) {

        return '';

    }

    return String(value)
        .trim()
        .toLowerCase()
        // Unify bracket/quote styles used to reference column names
        // (`col`, [col], "col") so the same reference in different Domo
        // export formats still matches.
        .replace(/[`"]/g, '')
        .replace(/[[\]]/g, '')
        // Collapse all whitespace (including newlines from multi-line
        // formulas) down to a single space.
        .replace(/\s+/g, ' ')
        // Remove spacing immediately inside parentheses/commas so
        // "SUM( a , b )" and "SUM(a,b)" normalize identically.
        .replace(/\(\s+/g, '(')
        .replace(/\s+\)/g, ')')
        .replace(/\s*,\s*/g, ',')
        .trim();

}


/*
|--------------------------------------------------------------------------
| Calculate Page Pair Score
|--------------------------------------------------------------------------
|
| Base:
|
|   20
|
| Worksheet:
|
|   +25 per matching worksheet
|
| Datasource:
|
|   +20 per matching datasource
|
| Column:
|
|   +5 per matching column
|
| Column type:
|
|   +2 per matching type
|
| Calculated field:
|
|   +10 per matching calculated field
|
| Maximum:
|
|   97
|
| This gives the same style of score as:
|
|   20 = basic relationship
|   21 = slightly stronger relationship
|   97 = very strong relationship
|
|--------------------------------------------------------------------------
*/

function calculatePagePairScore({
    worksheetMatches = 0,
    datasourceMatches = 0,
    columnMatches = 0,
    typeMatches = 0,
    calculatedFieldMatches = 0
}) {

    let score = 20;


    /*
    |--------------------------------------------------------------------------
    | Worksheet
    |--------------------------------------------------------------------------
    */

    score +=
        worksheetMatches * 15;


    /*
    |--------------------------------------------------------------------------
    | Datasource
    |--------------------------------------------------------------------------
    */

    score +=
        datasourceMatches * 12;


    /*
    |--------------------------------------------------------------------------
    | Columns
    |--------------------------------------------------------------------------
    */

    score +=
        Math.min(
            columnMatches * 3,
            20
        );


    /*
    |--------------------------------------------------------------------------
    | Column types
    |--------------------------------------------------------------------------
    */

    score +=
        Math.min(
            typeMatches,
            10
        );


    /*
    |--------------------------------------------------------------------------
    | Calculated fields
    |--------------------------------------------------------------------------
    */

    score +=
        Math.min(
            calculatedFieldMatches * 5,
            15
        );


    /*
    |--------------------------------------------------------------------------
    | Maximum score
    |--------------------------------------------------------------------------
    */

    return Math.min(
        97,
        score
    );

}


/*
|--------------------------------------------------------------------------
| Overall Status Metric
|--------------------------------------------------------------------------
*/

function OverallStatusMetric({
    label,
    value
}) {

    return (

        <div
            style={{
                border:
                    '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '12px 14px',
                background:
                    'var(--card-bg, #fff)'
            }}
        >

            <div
                style={{
                    fontSize: '11px',
                    color:
                        'var(--text-secondary)',
                    marginBottom: '5px'
                }}
            >
                {label}
            </div>


            <div
                style={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#1e447e'
                }}
            >
                {value}
            </div>

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| Recommendation Badge
|--------------------------------------------------------------------------
*/

function OverallRecommendationBadge({
    value
}) {

    const merge =
        value === 'MERGE';


    return (

        <span
            style={{
                display:
                    'inline-flex',
                alignItems:
                    'center',
                justifyContent:
                    'center',
                padding:
                    '4px 10px',
                borderRadius:
                    '12px',
                fontSize:
                    '10px',
                fontWeight:
                    700,
                background:
                    merge
                        ? '#e8f7ee'
                        : '#f1f3f5',
                color:
                    merge
                        ? '#198754'
                        : '#6c757d'
            }}
        >

            {value}

        </span>

    );

}


/*
|--------------------------------------------------------------------------
| Score Background
|--------------------------------------------------------------------------
*/

function getOverallScoreBackground(
    score
) {

    if (
        score >= 80
    ) {

        return '#e8f7ee';

    }


    if (
        score >= 40
    ) {

        return '#fff4e5';

    }


    return '#f1f3f5';

}


/*
|--------------------------------------------------------------------------
| Score Color
|--------------------------------------------------------------------------
*/

function getOverallScoreColor(
    score
) {

    if (
        score >= 80
    ) {

        return '#198754';

    }


    if (
        score >= 40
    ) {

        return '#d97706';

    }


    return '#6c757d';

}


/*
|--------------------------------------------------------------------------
| Comparison Explanation
|--------------------------------------------------------------------------
*/

function ComparisonExplanation({
    title,
    text
}) {

    return (

        <div>

            <div
                style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    marginBottom: '3px'
                }}
            >
                {title}
            </div>


            <div
                style={{
                    fontSize: '10px',
                    lineHeight: 1.5,
                    color:
                        'var(--text-secondary)'
                }}
            >
                {text}
            </div>

        </div>

    );

}


/*
|--------------------------------------------------------------------------
| Table Styles
|--------------------------------------------------------------------------
*/

const overallStatusHeaderStyle = {

    textAlign: 'left',

    padding: '11px 14px',

    borderBottom:
        '1px solid var(--border-color)',

    color:
        'var(--text-secondary)',

    fontWeight: 600,

    whiteSpace: 'nowrap',

    background:
        'var(--hover-bg)'

};


const overallStatusRowHeaderStyle = {

    padding: '11px 14px',

    borderRight:
        '1px solid var(--border-color)',

    color:
        'var(--text-primary)',

    background:
        'var(--card-bg, #fff)'

};


const overallStatusCellStyle = {

    padding: '11px 14px',

    textAlign: 'center',

    borderBottom:
        '1px solid var(--border-color)',

    borderRight:
        '1px solid var(--border-color)',

    minWidth: '80px'

};

/*
|--------------------------------------------------------------------------
| Assessment Summary Card
|--------------------------------------------------------------------------
*/

function AssessmentSummaryCard({
    title,
    value,
    subtitle,
    icon
}) {

    return (

        <div
            style={summaryCardStyle}
        >

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >

                <div>

                    <div style={smallLabelStyle}>
                        {title}
                    </div>

                    <div
                        style={{
                            fontSize: '28px',
                            fontWeight: 700,
                            color: '#1e447e'
                        }}
                    >
                        {value}
                    </div>

                    <div style={smallLabelStyle}>
                        {subtitle}
                    </div>

                </div>


                {/* <IconBox icon={icon} /> */}

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Complexity Card
|--------------------------------------------------------------------------
*/

function ComplexityCard({
    counts,
    overall
}) {

    return (

        <div style={summaryCardStyle}>

            <div style={smallLabelStyle}>
                Complexity
            </div>

            {/* <div
                style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color:
                        getStatusColor(overall),
                    marginBottom: '12px'
                }}
            >
                {overall}
            </div> */}

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '7px'
                }}
            >

                <ComplexityRow
                    label="Low"
                    value={counts.low}
                    status="low"
                />

                <ComplexityRow
                    label="Medium"
                    value={counts.medium}
                    status="medium"
                />

                <ComplexityRow
                    label="High"
                    value={counts.high}
                    status="high"
                />

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Complexity Row
|--------------------------------------------------------------------------
*/

function ComplexityRow({
    label,
    value,
    status
}) {

    return (

        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '11px'
            }}
        >

            <div
                style={{
                    display: 'flex',
                    gap: '7px',
                    alignItems: 'center'
                }}
            >

                <span
                    style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        background:
                            getStatusColor(status)
                    }}
                />

                {label}

            </div>

            <strong>
                {value}
            </strong>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Resource Summary
|--------------------------------------------------------------------------
*/

function ResourceSummaryCard({
    summary
}) {

    const total =
        Number(summary.datasets || 0) +
        Number(summary.cards || 0) +
        Number(summary.pages || 0) +
        Number(summary.dataflows || 0) +
        Number(summary.beastmodes || 0);


    return (

        <div style={summaryCardStyle}>

            <div style={smallLabelStyle}>
                Discovered Resources
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <div
                style={{
                    fontSize: '27px',
                    fontWeight: 700,
                    marginBottom: '8px'
                }}
            >
                {total}
            </div>

            {/* <div
                style={{
                    fontSize: '11px',
                    lineHeight: 1.7,
                    color:
                        'var(--text-secondary)'
                }}
            >
                {summary.datasets} Datasets - {' '}
                {summary.cards} Cards
                <br />
                {summary.pages} Pages - {' '}
                {summary.dataflows} DataFlows
                <br />
                {Number(summary.beastmodes || 0)} Beast Modes
                <br />
                {summary.users ?? 0} Users -  {summary.groups ?? 0} Groups
                <br />
                {summary.dependencies ?? summary.lineageEdges ?? 0} Dependencies
            </div> */}
            </div>
        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Resource Metric
|--------------------------------------------------------------------------
*/

function ResourceMetric({
    title,
    value,
    icon,
    breakdown
}) {

    return (

        <div
            style={{
                background:
                    'var(--card-bg)',
                border:
                    '1px solid var(--border-color)',
                borderRadius: 'var(--radius-lg)',
                padding: '13px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '11px',
                boxShadow: 'var(--shadow-sm)',
                transition: 'box-shadow .15s, transform .15s'
            }}
            onMouseOver={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseOut={e => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.transform = 'none'; }}
        >

            <IconBox icon={icon} />

            <div>

                <div style={smallLabelStyle}>
                    {title}
                </div>

                {!(Array.isArray(breakdown) && breakdown.length > 0) && (
                    <div
                        style={{
                            fontSize: '17px',
                            fontWeight: 600
                        }}
                    >
                        {value || 0}
                    </div>
                )}

                {Array.isArray(breakdown) && breakdown.length > 0 && (
                    <div
                        style={{
                            display: 'flex',
                            gap: '8px',
                            marginTop: '3px'
                        }}
                    >
                        {breakdown.map(item => (
                            <div
                                key={item.label}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '10px',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                <span
                                    style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        background: item.color,
                                        display: 'inline-block'
                                    }}
                                />
                                {item.label} {item.value ?? 0}
                            </div>
                        ))}
                    </div>
                )}

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Icon Box
|--------------------------------------------------------------------------
*/

function IconBox({
    icon
}) {

    return (

        <div
            style={{
                width: '38px',
                height: '38px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--accent-light)',
                color: 'var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}
        >

            <FontAwesomeIcon
                icon={icon}
            />

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Assessment Overview
|--------------------------------------------------------------------------
*/

// 
/*
|--------------------------------------------------------------------------
| Merge Sets
|--------------------------------------------------------------------------
| Groups pages with strong pairwise similarity into recommended merge sets.
| Connector names are resolved from the underlying Domo datasets/cards when
| the source metadata is available.
*/
/*
|--------------------------------------------------------------------------
| Export Rows: Merge Sets
|--------------------------------------------------------------------------
*/

function buildMergeSetsExportRows(mergeSets = [], isolated = []) {

    const rows = [];

    mergeSets.forEach(set => {
        set.resources.forEach(resource => {
            rows.push({
                'Merge Set': `Set #${set.number}`,
                'Page': resource.name || '-',
                'Average Score': set.avgScore,
                'Pages in Set': set.resources.length,
                'Shared Datasources': (set.sharedDatasources || []).map(d => d.name || d).join(', ') || '-'
            });
        });
    });

    isolated.forEach(resource => {
        rows.push({
            'Merge Set': 'Not merged',
            'Page': resource.name || '-',
            'Average Score': '-',
            'Pages in Set': 1,
            'Shared Datasources': '-'
        });
    });

    return rows;
}

function MergeSetsTab({
    pages = [],
    cards = [],
    datasets = [],
    beastmodes = []
}) {

    const [viewMode, setViewMode] = useState('sets');

    const resources = useMemo(() => buildOverallComparisonResources(
        pages,
        cards,
        datasets,
        beastmodes
    ), [pages, cards, datasets, beastmodes]);

    const mergeSets = useMemo(() => {
        if (resources.length < 2) return [];

        const parent = resources.map((_, index) => index);

        const find = (value) => {
            let current = value;
            while (parent[current] !== current) {
                parent[current] = parent[parent[current]];
                current = parent[current];
            }
            return current;
        };

        const union = (a, b) => {
            const rootA = find(a);
            const rootB = find(b);
            if (rootA !== rootB) parent[rootB] = rootA;
        };

        const pairs = [];

        for (let i = 0; i < resources.length; i++) {
            for (let j = i + 1; j < resources.length; j++) {
                const score = getPageComparisonScore(resources[i], resources[j]);
                if (score >= 85) {
                    union(i, j);
                    pairs.push({
                        a: i,
                        b: j,
                        score,
                        detail: getPairwiseDetails(resources[i], resources[j], score)
                    });
                }
            }
        }

        const groups = new Map();
        resources.forEach((resource, index) => {
            const root = find(index);
            if (!groups.has(root)) groups.set(root, []);
            groups.get(root).push(index);
        });

        return Array.from(groups.values())
            .filter(indices => indices.length >= 2)
            .map((indices, index) => {
                const setPairs = pairs.filter(pair =>
                    indices.includes(pair.a) && indices.includes(pair.b)
                );
                const avgScore = setPairs.length
                    ? Math.round(setPairs.reduce((sum, pair) => sum + pair.score, 0) / setPairs.length)
                    : 0;

                const setResources = indices.map(i => resources[i]);
                const sharedDatasources = getSharedDatasourceDetails(setResources, datasets);

                return {
                    id: `merge-set-${index + 1}`,
                    number: index + 1,
                    resources: setResources,
                    pairs: setPairs,
                    avgScore,
                    sharedDatasources
                };
            });
    }, [resources, datasets]);

    const isolated = useMemo(() => {
        const mergedIds = new Set(
            mergeSets.flatMap(set => set.resources.map(resource => String(resource.id)))
        );
        return resources.filter(resource => !mergedIds.has(String(resource.id)));
    }, [resources, mergeSets]);

    if (!resources.length) {
        return <EmptyState message="No pages are available to build merge sets." />;
    }

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', gap: '12px' }}>
                <div>
                    <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '5px' }}>
                        Merge Sets
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Pages with a similarity score of 85 or higher are grouped into recommended merge sets. Datasource names and connector/source types are shown when Domo returns them.
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <ExportTabButton
                        rows={buildMergeSetsExportRows(mergeSets, isolated)}
                        sheetName="Merge Sets"
                    />
                    <ViewModeToggle
                        viewMode={viewMode}
                        onChange={setViewMode}
                        options={[
                            { key: 'sets', label: 'Table', icon: faObjectGroup },
                            { key: 'network', label: 'Diagram', icon: faSitemap }
                        ]}
                    />
                </div>
            </div>

            {viewMode === 'network' ? (

                <MergeSetsClusterDiagram
                    mergeSets={mergeSets}
                    isolated={isolated}
                />

            ) : (

            <>

            {mergeSets.length ? mergeSets.map(set => (
                <div key={set.id} style={{
                    marginBottom: '16px',
                    border: '1px solid var(--border-color)',
                    borderLeft: '3px solid #18a66b',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: 'var(--card-bg, #fff)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        padding: '12px 14px',
                        background: 'var(--hover-bg)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <strong>Set #{set.number}</strong>
                            <span style={{
                                background: '#e8fbf2',
                                color: '#078653',
                                border: '1px solid #9ce7c7',
                                borderRadius: '12px',
                                padding: '3px 9px',
                                fontSize: '10px',
                                fontWeight: 700
                            }}>
                                MERGE
                            </span>
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                avg score: <strong style={{ color: '#078653' }}>{set.avgScore}/100</strong>
                            </span>
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {set.resources.length} page(s)
                        </div>
                    </div>

                    <div style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '12px' }}>
                            {set.resources.map(resource => (
                                <span key={resource.key} style={{
                                    padding: '5px 8px',
                                    border: '1px solid #cbd8ee',
                                    borderRadius: '5px',
                                    background: '#f4f7fc',
                                    color: '#1e447e',
                                    fontSize: '11px',
                                    fontWeight: 600
                                }}>
                                    {resource.name}
                                </span>
                            ))}
                        </div>

                        <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                    <tr>
                                        {['PAGE', 'DATASOURCES', 'CONNECTOR / SOURCE', 'PAIRWISE SCORE'].map(header => (
                                            <th key={header} style={{ ...tableHeaderStyle, whiteSpace: 'nowrap' }}>{header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {set.resources.map(resource => {
                                        const sourceDetails = getPageDatasourceDetails(resource, datasets);
                                        const connectorNames = [...new Set(sourceDetails.map(item => item.connectorName).filter(Boolean))];
                                        return (
                                            <tr key={`set-page-${resource.key}`}>
                                                <td style={{ ...tableCellStyle, fontWeight: 600 }}>{resource.name}</td>
                                                <td style={tableCellStyle}>
                                                    {sourceDetails.length ? sourceDetails.map(item => (
                                                        <div key={`${resource.key}-${item.id || item.name}`} style={{ marginBottom: '3px' }}>
                                                            {item.name || item.id || '-'}
                                                        </div>
                                                    )) : '-'}
                                                </td>
                                                <td style={tableCellStyle}>
                                                    {connectorNames.length ? connectorNames.join(', ') : '-'}
                                                </td>
                                                <td style={tableCellStyle}>
                                                    {set.pairs
                                                        .filter(pair => pair.a === resources.indexOf(resource) || pair.b === resources.indexOf(resource))
                                                        .map(pair => pair.score)
                                                        .join(', ') || '-'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '7px' }}>
                            Shared datasources
                        </div>
                        {set.sharedDatasources.length ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                    <thead>
                                        <tr>
                                            <th style={tableHeaderStyle}>DATASOURCE</th>
                                            <th style={tableHeaderStyle}>CONNECTOR / SOURCE</th>
                                            <th style={tableHeaderStyle}>SOURCE TABLE</th>
                                            <th style={tableHeaderStyle}>QUERY TYPE</th>
                                            <th style={tableHeaderStyle}>SQL</th>
                                            <th style={tableHeaderStyle}>PRESENT IN</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {set.sharedDatasources.map(item => (
                                            <tr key={`${set.id}-${item.id || item.name}`}>
                                                <td style={{ ...tableCellStyle, fontWeight: 600 }}>{item.name || item.id || '-'}</td>
                                                <td style={tableCellStyle}>{item.connectorName || 'File'}</td>
                                                <td style={tableCellStyle}>{item.tableName || '-'}</td>
                                                <td style={tableCellStyle}>{item.queryType || '-'}</td>
                                                <td style={{...tableCellStyle, maxWidth:260}} title={item.query || ''}>{item.hasQuery ? 'SQL available' : '-'}</td>
                                                <td style={tableCellStyle}>{item.pages.join(', ')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>
                                No datasource is common to every page in this merge set.
                            </div>
                        )}
                    </div>
                </div>
            )) : (
                <div style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '30px',
                    textAlign: 'center',
                    color: 'var(--text-secondary)',
                    marginBottom: '16px'
                }}>
                    No page pairs currently meet the MERGE threshold of 85/100.
                </div>
            )}

            {isolated.length ? (
                <div style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: 'var(--card-bg, #fff)'
                }}>
                    <div style={{ padding: '12px 14px', background: 'var(--hover-bg)', fontSize: '14px', fontWeight: 600 }}>
                        Isolated Pages
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                            <thead>
                                <tr>
                                    <th style={tableHeaderStyle}>PAGE</th>
                                    <th style={tableHeaderStyle}>DATASOURCES</th>
                                    <th style={tableHeaderStyle}>CONNECTOR / SOURCE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isolated.map(resource => {
                                    const sourceDetails = getPageDatasourceDetails(resource, datasets);
                                    const connectorNames = [...new Set(sourceDetails.map(item => item.connectorName).filter(Boolean))];
                                    return (
                                        <tr key={`isolated-${resource.key}`}>
                                            <td style={{ ...tableCellStyle, fontWeight: 600 }}>{resource.name}</td>
                                            <td style={tableCellStyle}>{sourceDetails.map(item => item.name || item.id).filter(Boolean).join(', ') || '-'}</td>
                                            <td style={tableCellStyle}>{connectorNames.join(', ') || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            </>

            )}
        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Merge Sets Cluster Diagram
|--------------------------------------------------------------------------
| Purpose-built for Merge Sets (unlike the Overall Status network, which is
| an exploratory all-pairs diagram with an adjustable threshold). Here the
| clustering decision is already made — each merge set (score >= 85) is
| drawn as its own self-contained group of connected pages, laid out side
| by side so it reads as "these pages become one page", and isolated pages
| (no merge partner) are shown separately, clearly not connected to
| anything.
|--------------------------------------------------------------------------
*/

function MergeSetsClusterDiagram({ mergeSets = [], isolated = [] }) {

    if (!mergeSets.length && !isolated.length) {
        return <EmptyState message="No pages available to diagram." />;
    }

    return (
        <div>

            {mergeSets.length > 0 && (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginBottom: isolated.length ? '20px' : 0
                    }}
                >
                    {mergeSets.map(set => (
                        <MergeSetCluster key={set.id} set={set} />
                    ))}
                </div>
            )}

            {!mergeSets.length && (
                <div
                    style={{
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '30px',
                        textAlign: 'center',
                        color: 'var(--text-secondary)',
                        marginBottom: isolated.length ? '20px' : 0
                    }}
                >
                    No page pairs currently meet the MERGE threshold of 85/100.
                </div>
            )}

            {isolated.length > 0 && (
                <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '10px' }}>
                        Isolated pages (no merge candidate)
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {isolated.map(resource => (
                            <div
                                key={`isolated-node-${resource.key}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    border: '1px dashed var(--border-color)',
                                    borderRadius: '20px',
                                    padding: '7px 14px',
                                    fontSize: '12px',
                                    color: 'var(--text-secondary)',
                                    background: 'var(--hover-bg)'
                                }}
                            >
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#94a3b8', display: 'inline-block' }} />
                                {resource.name}
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </div>
    );

}

function MergeSetCluster({ set }) {

    const count = set.resources.length;

    // Every node needs its own position - a zero radius (previously used
    // for 2-node sets) stacked both nodes exactly on top of each other,
    // making their labels overlap/concatenate. A 2-node set is laid out
    // side by side instead of on a circle; 3+ nodes are spread around a
    // circle with a radius that grows with the node count so labels have
    // room to breathe, and the canvas grows with the radius so nodes/labels
    // never clip outside the SVG.
    const radius = count <= 1 ? 0 : count === 2 ? 60 : 55 + count * 8;
    const padding = 60;
    const width = Math.max(260, radius * 2 + padding);
    const height = Math.max(220, radius * 2 + padding);
    const centerX = width / 2;
    const centerY = height / 2;

    const nodes = set.resources.map((resource, index) => {

        if (count === 2) {
            return {
                key: resource.key,
                name: resource.name,
                x: index === 0 ? centerX - radius : centerX + radius,
                y: centerY,
                labelDy: 22
            };
        }

        // Start at the top (-90deg) so labels don't collide with the
        // set header. Nodes on the upper half get their label above the
        // node, lower half below, so labels fan outward from the circle
        // instead of all sitting directly underneath (which is what
        // caused overlap when several nodes landed near the same row).
        const angle = (2 * Math.PI * index) / Math.max(1, count) - Math.PI / 2;
        const y = centerY + radius * Math.sin(angle);

        return {
            key: resource.key,
            name: resource.name,
            x: centerX + radius * Math.cos(angle),
            y,
            labelDy: y < centerY ? -14 : 22
        };
    });

    return (
        <div
            style={{
                border: '1px solid var(--border-color)',
                borderLeft: '3px solid #18a66b',
                borderRadius: '8px',
                background: 'var(--card-bg, #fff)',
                padding: '12px',
                minWidth: '280px'
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '12px' }}>Set #{set.number}</strong>
                <span
                    style={{
                        background: '#e8fbf2',
                        color: '#078653',
                        border: '1px solid #9ce7c7',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        fontSize: '10px',
                        fontWeight: 700
                    }}
                >
                    MERGE
                </span>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    avg {set.avgScore}/100
                </span>
            </div>

            <svg width={width} height={height} style={{ display: 'block', margin: '0 auto' }}>

                {/* EDGES: connect every page in the set to every other page
                    (all pairs within a merge set score >= 85 by construction,
                    since Union-Find only merges pages that cleared that bar) */}
                {nodes.map((from, i) =>
                    nodes.slice(i + 1).map((to, j) => (
                        <line
                            key={`${from.key}-${to.key}`}
                            x1={from.x}
                            y1={from.y}
                            x2={to.x}
                            y2={to.y}
                            stroke="#18a66b"
                            strokeWidth={1.5}
                            opacity={0.6}
                        />
                    ))
                )}

                {nodes.map(node => (
                    <g key={node.key} transform={`translate(${node.x}, ${node.y})`}>
                        <circle r={8} fill="#e8fbf2" stroke="#18a66b" strokeWidth={1.5} />
                        <text
                            x={0}
                            y={node.labelDy}
                            textAnchor="middle"
                            fontSize={10}
                            fontWeight={600}
                            fill="var(--text-primary)"
                        >
                            {node.name.length > 18 ? `${node.name.slice(0, 18)}...` : node.name}
                        </text>
                        <title>{node.name}</title>
                    </g>
                ))}

            </svg>

        </div>
    );

}

function buildOverallComparisonResources(pages = [], cards = [], datasets = [], beastmodes = []) {
    return pages.map((page, index) => {
        const pageId = page.pageId || page.id || page.urn || `page-${index}`;
        const pageName = page.pageName || page.name || page.title || `Page ${index + 1}`;
        const pageCards = getPageCardsForOverallStatus(page, cards);
        const datasourceIds = [...new Set(pageCards.map(card => getCardDatasetId(card)).filter(Boolean).map(String))];
        const datasourceNames = [...new Set(pageCards.map(card => getCardDatasetName(card, datasets)).filter(Boolean))];
        const columnInfo = getPageColumnInfo(pageCards, datasets);
        const calculatedFields = getPageCalculatedFields(page, pageCards, beastmodes);
        const worksheets = pageCards.map(card => normalizeWorksheet(card));
        return {
            key: String(pageId),
            id: pageId,
            name: pageName,
            type: 'Page',
            page,
            cards: pageCards,
            worksheets,
            datasourceIds,
            datasourceNames,
            datasources: datasourceIds.map(id => {
                const dataset = datasets.find(d => String(d?.id ?? d?.datasetId ?? '') === String(id));
                const ds = getDatasetDatasourceDetails(dataset);
                return {
                    id,
                    name: dataset?.name || dataset?.datasetName || dataset?.displayName || id,
                    connectorName: ds.driver !== '-' ? ds.driver : 'File',
                    schema: ds.schema,
                    tableName: ds.tableName,
                    queryType: ds.queryType,
                    updateMode: ds.updateMode,
                    query: ds.query,
                    hasQuery: ds.hasQuery
                };
            }),
            columns: columnInfo.columns,
            columnTypes: columnInfo.types,
            columnDetails: columnInfo.columnDetails,
            calculatedFields,
            calculatedFieldNames: calculatedFields.map(field => field.name)
        };
    });
}

function getDatasetConnectorName(dataset) {
    if (!dataset || typeof dataset !== 'object') return 'File';

    const nested = [
        dataset.connector,
        dataset.dataSource,
        dataset.datasource,
        dataset.connection,
        dataset.connectionInfo,
        dataset.source
    ];

    for (const value of nested) {
        if (typeof value === 'string' && value.trim()) return value.trim();
        if (value && typeof value === 'object') {
            const candidate = value.name || value.type || value.connectorName || value.connectorType || value.sourceType || value.dataSourceType;
            if (candidate) return String(candidate);
        }
    }

    const direct = [
        dataset.connectorName,
        dataset.connectorType,
        dataset.dataSourceType,
        dataset.datasourceType,
        dataset.dataSourceName,
        dataset.sourceType,
        dataset.connectionType,
        dataset.transportType
    ];

    const found = direct.find(value => value !== undefined && value !== null && String(value).trim());
    return found ? String(found).trim() : 'File';
}


function getDatasetDatasourceDetails(dataset) {
    
    const ds = dataset?.c_datasource || dataset?.datasource || dataset?.dataSource || dataset?.source || {};
    const config = Array.isArray(ds?.configuration) ? ds.configuration : [];

    const getConfigValue = (name) => {
        const item = config.find(c => String(c?.name || '').toLowerCase() === String(name).toLowerCase());
        return item?.value ?? '';
    };

    const query = getConfigValue('query');
    const queryType = getConfigValue('queryType');
    const updateMode = getConfigValue('updatemode.mode');
    const tableName = ds?.datasource?.tableName || getConfigValue('tableName') || '';
    const schema = ds?.datasource?.schema || '';
    const driver = ds?.datasource?.driver || getDatasetConnectorName(dataset) || '';
    const source = ds?.datasource?.source || '';
    const available = Boolean((dataset?.datasource || dataset?.dataSource));

    return {
        available,
        tableName: tableName || '-',
        schema: schema || '-',
        driver: driver || '-',
        source: source || '-',
        queryType: queryType || '-',
        updateMode: updateMode || '-',
        query: query || '-',
        hasQuery: Boolean(String(query || '').trim()),
        configuration: config
    };
}

function getPageDatasourceDetails(resource, datasets = []) {
    const ids = toArray(resource?.datasourceIds).map(String).filter(Boolean);
    const byId = new Map((datasets || []).map(dataset => [String(dataset?.id ?? dataset?.datasetId ?? ''), dataset]));
    const details = [];
    const seen = new Set();

    ids.forEach(id => {
        const dataset = byId.get(id);
        const ds = getDatasetDatasourceDetails(dataset);
        const item = {
            id,
            name: dataset?.name || dataset?.datasetName || id,
            connectorName: ds.driver !== '-' ? ds.driver : 'File',
            schema: ds.schema,
            tableName: ds.tableName,
            queryType: ds.queryType,
            updateMode: ds.updateMode,
            query: ds.query,
            hasQuery: ds.hasQuery
        };
        const key = `${id}::${item.name}`;
        if (!seen.has(key)) {
            seen.add(key);
            details.push(item);
        }
    });

    if (!details.length && Array.isArray(resource?.datasourceNames)) {
        resource.datasourceNames.forEach(name => {
            const dataset = (datasets || []).find(item => String(item?.name || item?.datasetName || '').toLowerCase() === String(name).toLowerCase());
            details.push({
                id: dataset?.id || dataset?.datasetId || '',
                name,
                connectorName: getDatasetConnectorName(dataset)
            });
        });
    }

    return details;
}

function getSharedDatasourceDetails(resources = [], datasets = []) {
    if (!resources.length) return [];

    const maps = resources.map(resource => {
        const details = getPageDatasourceDetails(resource, datasets);
        const map = new Map();
        details.forEach(item => map.set(String(item.id || item.name).toLowerCase(), item));
        return map;
    });

    const first = maps[0];
    const shared = [];
    first.forEach((item, key) => {
        if (maps.every(map => map.has(key))) {
            shared.push({
                ...item,
                pages: resources.map(resource => resource.name)
            });
        }
    });
    return shared;
}

/*
|--------------------------------------------------------------------------
| Migration Waves
|--------------------------------------------------------------------------
| Groups discovered Domo resources into practical migration waves without
| changing the underlying assessment/readiness calculations.
*/
/*
|--------------------------------------------------------------------------
| Export Rows: Migration Waves
|--------------------------------------------------------------------------
*/

function buildMigrationWavesExportRows(items = []) {
    return items.map(item => ({
        'Page': item.name,
        'Wave': item.waveLabel,
        'Status': item.status,
        'Complexity': item.complexity,
        'Readiness': item.readiness,
        'Cards': item.cards,
        'Datasets': item.datasets,
        'Columns': item.columns,
        'Calculated Fields': item.calcFields,
        'Issues': item.issues,
        'Estimated Effort (hrs)': Math.round(item.effortHours * 100) / 100
    }));
}

function MigrationWavesTab({ pages = [], cards = [], datasets = [], beastmodes = [], pageComplexityConfig }) {

    const [viewMode, setViewMode] = useState('waves');

    /*
     * Page-wise migration waves: each row/tile represents a Domo page
     * (dashboard), grouped into waves based on how many cards/datasets it
     * pulls in and how many issues those resources carry. Built using the
     * same page -> cards -> datasources/columns/calculated-fields
     * resolution as the Overall Status tab, so the numbers here are
     * consistent with what that tab shows for the same page.
     */
    const pageResources = useMemo(
        () => buildOverallComparisonResources(pages, cards, datasets, beastmodes),
        [pages, cards, datasets, beastmodes]
    );

    const normalized = useMemo(() => {

        return pageResources.map((resource, index) => {

            const datasourceIds = Array.isArray(resource.datasourceIds) ? resource.datasourceIds : [];
            const datasourceDetails = getPageDatasourceDetails(resource, datasets);
            const primaryDatasource = datasourceDetails[0] || {};

            const cardCount = Array.isArray(resource.cards) ? resource.cards.length : 0;
            const datasetCount = datasourceIds.length;
            const columnCount = Array.isArray(resource.columns) ? resource.columns.length : 0;
            const calcFieldCount = Array.isArray(resource.calculatedFields) ? resource.calculatedFields.length : 0;

            const issues = Number(
                resource.issueCount ??
                (Array.isArray(resource.issues) ? resource.issues.length : 0)
            );

            /*
             * Use the same page-level complexity used by the Pages tab and
             * the top Complexity card (cards, datasets, variables, sets,
             * groups, hierarchy, app-linked pages), so complexity is
             * consistent everywhere instead of a separate formula here.
             */
            const { complexity } = calculatePageComplexity(
                resource.page,
                pages,
                datasets,
                cards,
                beastmodes,
                pageComplexityConfig
            );

            const readiness = calculateReadiness({
                issueCount: issues,
                complexity
            });

            const readinessKey = String(readiness).trim().toLowerCase();
            const complexityKey = String(complexity).trim().toLowerCase();

            /*
             * ---------------------------------------------------------
             * EFFORT
             * ---------------------------------------------------------
             *
             * Beast Modes intentionally excluded.
             */
            const effortHours =
                0.75 +
                (datasetCount * 0.5) +
                (columnCount * 0.05) +
                (cardCount * 0.25) +
                (calcFieldCount * 0.15) +
                (issues * 0.5);

            /*
             * ---------------------------------------------------------
             * WAVE
             * ---------------------------------------------------------
             */
            let wave = 2;
            let waveLabel = 'Wave 2 - Medium (standard migration)';
            let status = 'Review';

            if (
                readinessKey === 'blocked' ||
                readinessKey === 'blocked / manual review' ||
                complexityKey === 'high'
            ) {
                wave = 3;
                waveLabel = 'Wave 3 - Complex (manual review needed)';
                status = 'Complex';
            } else if (
                readinessKey === 'ready' &&
                complexityKey === 'low' &&
                issues === 0
            ) {
                wave = 1;
                waveLabel = 'Wave 1 - Easy (quick wins)';
                status = 'Ready';
            }

            return {
                key: `${resource.id || index}-${index}`,

                name: resource.name || `Page ${index + 1}`,

                driver: primaryDatasource.connectorName || 'File',
                schema: primaryDatasource.schema || '-',
                sourceTable: primaryDatasource.tableName || '-',

                cards: cardCount,
                pages: 1,
                datasets: datasetCount,
                columns: columnCount,
                calcFields: calcFieldCount,
                rows: null,
                issues,

                complexity,
                readiness,

                effortHours,
                wave,
                waveLabel,
                status
            };
        });
    }, [pageResources, datasets, pages, cards, beastmodes, pageComplexityConfig]);
    
    const waves = [1, 2, 3]
        .map(number => {
            const waveItems =
                normalized.filter(
                    item => item.wave === number
                );

            const labels = {
                1: 'Wave 1 - Easy (quick wins)',
                2: 'Wave 2 - Medium (standard migration)',
                3: 'Wave 3 - Complex (manual review needed)'
            };

            return {
                number,
                label: labels[number],
                items: waveItems,
                effort: waveItems.reduce(
                    (sum, item) =>
                        sum + item.effortHours,
                    0
                )
            };
        })
        .filter(
            wave => wave.items.length > 0
        );

    if (!normalized.length) {
        return (
            <div style={{ padding: '20px' }}>
                <div
                    style={{
                        border:
                            '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '40px',
                        textAlign: 'center',
                        color:
                            'var(--text-secondary)'
                    }}
                >
                    No migration-wave data is available
                    from the current Domo assessment.
                </div>
            </div>
        );
    }

    const waveStyles = {
        1: {
            border: '#18a66b',
            badge: '#e8fbf2',
            text: '#078653'
        },
        2: {
            border: '#e5a000',
            badge: '#fff5d9',
            text: '#b27700'
        },
        3: {
            border: '#e3343b',
            badge: '#fff0f1',
            text: '#d91f28'
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '18px',
                    gap: '12px'
                }}
            >
                <div>
                    <div
                        style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            marginBottom: '5px'
                        }}
                    >
                        Migration Waves
                    </div>

                    <div
                        style={{
                            fontSize: '12px',
                            color:
                                'var(--text-secondary)'
                        }}
                    >
                        Recommended migration sequence by page,
                        based on each page's cards, datasets,
                        columns, calculated fields and issues.
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <ExportTabButton
                        rows={buildMigrationWavesExportRows(normalized)}
                        sheetName="Migration Waves"
                    />
                    <ViewModeToggle
                        viewMode={viewMode}
                        onChange={setViewMode}
                        options={[
                            { key: 'waves', label: 'Table', icon: faTable },
                            { key: 'network', label: 'Diagram', icon: faSitemap }
                        ]}
                    />
                </div>
            </div>

            {viewMode === 'network' ? (

                <MigrationWavesNetwork items={normalized} waveStyles={waveStyles} />

            ) : (

            <>

            {waves.map(wave => {
                const style =
                    waveStyles[wave.number];

                return (
                    <div
                        key={wave.number}
                        style={{
                            marginBottom: '14px',
                            border:
                                '1px solid var(--border-color)',
                            borderLeft:
                                `3px solid ${style.border}`,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background:
                                'var(--card-bg, #fff)'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent:
                                    'space-between',
                                padding:
                                    '12px 14px',
                                background:
                                    'var(--hover-bg)'
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: style.text
                                }}
                            >
                                {wave.label}
                            </div>

                            <div
                                style={{
                                    fontSize: '11px',
                                    color:
                                        'var(--text-secondary)'
                                }}
                            >
                                {wave.items.length}
                                {' '}page(s)
                                {' -  '}
                                {Math.round(
                                    wave.effort * 10
                                ) / 10}
                                h estimated
                            </div>
                        </div>

                        <div
                            style={{
                                padding: '12px'
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    marginBottom: '12px'
                                }}
                            >
                                {wave.items.map(item => (
                                    <div
                                        key={item.key}
                                        style={{
                                            display:
                                                'inline-flex',
                                            alignItems:
                                                'center',
                                            gap: '7px',
                                            padding:
                                                '7px 10px',
                                            border:
                                                '1px solid var(--border-color)',
                                            borderRadius:
                                                '7px',
                                            background:
                                                'var(--card-bg, #fff)',
                                            fontSize: '12px'
                                        }}
                                    >
                                        <span>
                                            {item.name}
                                        </span>

                                        <span
                                            style={{
                                                background:
                                                    style.badge,
                                                color:
                                                    style.text,
                                                borderRadius:
                                                    '12px',
                                                padding:
                                                    '3px 8px',
                                                fontSize:
                                                    '10px',
                                                fontWeight:
                                                    600
                                            }}
                                        >
                                            {wave.number === 3
                                                ? '✕ Complex'
                                                : wave.number === 2
                                                    ? 'Review'
                                                    : '✓ Ready'}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div
                                style={{
                                    overflowX:
                                        'auto'
                                }}
                            >
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse:
                                            'collapse',
                                        fontSize: '11px'
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            {[
                                                'STATUS',
                                                'PAGE',
                                                'DRIVER',
                                                'SCHEMA',
                                                'SOURCE TABLE',
                                                'CARDS',
                                                'DATASETS',
                                                'COLUMNS',
                                                'CALC FIELDS',
                                                'ISSUES',
                                                'COMPLEXITY',
                                                'EFFORT'
                                            ].map(header => (
                                                <th
                                                    key={header}
                                                    style={{
                                                        ...tableHeaderStyle,
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {wave.items.map(
                                            item => (
                                                <tr key={`detail-${item.key}`}>
                                                    <td style={tableCellStyle}>
                                                        {item.status}
                                                    </td>

                                                    <td
                                                        style={{
                                                            ...tableCellStyle,
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {item.name}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {item.driver}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {item.schema}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {item.sourceTable}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {item.cards}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {item.datasets}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {item.columns}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {item.calcFields}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {item.issues}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {item.complexity}
                                                    </td>

                                                    <td style={tableCellStyle}>
                                                        {Math.round(item.effortHours * 10) / 10}h
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
            })}

            </>

            )}

            <div
                style={{
                    marginTop: '18px',
                    padding: '12px 14px',
                    border:
                        '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background:
                        'var(--hover-bg)',
                    fontSize: '11px',
                    color:
                        'var(--text-secondary)'
                }}
            >
                <strong
                    style={{
                        color:
                            'var(--text-primary)'
                    }}
                >
                    Wave logic:
                </strong>{' '}
                Ready + low complexity, Wave 1;
                review/medium complexity, Wave 2;
                blocked or high complexity, Wave 3.
                Estimated hours are planning estimates
                derived from discovered Domo objects and
                are not the backend assessment score.
            </div>
        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Migration Waves Network
|--------------------------------------------------------------------------
| Shows pages as nodes grouped into their wave (color-coded ring), so it's
| visible at a glance how many pages fall into each wave and roughly how
| "far" a page's complexity pushed it from Wave 1. Unlike the Overall
| Status / Merge Sets diagrams (which are about page-to-page similarity),
| this diagram is about each page's own migration difficulty, so nodes are
| grouped by wave rather than connected to each other.
|--------------------------------------------------------------------------
*/

function MigrationWavesNetwork({ items = [], waveStyles = {} }) {

    const [hoveredKey, setHoveredKey] = useState(null);

    const waveGroups = useMemo(() => {
        return [1, 2, 3]
            .map(number => ({
                number,
                items: items.filter(item => item.wave === number)
            }))
            .filter(group => group.items.length > 0);
    }, [items]);

    if (!items.length) {
        return <EmptyState message="No pages available to diagram." />;
    }

    const waveLabels = {
        1: 'Wave 1 - Easy',
        2: 'Wave 2 - Medium',
        3: 'Wave 3 - Complex'
    };

    return (
        <div>

            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}
            >

                {waveGroups.map(group => {

                    const style = waveStyles[group.number] || { border: '#94a3b8', badge: '#f3f4f6', text: '#6b7280' };
                    const count = group.items.length;

                    // Lay each wave's pages out on their own ring so wave
                    // membership is immediately visible by grouping, not
                    // just by color.
                    const radius = count <= 1 ? 0 : 55 + count * 7;
                    const padding = 60;
                    const size = Math.max(220, radius * 2 + padding);
                    const center = size / 2;

                    const nodes = group.items.map((item, index) => {

                        if (count === 1) {
                            return { ...item, x: center, y: center, labelDy: 22 };
                        }

                        if (count === 2) {
                            const x = index === 0 ? center - radius : center + radius;
                            return { ...item, x, y: center, labelDy: 22 };
                        }

                        const angle = (2 * Math.PI * index) / count - Math.PI / 2;
                        const y = center + radius * Math.sin(angle);

                        return {
                            ...item,
                            x: center + radius * Math.cos(angle),
                            y,
                            labelDy: y < center ? -14 : 22
                        };

                    });

                    return (
                        <div
                            key={group.number}
                            style={{
                                border: '1px solid var(--border-color)',
                                borderLeft: `3px solid ${style.border}`,
                                borderRadius: '8px',
                                background: 'var(--card-bg, #fff)',
                                padding: '12px',
                                minWidth: '260px'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                <strong style={{ fontSize: '12px' }}>{waveLabels[group.number]}</strong>
                                <span
                                    style={{
                                        background: style.badge,
                                        color: style.text,
                                        border: `1px solid ${style.border}55`,
                                        borderRadius: '12px',
                                        padding: '2px 8px',
                                        fontSize: '10px',
                                        fontWeight: 700
                                    }}
                                >
                                    {count} page{count === 1 ? '' : 's'}
                                </span>
                            </div>

                            <svg width={size} height={size} style={{ display: 'block', margin: '0 auto' }}>

                                {/* Guide ring so grouping reads clearly even with 1-2 nodes */}
                                {count > 1 && (
                                    <circle
                                        cx={center}
                                        cy={center}
                                        r={radius}
                                        fill="none"
                                        stroke={style.border}
                                        strokeWidth={1}
                                        strokeDasharray="3,4"
                                        opacity={0.35}
                                    />
                                )}

                                {nodes.map(node => {

                                    const dimmed = hoveredKey && hoveredKey !== node.key;

                                    return (
                                        <g
                                            key={node.key}
                                            transform={`translate(${node.x}, ${node.y})`}
                                            onMouseEnter={() => setHoveredKey(node.key)}
                                            onMouseLeave={() => setHoveredKey(null)}
                                            style={{ cursor: 'pointer' }}
                                            opacity={dimmed ? 0.4 : 1}
                                        >
                                            <circle
                                                r={9}
                                                fill={style.badge}
                                                stroke={style.border}
                                                strokeWidth={1.5}
                                            />
                                            <text
                                                x={0}
                                                y={node.labelDy}
                                                textAnchor="middle"
                                                fontSize={10}
                                                fontWeight={600}
                                                fill="var(--text-primary)"
                                            >
                                                {node.name.length > 18 ? `${node.name.slice(0, 18)}...` : node.name}
                                            </text>
                                            <title>
                                                {node.name} - {node.complexity} complexity, {node.issues} issue(s), ~{Math.round(node.effortHours * 10) / 10}h estimated
                                            </title>
                                        </g>
                                    );

                                })}

                            </svg>
                        </div>
                    );

                })}

            </div>

        </div>
    );

}

function AssessmentOverview({
    summary,
    dashboardAnalysis,
    datasets,
    pages
}) {

    const readyDashboards =
        dashboardAnalysis.filter(
            item =>
                String(
                    item.migrationReadiness
                ).toLowerCase() ===
                'ready'
        ).length;


    const reviewDashboards =
        dashboardAnalysis.length -
        readyDashboards;


    return (

        <div
            style={{
                padding:
                    '20px'
            }}
        >

            {/* =========================================================
                OVERVIEW MESSAGE
            ========================================================== */}


            {/* =========================================================
                DASHBOARD SUMMARY
            ========================================================== */}

            {/* <div
                style={{
                    display:
                        'grid',
                    gridTemplateColumns:
                        'repeat(auto-fit, minmax(200px, 1fr))',
                    gap:
                        '12px',
                    marginBottom:
                        '24px'
                }}
            >

                <MiniMetric
                    title="Dataset Analyzed"
                    value={
                        dashboardAnalysis.length
                    }
                    icon={
                        faTable
                    }
                />


                <MiniMetric
                    title="Ready Dataset"
                    value={
                        readyDashboards
                    }
                    icon={
                        faCircleCheck
                    }
                    status="ready"
                />


                <MiniMetric
                    title="Review Required"
                    value={
                        reviewDashboards
                    }
                    icon={
                        faTriangleExclamation
                    }
                    status="review"
                />


                <MiniMetric
                    title="Datasets"
                    value={
                        datasets.length
                    }
                    icon={
                        faDatabase
                    }
                />

            </div>
 */}

            {/* =========================================================
                DASHBOARD QUICK VIEW
            ========================================================== */}

            <div
                style={{
                    marginBottom:
                        '24px'
                }}
            >

                <div
                    style={{
                        fontSize:
                            '14px',
                        fontWeight:
                            600,
                        marginBottom:
                            '12px'
                    }}
                >
                    
                </div>


                {!dashboardAnalysis.length ? (

                    <div
                        style={{
                            padding:
                                '30px',
                            textAlign:
                                'center',
                            color:
                                'var(--text-secondary)',
                            fontSize:
                                '13px'
                        }}
                    >
                        No dashboard dependency
                        information available.
                    </div>

                ) : (

                    <div
                        style={{
                            overflowX:
                                'auto'
                        }}
                    >

                        <table
                            style={{
                                width:
                                    '100%',
                                borderCollapse:
                                    'collapse',
                                fontSize:
                                    '12px'
                            }}
                        >

                            <thead>

                                <tr>

                                    <th style={tableHeaderStyle}>
                                        Dataset
                                    </th>

                                    <th style={tableHeaderStyle}>
                                        Dashboards
                                    </th>

                                    <th style={tableHeaderStyle}>
                                        Cards
                                    </th>

                                    <th style={tableHeaderStyle}>
                                        Calculative fields
                                    </th>

                                    <th style={tableHeaderStyle}>
                                        Complexity
                                    </th>

                                    <th style={tableHeaderStyle}>
                                        Readiness
                                    </th>

                                </tr>

                            </thead>


                            <tbody>
    {dashboardAnalysis
        .slice(0, 10)
        .map((item, index) => {

            const datasetId =
                item.datasetId ||
                item.dataSetId;

            // Find dashboards/pages containing
            // cards that belong to this dataset
            const datasetPages = (pages || []).filter(
                (page) =>
                    (page.cards || []).some(
                        (card) =>
                            card.dataSetId === datasetId
                    )
            );

            // Get cards belonging to this dataset
            const datasetCards =
                datasetPages.flatMap(
                    (page) =>
                        (page.cards || []).filter(
                            (card) =>
                                card.dataSetId === datasetId
                        )
                );

            // Count calculated fields / Beast Modes
            const beastmodeCount =
                datasetCards.reduce(
                    (count, card) =>
                        count +
                        (card.calculatedFields?.length || 0),
                    0
                );

            return (
                <tr
                    key={
                        datasetId ||
                        index
                    }
                    style={{
                        borderBottom:
                            '1px solid var(--border-color)'
                    }}
                >

                    {/* Dataset */}
                    <td style={tableCellStyle}>
                        {item.datasetName || '-'}
                    </td>


                    {/* Dashboards */}
                    <td style={tableCellStyle}>
                        {datasetPages.length}
                    </td>


                    {/* Cards */}
                    <td style={tableCellStyle}>
                        {datasetCards.length}
                    </td>


                    {/* Beast Modes */}
                    <td style={tableCellStyle}>
                        {beastmodeCount}
                    </td>


                    {/* Complexity */}
                    <td style={tableCellStyle}>
                        <StatusBadge
                            value={
                                item.complexity
                            }
                        />
                    </td>


                    {/* Readiness */}
                    <td style={tableCellStyle}>
                        <StatusBadge
                            value={
                                item.migrationReadiness
                            }
                        />
                    </td>

                </tr>
            );
        })}
</tbody>

                        </table>

                    </div>

                )}

            </div>


            {/* =========================================================
                ASSESSMENT INSIGHTS
            ========================================================== */}

            {/* <div>

                <div
                    style={{
                        fontSize:
                            '14px',
                        fontWeight:
                            600,
                        marginBottom:
                            '12px'
                    }}
                >
                    Assessment Insights
                </div>


                <div
                    style={{
                        display:
                            'grid',
                        gridTemplateColumns:
                            'repeat(auto-fit, minmax(250px, 1fr))',
                        gap:
                            '12px'
                    }}
                >

                    <InsightCard
                        title="Migration Readiness"
                        value={
                            summary.migrationReadiness
                        }
                        description={
                            'Overall readiness based on the discovered Domo resources and assessment rules.'
                        }
                        status={
                            summary.migrationReadiness
                        }
                    />


                    <InsightCard
                        title="Overall Complexity"
                        value={
                            summary.overallComplexity
                        }
                        description={
                            'Complexity considers the number of dashboards, cards, datasets, DataFlows and Beast Modes.'
                        }
                        status={
                            summary.overallComplexity
                        }
                    />


                    <InsightCard
                        title="Assessment Score"
                        value={
                            summary.assessmentScore
                        }
                        description={
                            'Overall migration assessment score calculated by the backend.'
                        }
                    />


                    <InsightCard
                        title="Potential Issues"
                        value={
                            summary.issueCount
                        }
                        description={
                            summary.issueCount > 0
                                ? 'Some resources require review before migration.'
                                : 'No major migration issues were identified.'
                        }
                        status={
                            summary.issueCount > 0
                                ? 'Review Required'
                                : 'Ready'
                        }
                    />

                </div>

            </div> */}

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Assessment Explanation
|--------------------------------------------------------------------------
*/

function AssessmentExplanation({
    readinessCounts,
    complexityCounts,
    summary,
    overallReadiness,
    overallComplexity
}) {
   
    return (

        <div
            style={{
                border:
                    '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '15px',
                background: 'var(--hover-bg)'
            }}
        >

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '10px',
                    fontSize: '13px',
                    fontWeight: 600
                }}
            >

                <FontAwesomeIcon
                    icon={faCircleInfo}
                    style={{
                        color: '#1e447e'
                    }}
                />

                How the assessment is determined

            </div>


            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns:
                        'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '12px'
                }}
            >

                <ExplanationItem
                    title="Migration Readiness"
                    text={
                        readinessCounts.blocked > 0
                            ? 'Blocked resources were identified and must be resolved before migration.'
                            : readinessCounts.review > 0
                                ? 'Some resources require review before migration.'
                                : `Overall readiness is ${overallReadiness}.`
                    }
                />

                <ExplanationItem
                    title="Complexity"
                    text={
                        `Overall complexity is ${overallComplexity}. Distribution: ${complexityCounts.low} Low, ${complexityCounts.medium} Medium, ${complexityCounts.high} High.`
                    }
                />

                <ExplanationItem
                    title="Assessment Score"
                    text={
                        `The backend assessment score is ${summary.assessmentScore || 0}.`
                    }
                />

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Explanation Item
|--------------------------------------------------------------------------
*/

function ExplanationItem({
    title,
    text
}) {

    return (

        <div>

            <div
                style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    marginBottom: '4px'
                }}
            >
                {title}
            </div>

            <div
                style={{
                    fontSize: '11px',
                    lineHeight: 1.5,
                    color:
                        'var(--text-secondary)'
                }}
            >
                {text}
            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Mini Metric
|--------------------------------------------------------------------------
*/

function MiniMetric({
    title,
    value,
    icon,
    status
}) {

    return (

        <div
            style={{
                border:
                    '1px solid var(--border-color)',
                borderRadius: '7px',
                padding: '14px',
                display: 'flex',
                alignItems: 'center'
            }}
        >

            <div
                style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '7px',
                    background:
                        getStatusBackground(status),
                    color:
                        getStatusColor(status),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '10px'
                }}
            >

                <FontAwesomeIcon
                    icon={icon}
                />

            </div>


            <div>

                <div style={smallLabelStyle}>
                    {title}
                </div>

                <div
                    style={{
                        fontSize: '20px',
                        fontWeight: 600
                    }}
                >
                    {value}
                </div>

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Insight Card
|--------------------------------------------------------------------------
*/

function InsightCard({
    title,
    value,
    description
}) {

    return (

        <div
            style={{
                border:
                    '1px solid var(--border-color)',
                borderRadius: '7px',
                padding: '15px'
            }}
        >

            <div style={smallLabelStyle}>
                {title}
            </div>

            <div
                style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#1e447e',
                    marginBottom: '7px'
                }}
            >
                {value}
            </div>

            <div
                style={{
                    fontSize: '12px',
                    lineHeight: 1.5,
                    color:
                        'var(--text-secondary)'
                }}
            >
                {description}
            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Additional Domo Metadata Tabs
|--------------------------------------------------------------------------
*/
function MetricsTab({
    summary = {},
    columns = [],
    beastmodes = [],
    users = [],
    groups = [],
    dependencies = [],
    lineage = [],
    readinessCounts,
    overallReadiness,
    resourceCount,
    complexityCounts,
    overallComplexity
}) {

    const metrics = [
        { title: 'Columns', value: summary.columns || columns.length, icon: faColumns },
        { title: 'Cards', value: summary.cards ?? 0, icon: faChartBar },
        { title: 'DataFlows', value: summary.dataflows ?? 0, icon: faProjectDiagram },
        { title: 'Filters', value: summary.filters || 0, icon: faFilter },
        { title: 'Variables', value: summary.variables || 0, icon: faSquareRootVariable },
        { title: 'Hierarchy', value: summary.hierarchy || 0, icon: faIndent },
        { title: 'Sets', value: summary.sets || 0, icon: faObjectGroup },
        { title: 'Groups', value: summary.groups_ || 0, icon: faTags },
        { title: 'Beast Modes', value: summary.beastmodes ?? beastmodes.length, icon: faCode },
        { title: 'Users', value: users.length || Number(summary.users || 0), icon: faUsers },
        { title: 'User Groups', value: groups.length || Number(summary.groups || 0), icon: faUserGroup },
        { title: 'Dependencies', value: dependencies.length || lineage.length || Number(summary.dependencies || summary.lineageEdges || 0), icon: faSitemap }
    ];

    return (
        <div style={{ padding: '18px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '4px' }}>
                <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Metrics</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                        Remaining discovered resource counts not shown in the top summary cards.
                    </div>
                </div>
                <ExportTabButton
                    rows={metrics.map(m => ({ Metric: m.title, Value: m.value }))}
                    sheetName="Metrics"
                />
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                    gap: '10px'
                }}
            >

                {metrics.map(metric => (
                    <ResourceMetric
                        key={metric.title}
                        title={metric.title}
                        value={metric.value}
                        icon={metric.icon}
                    />
                ))}

            </div>

        </div>
    );

}

// function MetadataTab({ title, description, data, columns, emptyMessage }) {
//     return (
//         <div style={{ padding: '18px' }}>
//             <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>{title}</div>
//             <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '14px' }}>{description}</div>
//             {!data?.length ? <EmptyState message={emptyMessage || `No ${title.toLowerCase()} returned by Domo.`} /> : (
//                 <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
//                     <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
//                         <thead><tr>{columns.map(c => <th key={c.key} style={tableHeaderStyle}>{c.label}</th>)}</tr></thead>
//                         <tbody>{data.map((item, i) => <tr key={i}>{columns.map(c => <td key={c.key} style={tableCellStyle}>{formatMetadataValue(item?.[c.key])}</td>)}</tr>)}</tbody>
//                     </table>
//                 </div>
//             )}
//         </div>
//     );
// }

function MetadataTab({ title, description, data, columns, emptyMessage, toolbar }) {

    const exportRows = (data || []).map(item => {
        const row = {};
        columns.forEach(c => { row[c.label] = formatMetadataValue(item?.[c.key]); });
        return row;
    });

    return (
        <div style={{ padding: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: toolbar ? '10px' : '4px' }}>
                <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>{title}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: toolbar ? 0 : '14px' }}>{description}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <ExportTabButton rows={exportRows} sheetName={title} />
                    {toolbar}
                </div>
            </div>
            {!data?.length ? <EmptyState message={emptyMessage || `No ${title.toLowerCase()} returned by Domo.`} /> : (
                <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead><tr>{columns.map(c => <th key={c.key} style={tableHeaderStyle}>{c.label}</th>)}</tr></thead>
                        <tbody>{data.map((item, i) => <tr key={i}>{columns.map(c => <td key={c.key} style={tableCellStyle}>{formatMetadataValue(item?.[c.key])}</td>)}</tr>)}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function formatMetadataValue(value) {
    if (value === null || value === undefined || value === '') return '-';
    if (Array.isArray(value)) return value.map(v => typeof v === 'object' ? (v.name || v.id || JSON.stringify(v)) : String(v)).join(', ');
    if (typeof value === 'object') return value.name || value.title || value.id || JSON.stringify(value);
    return String(value);
}

function ColumnsTab({ data = [], datasets = [] }) {
    const datasetMap = new Map(datasets.map(d => [String(d.id || d.datasetId), d.name || d.datasetName || d.title || '-']));
    const rows = data.map(c => ({ ...c, datasetDisplay: datasetMap.get(String(c.datasetId || c.dataSetId)) || c.datasetName || '-' }));
    return <MetadataTab title="Columns" description="Dataset schema columns discovered from Domo." data={rows} columns={[{key:'name',label:'ColumnName'},  {key:'datasetDisplay',label:'Dataset'}, {key:'type',label:'Data Type'}]} emptyMessage="No column metadata returned by Domo." />;
}

/*
|--------------------------------------------------------------------------
| Export Rows: Data Model
|--------------------------------------------------------------------------
*/

function buildDataModelExportRows(rows = []) {
    return rows.map(row => ({
        Dataset: row.dataset,
        'Dataset ID': row.datasetId,
        Connector: row.connector,
        Schema: row.schema,
        'Source Table': row.sourceTable,
        'Query Type': row.queryType,
        'Update Mode': row.updateMode,
        Column: row.column,
        'Data Type': row.dataType,
        Cards: row.cards,
        Pages: row.pages,
        'Beast Modes': row.beastModes
    }));
}

function DataModelTab({ datasets = [], columns = [], cards = [], pages = [], beastmodes = [] }) {

    const [viewMode, setViewMode] = useState('normal');

    /*
     * Data Model is intentionally different from Datasets and Dependencies:
     * - Datasets = dataset inventory / operational metadata
     * - Dependencies = object-to-object lineage
     * - Data Model = schema + usage footprint (column, datatype, cards, pages, calculations)
     */
    const datasetMap = new Map(
        (Array.isArray(datasets) ? datasets : []).map(d => [
            String(d.id ?? d.datasetId),
            d
        ])
    );

    const cardUsage = new Map();
    const pageUsage = new Map();
    const beastUsage = new Map();

    (Array.isArray(cards) ? cards : []).forEach(card => {
        const datasetId = card.dataSetId ?? card.datasetId ?? card.datasourceId ?? card.dataSourceId;
        if (!datasetId) return;
        const key = String(datasetId);
        const list = cardUsage.get(key) || [];
        list.push(card.cardName || card.name || card.title || card.id || card.cardId || 'Card');
        cardUsage.set(key, list);
    });

    (Array.isArray(pages) ? pages : []).forEach(page => {
        (Array.isArray(page.cards) ? page.cards : []).forEach(card => {
            const datasetId = card.dataSetId ?? card.datasetId ?? card.datasourceId ?? card.dataSourceId;
            if (!datasetId) return;
            const key = String(datasetId);
            const list = pageUsage.get(key) || [];
            const pageName = page.pageName || page.name || page.title || page.id || page.pageId || 'Page';
            if (!list.includes(pageName)) list.push(pageName);
            pageUsage.set(key, list);
        });
    });

    (Array.isArray(beastmodes) ? beastmodes : []).forEach(bm => {
        const datasetId = bm.datasetId ?? bm.dataSetId;
        if (!datasetId) return;
        const key = String(datasetId);
        const list = beastUsage.get(key) || [];
        list.push(bm.beastmodeName || bm.name || bm.title || bm.id || bm.beastModeId || 'Beast Mode');
        beastUsage.set(key, list);
    });

    const schemaRows = Array.isArray(columns) ? columns.map((column, index) => {
        const datasetId = String(column.datasetId ?? column.dataSetId ?? column.datasourceId ?? '');
        const dataset = datasetMap.get(datasetId) || {};
        const type = column.dataType || column.type || column.columnType || column.sqlType || '-';
        const datasource = getDatasetDatasourceDetails(dataset);
        return {
            dataset: dataset.name || dataset.datasetName || dataset.displayName || dataset.title || column.datasetName || datasetId || '-',
            datasetId: datasetId || '-',
            connector: datasource.driver || getDatasetConnectorName(dataset) || column.connectorName || column.connector || '-',
            schema: datasource.schema,
            sourceTable: datasource.tableName,
            queryType: datasource.queryType,
            updateMode: datasource.updateMode,
            column: column.name || column.columnName || column.title || `Column ${index + 1}`,
            dataType: type,
            cards: (cardUsage.get(datasetId) || []).join(', ') || '-',
            pages: (pageUsage.get(datasetId) || []).join(', ') || '-',
            beastModes: (beastUsage.get(datasetId) || []).join(', ') || '-'
        };
    }) : [];

    /* If schema endpoints returned no columns, still show one row per dataset so the tab explains the model footprint. */
    const rows = schemaRows.length ? schemaRows : (Array.isArray(datasets) ? datasets.map(d => {
        const id = String(d.id ?? d.datasetId ?? '');
        return {
            dataset: d.name || d.datasetName || d.displayName || d.title || id || '-',
            datasetId: id || '-',
            ...getDatasetDatasourceDetails(d),
            connector: getDatasetDatasourceDetails(d).driver || getDatasetConnectorName(d) || '-',
            column: 'Schema not returned',
            dataType: '-',
            cards: (cardUsage.get(id) || []).join(', ') || '-',
            pages: (pageUsage.get(id) || []).join(', ') || '-',
            beastModes: (beastUsage.get(id) || []).join(', ') || '-'
        };
    }) : []);

    const modeToggle = (
        <ViewModeToggle
            viewMode={viewMode}
            onChange={setViewMode}
        />
    );

    if (viewMode === 'diagram') {

        /*
         * Diagram edges: Dataset -> Column (schema footprint) and
         * Dataset -> Card / Page / Beast Mode (usage footprint), built from
         * the same usage maps as the Normal-mode table so both views stay
         * consistent with each other.
         */
        const diagramRows = [];

        /*
         * Columns are aggregated into a single node per dataset (e.g.
         * "42 columns") instead of one node per column. With large schemas
         * (hundreds/thousands of columns) creating an individual node+edge
         * per column made the diagram render and hover-highlight extremely
         * slow. Column-level detail is still available in Normal mode.
         */
        const columnCountByDataset = new Map();
        (Array.isArray(columns) ? columns : []).forEach(column => {
            const datasetId = String(column.datasetId ?? column.dataSetId ?? column.datasourceId ?? '');
            if (!datasetId) return;
            columnCountByDataset.set(datasetId, (columnCountByDataset.get(datasetId) || 0) + 1);
        });

        columnCountByDataset.forEach((count, datasetId) => {
            const dataset = datasetMap.get(datasetId) || {};
            const datasetName = dataset.name || dataset.datasetName || dataset.displayName || dataset.title || datasetId;
            const label = `${count} column${count === 1 ? '' : 's'}`;

            diagramRows.push({
                sourceType: 'Dataset',
                sourceId: datasetId,
                sourceName: datasetName,
                targetType: 'Column',
                targetId: `${datasetId}:columns`,
                targetName: label,
                datasetName,
                relationship: 'has_columns',
                cardDetail: '',
                pageDetail: ''
            });
        });

        cardUsage.forEach((names, datasetId) => {
            const dataset = datasetMap.get(datasetId) || {};
            const datasetName = dataset.name || dataset.datasetName || dataset.displayName || dataset.title || datasetId;
            names.forEach(name => diagramRows.push({
                sourceType: 'Dataset',
                sourceId: datasetId,
                sourceName: datasetName,
                targetType: 'Card',
                targetId: `${datasetId}:${name}`,
                targetName: name,
                datasetName,
                relationship: 'used_by',
                cardDetail: name,
                pageDetail: ''
            }));
        });

        pageUsage.forEach((names, datasetId) => {
            const dataset = datasetMap.get(datasetId) || {};
            const datasetName = dataset.name || dataset.datasetName || dataset.displayName || dataset.title || datasetId;
            names.forEach(name => diagramRows.push({
                sourceType: 'Dataset',
                sourceId: datasetId,
                sourceName: datasetName,
                targetType: 'Page',
                targetId: `${datasetId}:${name}`,
                targetName: name,
                datasetName,
                relationship: 'used_by',
                cardDetail: '',
                pageDetail: name
            }));
        });

        beastUsage.forEach((names, datasetId) => {
            const dataset = datasetMap.get(datasetId) || {};
            const datasetName = dataset.name || dataset.datasetName || dataset.displayName || dataset.title || datasetId;
            names.forEach(name => diagramRows.push({
                sourceType: 'Dataset',
                sourceId: datasetId,
                sourceName: datasetName,
                targetType: 'Beast Mode',
                targetId: `${datasetId}:${name}`,
                targetName: name,
                datasetName,
                relationship: 'contains',
                cardDetail: '',
                pageDetail: ''
            }));
        });

        return (
            <div style={{ padding: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px', gap: '12px' }}>
                    <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Data Model</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            Diagrammatic view of dataset schema and usage: which columns belong to each dataset, and which cards, pages, and Beast Modes use it.
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                        <ExportTabButton rows={buildDataModelExportRows(rows)} sheetName="Data Model" />
                        {modeToggle}
                    </div>
                </div>
                {!diagramRows.length ? (
                    <EmptyState message="No data model metadata available." />
                ) : (
                    <DependencyDiagram
                        rows={diagramRows}
                        typeOrder={['dataset', 'column', 'card', 'page', 'beastmode']}
                        typeLabel={{
                            dataset: 'Dataset',
                            column: 'Column',
                            card: 'Card',
                            page: 'Page',
                            beastmode: 'Beast Mode'
                        }}
                        typeColor={{
                            dataset: '#1e447e',
                            column: '#0891b2',
                            card: '#0f766e',
                            page: '#b45309',
                            beastmode: '#be185d'
                        }}
                    />
                )}
            </div>
        );

    }

    return (
        <div style={{ padding: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px', gap: '12px' }}>
                <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Data Model</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                        Schema and usage model: each row shows the dataset column and datatype together with the datasource schema/table, connector, query type, update mode, and cards, pages, and Beast Modes using the dataset.
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <ExportTabButton rows={buildDataModelExportRows(rows)} sheetName="Data Model" />
                    {modeToggle}
                </div>
            </div>
            {!rows.length ? <EmptyState message="No data model metadata available." /> : (
                <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead>
                            <tr>
                                {[
                                    { key: 'dataset', label: 'Dataset' },
                                    { key: 'datasetId', label: 'Dataset ID' },
                                    { key: 'connector', label: 'Connector / Source' },
                                    { key: 'schema', label: 'Schema' },
                                    { key: 'sourceTable', label: 'Source Table' },
                                    { key: 'queryType', label: 'Query Type' },
                                    { key: 'updateMode', label: 'Update Mode' },
                                    { key: 'column', label: 'Column' },
                                    { key: 'dataType', label: 'Data Type' },
                                    { key: 'cards', label: 'Cards Using Dataset' },
                                    { key: 'pages', label: 'Pages Using Dataset' },
                                    { key: 'beastModes', label: 'Beast Modes' }
                                ].map(c => <th key={c.key} style={tableHeaderStyle}>{c.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((item, i) => (
                                <tr key={i}>
                                    {[
                                        'dataset', 'datasetId', 'connector', 'schema', 'sourceTable',
                                        'queryType', 'updateMode', 'column', 'dataType', 'cards', 'pages', 'beastModes'
                                    ].map(key => (
                                        <td key={key} style={tableCellStyle}>{formatMetadataValue(item?.[key])}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function UsersGroupsTab({ users = [], groups = [], memberships = [] }) {
    const [search, setSearch] = useState('');

    const rows = [
        ...users.map(u => ({ type:'User', name:u.name || u.displayName || u.username || u.email || u.id, id:u.id || u.userId, groups: memberships.filter(m => String(m.userId || m.user?.id) === String(u.id || u.userId)).map(m => m.groupName || m.group?.name).filter(Boolean).join(', ') })),
        ...groups.map(g => ({ type:'Group', name:g.name || g.displayName || g.id, id:g.id || g.groupId, groups: g.description || '-' }))
    ];

    const filteredRows = useMemo(() => {
        if (!search.trim()) return rows;
        const term = search.trim().toLowerCase();
        return rows.filter(row =>
            String(row.type || '').toLowerCase().includes(term) ||
            String(row.name || '').toLowerCase().includes(term) ||
            String(row.id || '').toLowerCase().includes(term) ||
            String(row.groups || '').toLowerCase().includes(term)
        );
    }, [rows, search]);

    const searchBox = (
        <div style={{ position: 'relative', flexShrink: 0 }}>
            <FontAwesomeIcon
                icon={faSearch}
                style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--text-tertiary)' }}
            />
            <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search users & groups..."
                style={{
                    width: '220px',
                    padding: '7px 10px 7px 28px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                }}
            />
        </div>
    );

    return (
        <MetadataTab
            title="Users & Groups"
            description={`Domo users, groups, and available membership information. Showing ${filteredRows.length} of ${rows.length}.`}
            data={filteredRows}
            columns={[{key:'type',label:'Type'}, {key:'name',label:'Name'}, {key:'id',label:'ID'}, {key:'groups',label:'Membership / Details'}]}
            emptyMessage={rows.length ? 'No users or groups match your search.' : 'No users or groups returned by Domo.'}
            toolbar={searchBox}
        />
    );
}

/*
|--------------------------------------------------------------------------
| Export Rows: Dependencies
|--------------------------------------------------------------------------
*/

function buildDependenciesExportRows(displayRows = []) {
    return displayRows.map(r => ({
        'Source Type': r.sourceType,
        'Source': r.source,
        'Target Type': r.targetType,
        'Target': r.target,
        'Dataset': r.datasetName,
        'Relationship': r.relationship,
        'Card / Detail': r.cardDetail,
        'Page / Detail': r.pageDetail
    }));
}

function DependenciesTab({
    dependencies = [],
    lineage = [],
    datasets = [],
    cards = [],
    pages = [],
    dataflows = [],
    beastmodes = [],
    apps = []
}) {
    const [viewMode, setViewMode] = useState('normal');

    const datasetMap = new Map((Array.isArray(datasets) ? datasets : []).map(d => [String(d.id ?? d.datasetId), d]));
    const cardMap = new Map((Array.isArray(cards) ? cards : []).map(c => [String(c.id ?? c.cardId), c]));
    const pageMap = new Map((Array.isArray(pages) ? pages : []).map(p => [String(p.id ?? p.pageId), p]));
    const flowMap = new Map((Array.isArray(dataflows) ? dataflows : []).map(f => [String(f.id ?? f.dataflowId), f]));
    const beastMap = new Map((Array.isArray(beastmodes) ? beastmodes : []).map(b => [String(b.id ?? b.beastModeId), b]));
    const appMap = new Map((Array.isArray(apps) ? apps : []).map(a => [String(a.dataAppId ?? a.id ?? a.appId), a]));

    const nameFor = (type, id, fallback = '-') => {
        if (id == null || id === '') return fallback;
        const key = String(id);
        const t = String(type || '').toLowerCase();
        const obj = t === 'dataset' ? datasetMap.get(key)
            : t === 'card' ? cardMap.get(key)
            : t === 'page' ? pageMap.get(key)
            : t === 'dataflow' ? flowMap.get(key)
            : t === 'beastmode' || t === 'beast mode' ? beastMap.get(key)
            : t === 'app' ? appMap.get(key)
            : null;
        if (!obj) return String(id);
        return obj.name || obj.title || obj.cardName || obj.pageName || obj.dataflowName || obj.beastModeName || String(id);
    };

    const rows = [];
    const addRow = (sourceType, sourceId, targetType, targetId, datasetId, relationship, extra = {}, targetArray = rows) => {
        const normalizedDatasetId = datasetId ?? (String(sourceType).toLowerCase() === 'dataset' ? sourceId : null) ?? (String(targetType).toLowerCase() === 'dataset' ? targetId : null);
        targetArray.push({
            sourceType: sourceType || '-',
            sourceId: sourceId ?? '-',
            sourceName: nameFor(sourceType, sourceId),
            targetType: targetType || '-',
            targetId: targetId ?? '-',
            targetName: nameFor(targetType, targetId),
            datasetName: normalizedDatasetId != null ? nameFor('dataset', normalizedDatasetId) : (extra.datasetName || '-'),
            relationship: relationship || '-',
            cardDetail: extra.cardDetail || '',
            pageDetail: extra.pageDetail || ''
        });
    };

    // Explicit lineage edges. Enrich IDs with actual Domo names from the
    // discovered datasets/cards/pages instead of showing only numeric IDs.
    (Array.isArray(lineage) ? lineage : []).forEach(edge => {
        const sourceType = edge.fromType || edge.sourceType;
        const sourceId = edge.fromId ?? edge.sourceId;
        const targetType = edge.toType || edge.targetType;
        const targetId = edge.toId ?? edge.targetId;
        const datasetId = edge.datasetId ?? (String(sourceType).toLowerCase() === 'dataset' ? sourceId : null) ?? (String(targetType).toLowerCase() === 'dataset' ? targetId : null);
        const targetTypeLower = String(targetType || '').toLowerCase();
        const sourceTypeLower = String(sourceType || '').toLowerCase();
        addRow(sourceType, sourceId, targetType, targetId, datasetId, edge.relation || edge.relationship, {
            datasetName: edge.datasetName,
            cardDetail: targetTypeLower === 'card' ? nameFor('card', targetId) : sourceTypeLower === 'card' ? nameFor('card', sourceId) : '',
            pageDetail: targetTypeLower === 'page' ? nameFor('page', targetId) : sourceTypeLower === 'page' ? nameFor('page', sourceId) : ''
        });
    });

    // Dataset dependency objects are the richer Domo representation and are
    // used as a fallback when lineage is absent. They also fill in card/page
    // names where lineage only contains IDs.
    if (!rows.length) {
        (Array.isArray(dependencies) ? dependencies : []).forEach(dep => {
            const datasetId = dep.datasetId ?? dep.id;
            const datasetName = dep.datasetName || dep.name || nameFor('dataset', datasetId);
            (Array.isArray(dep.cards) ? dep.cards : []).forEach(card => addRow(
                'Dataset', datasetId, 'Card', card.cardId ?? card.id, datasetId, 'used_by', {
                    datasetName,
                    cardDetail: card.cardName || card.name || nameFor('card', card.cardId ?? card.id),
                    pageDetail: card.pageName || nameFor('page', card.pageId)
                }
            ));
            (Array.isArray(dep.pages) ? dep.pages : []).forEach(page => addRow(
                'Dataset', datasetId, 'Page', page.pageId ?? page.id, datasetId, 'used_by', {
                    datasetName,
                    cardDetail: page.cardName || (Array.isArray(page.cards) ? page.cards.map(c => c.name || c.cardName || nameFor('card', c.id || c.cardId)).join(', ') : ''),
                    pageDetail: page.pageName || page.name || nameFor('page', page.pageId ?? page.id)
                }
            ));
            (Array.isArray(dep.dataflows) ? dep.dataflows : []).forEach(flow => addRow(
                'Dataset', datasetId, 'DataFlow', flow.dataflowId ?? flow.id, datasetId, 'used_by', {
                    datasetName,
                    pageDetail: flow.dataflowName || flow.name || nameFor('dataflow', flow.dataflowId ?? flow.id)
                }
            ));
            (Array.isArray(dep.beastmodes) ? dep.beastmodes : []).forEach(bm => addRow(
                'Dataset', datasetId, 'Beast Mode', bm.id ?? bm.beastModeId, datasetId, 'contains', {
                    datasetName,
                    cardDetail: bm.name || bm.beastModeName || nameFor('beastmode', bm.id ?? bm.beastModeId)
                }
            ));
        });
    }

    /*
     * Diagram-only edges: Page -> Card ("contains"), Page -> Dataset
     * ("uses"), and Page -> App ("belongs_to", resolved from page.app_id
     * against apps[].dataAppId/title). These are not part of the Normal
     * mode table (which stays Dataset-centric to match the existing
     * lineage/dependency export), but they let the diagram show how a page
     * relates to its cards, datasets, and app without duplicating the
     * existing Dataset -> Page / Dataset -> Card edges.
     */
    const diagramOnlyRows = [];
    const seenPageDatasetPairs = new Set();
    (Array.isArray(pages) ? pages : []).forEach(page => {
        const pageId = page.pageId ?? page.id;
        const pageName = page.pageName || page.name || page.title || nameFor('page', pageId);
        const pageAppId = page.app_id ?? page.appId ?? page.dataAppId;

        (Array.isArray(page.cards) ? page.cards : []).forEach(card => {
            const cardId = card.cardId ?? card.id;
            const cardName = card.cardName || card.name || nameFor('card', cardId);
            const datasetId = card.dataSetId ?? card.datasetId ?? card.datasourceId ?? card.dataSourceId;

            addRow('Page', pageId, 'Card', cardId, datasetId, 'contains', {
                datasetName: datasetId != null ? nameFor('dataset', datasetId) : '-',
                cardDetail: cardName,
                pageDetail: pageName
            }, diagramOnlyRows);

            if (datasetId != null) {
                const pairKey = `${pageId}:${datasetId}`;
                if (!seenPageDatasetPairs.has(pairKey)) {
                    seenPageDatasetPairs.add(pairKey);
                    addRow('Page', pageId, 'Dataset', datasetId, datasetId, 'uses', {
                        datasetName: nameFor('dataset', datasetId),
                        cardDetail: cardName,
                        pageDetail: pageName
                    }, diagramOnlyRows);
                }
            }
        });

        // Page -> App: pages carry app_id, apps are keyed by dataAppId/title.
        if (pageAppId != null && pageAppId !== '') {
            addRow('Page', pageId, 'App', pageAppId, null, 'belongs_to', {
                datasetName: '-',
                cardDetail: nameFor('app', pageAppId),
                pageDetail: pageName
            }, diagramOnlyRows);
        }
    });

    const toDisplayRow = r => ({
        ...r,
        source: r.sourceName && r.sourceName !== String(r.sourceId) ? `${r.sourceName} (${r.sourceId})` : r.sourceName,
        target: r.targetName && r.targetName !== String(r.targetId) ? `${r.targetName} (${r.targetId})` : r.targetName,
        cardDetail: r.cardDetail || (String(r.targetType).toLowerCase() === 'card' ? r.targetName : '-'),
        pageDetail: r.pageDetail || (String(r.targetType).toLowerCase() === 'page' ? r.targetName : '-')
    });

    const displayRows = rows.map(toDisplayRow);

    // Diagram mode only: adds Page -> Card / Page -> Dataset edges on top of
    // the Normal mode rows, without touching the table/export data above.
    const diagramRows = [...rows, ...diagramOnlyRows].map(toDisplayRow);

    const modeToggle = (
        <ViewModeToggle
            viewMode={viewMode}
            onChange={setViewMode}
        />
    );

    if (viewMode === 'diagram') {

        return (
            <div style={{ padding: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px', gap: '12px' }}>
                    <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Dependencies</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            Diagrammatic view of lineage and object dependency relationships discovered from Domo, including page-to-card, page-to-dataset and page-to-app relationships.
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                        <ExportTabButton rows={buildDependenciesExportRows(displayRows)} sheetName="Dependencies" />
                        {modeToggle}
                    </div>
                </div>
                {!diagramRows.length ? (
                    <EmptyState message="No dependency or lineage metadata returned by Domo." />
                ) : (
                    <DependencyDiagram rows={diagramRows} />
                )}
            </div>
        );

    }

    return (
        <div style={{ padding: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px', gap: '12px' }}>
                <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Dependencies</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                        Lineage and object dependency relationships discovered from Domo. Names are resolved from the discovered cards, pages, datasets and DataFlows.
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <ExportTabButton rows={buildDependenciesExportRows(displayRows)} sheetName="Dependencies" />
                    {modeToggle}
                </div>
            </div>
            {!displayRows.length ? <EmptyState message="No dependency or lineage metadata returned by Domo." /> : (
                <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead>
                            <tr>
                                {[
                                    { key: 'sourceType', label: 'Source Type' },
                                    { key: 'source', label: 'Source' },
                                    { key: 'targetType', label: 'Target Type' },
                                    { key: 'target', label: 'Target' },
                                    { key: 'datasetName', label: 'Dataset' },
                                    { key: 'relationship', label: 'Relationship' },
                                    { key: 'cardDetail', label: 'Card / Detail' },
                                    { key: 'pageDetail', label: 'Page / Detail' }
                                ].map(c => <th key={c.key} style={tableHeaderStyle}>{c.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {displayRows.map((item, i) => (
                                <tr key={i}>
                                    {[
                                        'sourceType', 'source', 'targetType', 'target',
                                        'datasetName', 'relationship', 'cardDetail', 'pageDetail'
                                    ].map(key => (
                                        <td key={key} style={tableCellStyle}>{formatMetadataValue(item?.[key])}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/*
|--------------------------------------------------------------------------
| View Mode Toggle (Normal / Diagram)
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| Export Tab Button
|--------------------------------------------------------------------------
| Small reusable "Export Tab" button used by every assessment tab
| (including the analysis/diagram tabs - Overall Status, Merge Sets,
| Migration Waves, Metrics, Data Model - not just the plain data-table
| tabs). Exports whatever flat rows the calling tab builds for its own
| view into a single-sheet Excel workbook, independent of the full
| "Export to Excel" button in the page header.
|--------------------------------------------------------------------------
*/

function ExportTabButton({ rows, sheetName, filenamePrefix = 'Domo_Assessment' }) {

    const hasRows = Array.isArray(rows) && rows.length > 0;

    const handleClick = () => {
        if (!hasRows) return;
        exportWorkbook(
            [{ name: sheetName, rows }],
            `${filenamePrefix}_${sheetName.replace(/[^a-z0-9]+/gi, '_')}`
        );
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={!hasRows}
            title={`Export ${sheetName} to Excel`}
            aria-label={`Export ${sheetName} to Excel`}
            style={{
                height: '34px',
                flexShrink: 0,
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '0 14px',
                background: 'var(--card-bg)',
                color: hasRows ? 'var(--success)' : 'var(--text-tertiary)',
                fontSize: '13px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: hasRows ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
                transition: 'background .15s'
            }}
            onMouseOver={e => { if (hasRows) e.currentTarget.style.background = 'var(--success-bg)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'var(--card-bg)'; }}
        >
            <FontAwesomeIcon icon={faFileExcel} />
            Export Tab
        </button>
    );
}

function ViewModeToggle({ viewMode, onChange, options: optionsProp }) {

    const options = optionsProp || [
        { key: 'normal', label: 'Table', icon: faTable },
        { key: 'diagram', label: 'Diagram', icon: faSitemap }
    ];

    return (
        <div
            style={{
                display: 'flex',
                border: '1px solid var(--border-color)',
                borderRadius: '7px',
                overflow: 'hidden',
                flexShrink: 0
            }}
        >
            {options.map(opt => {

                const active = viewMode === opt.key;

                return (
                    <button
                        key={opt.key}
                        type="button"
                        onClick={() => onChange(opt.key)}
                        title={`${opt.label} mode`}
                        style={{
                            border: 'none',
                            padding: '7px 12px',
                            fontSize: '12px',
                            fontWeight: active ? 600 : 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            background: active ? '#1e447e' : 'var(--card-bg, #fff)',
                            color: active ? '#fff' : 'var(--text-secondary)'
                        }}
                    >
                        <FontAwesomeIcon icon={opt.icon} />
                        {opt.label}
                    </button>
                );

            })}
        </div>
    );
}

/*
|--------------------------------------------------------------------------
| Dependency Diagram (SVG node/edge graph, reused by Dependencies and
| Data Model tabs)
|--------------------------------------------------------------------------
*/

function DependencyDiagram({
    rows,
    typeOrder: typeOrderProp,
    typeLabel: typeLabelProp,
    typeColor: typeColorProp
}) {

    /*
     * Build a node/edge graph from the dependency rows. Nodes are grouped
     * into columns by type so the diagram reads left-to-right, similar to a
     * lineage flow chart. typeOrder/typeLabel/typeColor can be overridden by
     * callers (e.g. Users & Groups passes its own user/group types) so this
     * component isn't hardcoded to the Dependencies tab's object types.
     */

    /*
     * Default column order follows the natural drill-down hierarchy:
     * App -> Page -> Card -> Dataset (DataFlow/Beast Mode sit alongside
     * Card since they're also dataset-level objects). The edge anchoring
     * logic below already adapts to whichever direction a given edge runs,
     * so reordering this list only changes left-to-right column placement.
     */
    const typeOrder = typeOrderProp || ['app', 'page', 'card', 'dataflow', 'beastmode', 'dataset'];

    const typeLabel = typeLabelProp || {
        dataset: 'Dataset',
        dataflow: 'DataFlow',
        card: 'Card',
        page: 'Page',
        beastmode: 'Beast Mode',
        app: 'App'
    };

    const typeColor = typeColorProp || {
        dataset: '#1e447e',
        dataflow: '#7c3aed',
        card: '#0f766e',
        page: '#b45309',
        beastmode: '#be185d',
        app: '#4338ca'
    };

    const fallbackType = typeOrderProp ? typeOrder[0] : 'dataset';

    const normalizeType = t => {
        const v = String(t || '').toLowerCase().replace(/\s+/g, '');
        if (v === 'beastmode' || v === 'beast_mode') return 'beastmode';
        if (typeOrder.includes(v)) return v;
        return fallbackType;
    };

    /*
     * Building the node/edge graph (and the adjacency list used for
     * highlighting) is done once per `rows` change via useMemo, not on
     * every render. Without this, large datasets (hundreds/thousands of
     * rows) rebuilt the entire graph on every single mouse-move/hover
     * event, which is what caused the diagram to hang.
     */
    const graph = useMemo(() => {

        const nodesMap = new Map();

        const addNode = (type, id, name) => {
            const t = normalizeType(type);
            const key = `${t}:${id}`;
            if (!nodesMap.has(key)) {
                nodesMap.set(key, {
                    key,
                    type: t,
                    id,
                    name: name || String(id) || 'Unknown'
                });
            }
            return key;
        };

        const edges = [];
        const adjacency = new Map();

        const link = (a, b, edgeIndex) => {
            if (!adjacency.has(a)) adjacency.set(a, []);
            adjacency.get(a).push({ key: b, edgeIndex });
        };

        rows.forEach(row => {

            const sourceKey = addNode(row.sourceType, row.sourceId, row.sourceName);
            const targetKey = addNode(row.targetType, row.targetId, row.targetName);

            const edgeIndex = edges.length;
            edges.push({
                sourceKey,
                targetKey,
                relationship: row.relationship,
                row
            });

            link(sourceKey, targetKey, edgeIndex);
            link(targetKey, sourceKey, edgeIndex);

        });

        const nodes = Array.from(nodesMap.values());

        const columns = typeOrder
            .map(type => ({
                type,
                nodes: nodes.filter(n => n.type === type)
            }))
            .filter(col => col.nodes.length);

        const columnWidth = 230;
        const rowHeight = 56;
        const nodeWidth = 190;
        const nodeHeight = 38;
        const paddingX = 30;
        const paddingY = 30;

        const positions = new Map();
        const colIndexByKey = new Map();

        columns.forEach((col, colIndex) => {
            col.nodes.forEach((node, rowIndex) => {
                positions.set(node.key, {
                    x: paddingX + colIndex * columnWidth,
                    y: paddingY + rowIndex * rowHeight
                });
                colIndexByKey.set(node.key, colIndex);
            });
        });

        const maxRows = Math.max(1, ...columns.map(c => c.nodes.length));
        const svgWidth = paddingX * 2 + Math.max(1, columns.length) * columnWidth - (columnWidth - nodeWidth);
        const svgHeight = paddingY * 2 + maxRows * rowHeight;

        return {
            nodesMap, nodes, edges, adjacency, columns,
            columnWidth, rowHeight, nodeWidth, nodeHeight, paddingX, paddingY,
            positions, colIndexByKey, svgWidth, svgHeight
        };

    }, [rows]);

    const {
        nodesMap, nodes, edges, adjacency, columns,
        columnWidth, rowHeight, nodeWidth, nodeHeight, paddingX, paddingY,
        positions, colIndexByKey, svgWidth, svgHeight
    } = graph;

    const [hoveredKey, setHoveredKey] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);

    const activeKey = selectedKey || hoveredKey;

    /*
     * Highlighting follows the full connected chain from the active node
     * (e.g. App -> Page -> Card -> Dataset), not just its immediate
     * neighbors. This is a multi-hop BFS over the precomputed adjacency
     * list (O(V+E) instead of re-scanning every edge per queued node), so
     * selecting an App highlights every Page under it plus every
     * Card/Dataset those pages touch. `parentEdgeByKey` records the edge
     * each node was first reached through, so the side panel can list every
     * node in the chain (not just the active node's direct edges).
     */
    const { reachableKeys, parentEdgeByKey } = useMemo(() => {

        const reachable = new Set();
        const parentEdge = new Map();

        if (activeKey) {
            reachable.add(activeKey);
            const queue = [activeKey];
            while (queue.length) {
                const current = queue.shift();
                (adjacency.get(current) || []).forEach(({ key: next, edgeIndex }) => {
                    if (!reachable.has(next)) {
                        reachable.add(next);
                        parentEdge.set(next, { edge: edges[edgeIndex], parentKey: current });
                        queue.push(next);
                    }
                });
            }
        }

        return { reachableKeys: reachable, parentEdgeByKey: parentEdge };

    }, [activeKey, adjacency, edges]);

    const isDimmed = key => {
        if (!activeKey) return false;
        return !reachableKeys.has(key);
    };

    const activeNode = activeKey ? nodesMap.get(activeKey) : null;

    /* Full, untruncated relationship details for the active node, used by the
     * side panel since node labels in the SVG are too narrow to show
     * everything when the dataset/object names are long. Shows the entire
     * connected chain (e.g. every Page/Card/Dataset under an App), not just
     * the active node's direct edges. */
    const activeConnections = useMemo(() => {

        if (!activeKey) return [];

        const hopsOf = key => {
            let count = 0;
            let cursor = key;
            while (parentEdgeByKey.has(cursor)) {
                count += 1;
                cursor = parentEdgeByKey.get(cursor).parentKey;
            }
            return count;
        };

        return Array.from(reachableKeys)
            .filter(key => key !== activeKey)
            .map(key => {
                const otherNode = nodesMap.get(key);
                const link = parentEdgeByKey.get(key);
                const e = link?.edge;
                const direction = e && e.sourceKey === activeKey ? 'to'
                    : e && e.targetKey === activeKey ? 'from'
                    : (e && link.parentKey === e.sourceKey ? 'to' : 'from');
                const hops = hopsOf(key);
                return {
                    direction,
                    relationship: e?.relationship,
                    otherNode,
                    row: e?.row,
                    hops,
                    direct: hops === 1
                };
            })
            .sort((a, b) => a.hops - b.hops || String(a.otherNode?.type).localeCompare(String(b.otherNode?.type)));

    }, [activeKey, reachableKeys, parentEdgeByKey, nodesMap]);

    return (
        <div>

            {/* LEGEND */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                {typeOrder.filter(t => nodes.some(n => n.type === t)).map(t => (
                    <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: typeColor[t], display: 'inline-block' }} />
                        {typeLabel[t]}
                    </div>
                ))}
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: 'auto' }}>
                    Click a node to highlight its full chain (e.g. App to Page to Card to Dataset) and pin details below.
                </div>
            </div>

            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>

                <div
                    style={{
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        overflow: 'auto',
                        background: 'var(--card-bg, #fff)',
                        // padding: '10px',
                        flex: '1 1 auto',
                        minWidth: 0,
                        marginLeft: -25
                    }}
                >
                    <svg
                        width={Math.max(svgWidth, 400)}
                        height={Math.max(svgHeight, 200)}
                        style={{ display: 'block' }}
                    >

                        {/* EDGES */}
                        {edges.map((edge, i) => {

                            const from = positions.get(edge.sourceKey);
                            const to = positions.get(edge.targetKey);

                            if (!from || !to) return null;

                            // Anchor on whichever side actually faces the
                            // other node. This keeps "backward" edges (e.g.
                            // Page -> Card/Dataset, where Page sits to the
                            // right of Card/Dataset) from arcing behind
                            // other columns.
                            const fromColIndex = colIndexByKey.get(edge.sourceKey);
                            const toColIndex = colIndexByKey.get(edge.targetKey);
                            const forward = toColIndex >= fromColIndex;

                            const x1 = forward ? from.x + nodeWidth : from.x;
                            const y1 = from.y + nodeHeight / 2;
                            const x2 = forward ? to.x : to.x + nodeWidth;
                            const y2 = to.y + nodeHeight / 2;

                            const midX = (x1 + x2) / 2;

                            const dimmed = activeKey &&
                                (!reachableKeys.has(edge.sourceKey) || !reachableKeys.has(edge.targetKey));

                            return (
                                <path
                                    key={i}
                                    d={`M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`}
                                    fill="none"
                                    stroke={dimmed ? '#e2e8f0' : '#94a3b8'}
                                    strokeWidth={1.5}
                                    opacity={dimmed ? 0.4 : 0.9}
                                />
                            );

                        })}

                        {/* NODES */}
                        {nodes.map(node => {

                            const pos = positions.get(node.key);

                            if (!pos) return null;

                            const dimmed = isDimmed(node.key);
                            const selected = node.key === selectedKey;

                            return (
                                <g
                                    key={node.key}
                                    transform={`translate(${pos.x}, ${pos.y})`}
                                    onMouseEnter={() => setHoveredKey(node.key)}
                                    onMouseLeave={() => setHoveredKey(null)}
                                    onClick={() => setSelectedKey(prev => prev === node.key ? null : node.key)}
                                    style={{ cursor: 'pointer' }}
                                    opacity={dimmed ? 0.35 : 1}
                                >
                                    <rect
                                        width={nodeWidth}
                                        height={nodeHeight}
                                        rx={7}
                                        fill={typeColor[node.type]}
                                        fillOpacity={selected ? 0.28 : 0.12}
                                        stroke={typeColor[node.type]}
                                        strokeWidth={selected ? 2.5 : 1.5}
                                    />
                                    <text
                                        x={10}
                                        y={15}
                                        fontSize={9}
                                        fontWeight={700}
                                        fill={typeColor[node.type]}
                                    >
                                        {typeLabel[node.type]}
                                    </text>
                                    <text
                                        x={10}
                                        y={30}
                                        fontSize={11}
                                        fontWeight={600}
                                        fill="var(--text-primary)"
                                    >
                                        {String(node.name).length > 24 ? `${String(node.name).slice(0, 24)}...` : node.name}
                                    </text>
                                    <title>{node.name}</title>
                                </g>
                            );

                        })}

                    </svg>
                </div>

                {/* DETAILS PANEL - shows the full, untruncated relationship info for the hovered/selected node */}
                <div
                    style={{
                        width: '220px',
                        flexShrink: 0,
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--card-bg, #fff)',
                        padding: '14px',
                        maxHeight: Math.max(svgHeight, 200) + 22,
                        overflowY: 'auto',
                        position: 'sticky',
                        top: 0
                    }}
                >
                    {!activeNode ? (
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                            Hover or click a node in the diagram to see its full name and every related dependency here.
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: typeColor[activeNode.type], display: 'inline-block', flexShrink: 0 }} />
                                <div style={{ fontSize: '10px', fontWeight: 700, color: typeColor[activeNode.type], textTransform: 'uppercase' }}>
                                    {typeLabel[activeNode.type]}
                                </div>
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '2px', wordBreak: 'break-word' }}>
                                {activeNode.name}
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '12px' }}>
                                ID: {String(activeNode.id)}
                            </div>

                            <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px' }}>
                                {activeConnections.length} connected object{activeConnections.length === 1 ? '' : 's'} in this chain
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {activeConnections.map((conn, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '6px',
                                            padding: '8px',
                                            fontSize: '11px',
                                            marginLeft: `${(conn.hops - 1) * 10}px`
                                        }}
                                    >
                                        <div style={{ color: 'var(--text-tertiary)', fontSize: '10px', marginBottom: '4px' }}>
                                            {conn.direct
                                                ? (conn.direction === 'to' ? 'Depends on / links to' : 'Used by / linked from')
                                                : `${conn.hops} hops away`}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                            <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: typeColor[conn.otherNode?.type] || '#94a3b8', display: 'inline-block', flexShrink: 0 }} />
                                            <span style={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                                {conn.otherNode?.name || 'Unknown'}
                                            </span>
                                        </div>
                                        <div style={{ color: 'var(--text-secondary)' }}>
                                            Type: {typeLabel[conn.otherNode?.type] || '-'} &middot; Relationship: {conn.relationship || '-'}
                                        </div>
                                        {conn.row?.datasetName && conn.row.datasetName !== '-' && (
                                            <div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>
                                                Dataset: {conn.row.datasetName}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

            </div>

        </div>
    );
}

function SecurityAccessTab({ data = [] }) {
    return <MetadataTab title="Security & Access" description="Access-control and permission metadata returned by Domo. Missing fields are shown as unavailable rather than inferred." data={data} columns={[{key:'resourceType',label:'Resource Type'}, {key:'resourceId',label:'Resource ID'}, {key:'resourceName',label:'Resource'}, {key:'owner',label:'Owner'}, {key:'permissions',label:'Permissions'}, {key:'security',label:'Security'}, {key:'metadataReturned',label:'Metadata Returned'}]} emptyMessage="No security/access metadata was returned by Domo." />;
}

function GovernanceTab({ data = [] }) {
    return <MetadataTab title="Governance" description="Ownership, tags, certification, refresh and governance metadata returned by Domo." data={data} columns={[{key:'resourceType',label:'Resource Type'}, {key:'resourceId',label:'Resource ID'}, {key:'resourceName',label:'Resource'}, {key:'owner',label:'Owner'}, {key:'description',label:'Description'}, {key:'tags',label:'Tags'}, {key:'certification',label:'Certification'}, {key:'refresh',label:'Refresh'}, {key:'schedule',label:'Schedule'}]} emptyMessage="No governance metadata was returned by Domo." />;
}

function IssuesTab({ data = [] }) {
    return <MetadataTab title="Issues" description="Assessment issues identified during Domo discovery and migration analysis." data={data} columns={[{key:'severity',label:'Severity'}, {key:'type',label:'Type'}, {key:'message',label:'Issue'}, {key:'datasetName',label:'Dataset'}, {key:'pageName',label:'Page'}, {key:'cardName',label:'Card'}]} emptyMessage="No assessment issues found." />;
}

function ApiCoverageTab({ data = [] }) {
    return <MetadataTab title="API Coverage" description="Shows which Domo metadata categories were actually returned by the current access/API implementation." data={data} columns={[{key:'name',label:'Metadata'}, {key:'count',label:'Count'}, {key:'status',label:'Status'}, {key:'message',label:'Details'}]} emptyMessage="No API coverage information returned." />;
}

/*
|--------------------------------------------------------------------------
| Assessment Table
|--------------------------------------------------------------------------
*/

function AssessmentTable({
    type,
    data,
    pages,
    datasets,
    cards,
    beastmodes,
    onViewPage,
    pageComplexityConfig
}) {

    if (!data?.length) {

        return (
            <EmptyState
                message={
                    `No ${getTabLabel(type)} found.`
                }
            />
        );
    }


    switch (type) {

        case 'datasets':

            return (
                <DatasetAssessmentTable
                    data={data}
                    pages={pages}
                />
            );


        case 'cards':

            return (
                <CardAssessmentTable
                    data={data}
                    pages={pages}
                />
            );


        case 'pages':

            return (
                <PageAssessmentTable
                    data={data}
                    onViewPage={onViewPage}
                    allPages={pages}
                    datasets={datasets}
                    cards={cards}
                    beastmodes={beastmodes}
                    pageComplexityConfig={pageComplexityConfig}
                />
            );


        case 'dataflows':

            return (
                <DataflowAssessmentTable
                    data={data}
                />
            );


        case 'beastmodes':

            return (
                <BeastModeAssessmentTable
                    data={data}
                />
            );


        default:
            return null;
    }
}


/*
|--------------------------------------------------------------------------
| Dataset Table
|--------------------------------------------------------------------------
*/

function DatasetQueryModal({ dataset, onClose }) {
    if (!dataset) return null;
    const ds = getDatasetDatasourceDetails(dataset);
    return (
        <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:10000,padding:20}}>
            <div style={{width:'min(900px,100%)',maxHeight:'85vh',background:'var(--card-bg,#fff)',border:'1px solid var(--border-color)',borderRadius:10,overflow:'hidden',boxShadow:'0 15px 50px rgba(0,0,0,.2)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'15px 18px',borderBottom:'1px solid var(--border-color)'}}>
                    <div><div style={{fontSize:15,fontWeight:700}}>Datasource SQL</div><div style={{fontSize:11,color:'var(--text-secondary)',marginTop:3}}>{dataset.datasetName || dataset.name || dataset.datasetId || 'Dataset'}</div></div>
                    <button type="button" onClick={onClose} style={{border:'none',background:'var(--hover-bg)',borderRadius:6,width:32,height:32,cursor:'pointer',fontSize:18}}>x</button>
                </div>
                <div style={{padding:18,overflowY:'auto'}}>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:14}}>
                        {[['Driver',ds.driver],['Schema',ds.schema],['Table',ds.tableName],['Query Type',ds.queryType]].map(([k,v])=><div key={k} style={{border:'1px solid var(--border-color)',borderRadius:7,padding:9}}><div style={{fontSize:10,color:'var(--text-secondary)'}}>{k}</div><div style={{fontSize:11,fontWeight:600,marginTop:3}}>{v}</div></div>)}
                    </div>
                    <div style={{fontSize:11,fontWeight:700,marginBottom:6}}>SQL Query</div>
                    <pre style={{margin:0,padding:14,border:'1px solid var(--border-color)',borderRadius:7,background:'var(--hover-bg)',fontSize:11,lineHeight:1.5,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{ds.query !== '-' ? ds.query : 'No SQL query returned for this dataset.'}</pre>
                </div>
            </div>
        </div>
    );
}

function DatasetAssessmentTable({ data, pages = [] }) {
    const [queryDataset, setQueryDataset] = useState(null);
    const getCardCount = datasetId => !datasetId ? 0 : pages.reduce((count,page) => count + (Array.isArray(page.cards) ? page.cards.filter(card => String(card.dataSetId || card.datasetId) === String(datasetId)).length : 0), 0);
    return (
        <>
            <AssessmentSimpleTable
                headers={['Dataset','Dataset ID','Columns','Rows','Cards','Datasource / Connector','Schema','Source Table','Query Type','Update Mode','SQL','Updated At']}
                rows={(data || []).map(item => {
                    const datasetId = item.datasetId || item.id;
                    const ds = getDatasetDatasourceDetails(item);
                    const connector = ds.driver !== '-' ? ds.driver : 'File';
                    return [
                        item.datasetName || item.name || '-', datasetId || '-', item.schema?.length ?? item.columns?.length ?? 0,
                        item.rows ?? 0, getCardCount(datasetId), connector, ds.schema, ds.tableName, ds.queryType, ds.updateMode,
                        ds.hasQuery ? <button type="button" title="View SQL query" onClick={() => setQueryDataset(item)} style={{border:'none',background:'transparent',cursor:'pointer',padding:'2px 6px',color:'#1e447e'}}><FontAwesomeIcon icon={faCircleInfo} /></button> : '-',
                        item?.updatedAt || '-'
                    ];
                })}
            />
            {queryDataset && <DatasetQueryModal dataset={queryDataset} onClose={() => setQueryDataset(null)} />}
        </>
    );
}

/*
|--------------------------------------------------------------------------
| Card Table
|--------------------------------------------------------------------------
*/

function CardAssessmentTable({
    data,
    pages
}) {

    const [filterCard, setFilterCard] = useState(null);
    const [variableCard, setVariableCard] = useState(null);
    const [hierarchyCard, setHierarchyCard] = useState(null);
    const [setsCard, setSetsCard] = useState(null);
    const [groupsCard, setGroupsCard] = useState(null);
    const [calcCard, setCalcCard] = useState(null);

    const cardMap = {};


    (pages || []).forEach(
        page => {

            (page.cards || []).forEach(
                card => {

                    cardMap[
                        `${page.id}_${card.id}`
                    ] = card;

                }
            );

        }
    );


    return (

        <>

        <AssessmentSimpleTable
            headers={[
                'Card',
                'Dataset',
                'Chart Type',
                'Page',
                'Calculated Fields',
                'Filters',
                'Variables',
                'Hierarchy',
                'Sets',
                'Groups'
            ]}
            rows={
                data.map(
                    item => {

                        const pageId =
                            item.pageId;


                        const cardId =
                            item.cardId ||
                            item.id ||
                            item.urn;


                        const card =
                            cardMap[
                                `${pageId}_${cardId}`
                            ];


                        const dataSetId =
                            card?.dataSetId ||
                            item.dataSetId ||
                            item.datasetId;


                        const cardFilters = Array.from(
                            new Map(
                                [
                                    ...(card?.chartBody?.filters || []),
                                    ...(card?.chartBody?.filters || [])
                                ].map(filter => [
                                    JSON.stringify(filter),
                                    filter
                                ])
                            ).values()
                        );

                        const variables = card?.beastMode?.length
                        ? card.beastMode
                            .flatMap(calculation => calculation.templateDependencyData || [])
                            .filter(dependency => dependency.variable === true)
                        : [];

                        const hierarchy = card?.hierarchy?.length
                        ? card.hierarchy : [];

                        const sets = card?.sets?.length
                        ? card.sets : [];

                        const groups = card?.groups?.length
                        ? card.groups : [];

                        const calc = card?.calc?.length
                        ? card.calc : [];

                        return [

                            item.cardName ||
                                item.name ||
                                item.title ||
                                card?.title ||
                                '-',

                            item.datasetName ||
                                dataSetId ||
                                item.datasourceId ||
                                '-',

                            item.chartType ||
                                card?.chartType ||
                                item.type ||
                                '-',

                            item.pageName ||
                                '-',

                            calc.length ? (
                                <button
                                    type="button"
                                    title="View calculated values"
                                    onClick={() =>
                                        setCalcCard({
                                            name:
                                                item.cardName ||
                                                item.name ||
                                                item.title ||
                                                '-',
                                            variables: calc
                                        })
                                    }
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        padding: '2px 6px',
                                        color: '#1e447e',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '11px',
                                        fontWeight: 600
                                    }}
                                >
                                    {calc.length}
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </button>
                            ) : '-',

                            cardFilters.length ? (
                                <button
                                    type="button"
                                    title="View filter values"
                                    onClick={() =>
                                        setFilterCard({
                                            name:
                                                item.cardName ||
                                                item.name ||
                                                item.title ||
                                                '-',
                                            filters: cardFilters
                                        })
                                    }
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        padding: '2px 6px',
                                        color: '#1e447e',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '11px',
                                        fontWeight: 600
                                    }}
                                >
                                    {cardFilters.length}
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </button>
                            ) : '-',

                            variables.length ? (
                                <button
                                    type="button"
                                    title="View variables values"
                                    onClick={() =>
                                        setVariableCard({
                                            name:
                                                item.cardName ||
                                                item.name ||
                                                item.title ||
                                                '-',
                                            variables: variables
                                        })
                                    }
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        padding: '2px 6px',
                                        color: '#1e447e',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '11px',
                                        fontWeight: 600
                                    }}
                                >
                                    {variables.length}
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </button>
                            ) : '-',

                            hierarchy.length ? (
                                <button
                                    type="button"
                                    title="View hierarchy values"
                                    onClick={() =>
                                        setHierarchyCard({
                                            name:
                                                item.cardName ||
                                                item.name ||
                                                item.title ||
                                                '-',
                                            variables: hierarchy
                                        })
                                    }
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        padding: '2px 6px',
                                        color: '#1e447e',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '11px',
                                        fontWeight: 600
                                    }}
                                >
                                    {hierarchy.length}
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </button>
                            ) : '-',

                            sets.length ? (
                                <button
                                    type="button"
                                    title="View sets values"
                                    onClick={() =>
                                        setSetsCard({
                                            name:
                                                item.cardName ||
                                                item.name ||
                                                item.title ||
                                                '-',
                                            variables: sets
                                        })
                                    }
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        padding: '2px 6px',
                                        color: '#1e447e',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '11px',
                                        fontWeight: 600
                                    }}
                                >
                                    {sets.length}
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </button>
                            ) : '-',

                            groups.length ? (
                                <button
                                    type="button"
                                    title="View sets values"
                                    onClick={() =>
                                        setGroupsCard({
                                            name:
                                                item.cardName ||
                                                item.name ||
                                                item.title ||
                                                '-',
                                            variables: groups
                                        })
                                    }
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        padding: '2px 6px',
                                        color: '#1e447e',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '11px',
                                        fontWeight: 600
                                    }}
                                >
                                    {groups.length}
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </button>
                            ) : '-'

                        ];

                    }
                )
            }
        />

        {filterCard && (
            <CardFiltersModal
                cardName={filterCard.name}
                filters={filterCard.filters}
                onClose={() => setFilterCard(null)}
            />
        )}

        {variableCard && (
            <CardVariablesModal
                cardName={variableCard.name}
                filters={variableCard.variables}
                onClose={() => setVariableCard(null)}
            />
        )}

        {hierarchyCard && (
            <CardHierarchyModal
                cardName={hierarchyCard.name}
                filters={hierarchyCard.variables}
                onClose={() => setHierarchyCard(null)}
            />
        )}

        {setsCard && (
            <CardSetsModal
                cardName={setsCard.name}
                filters={setsCard.variables}
                onClose={() => setSetsCard(null)}
            />
        )}

        {groupsCard && (
            <CardGroupsModal
                cardName={groupsCard.name}
                filters={groupsCard.variables}
                onClose={() => setGroupsCard(null)}
            />
        )}

        {calcCard && (
            <CardCalcModal
                cardName={calcCard.name}
                filters={calcCard.variables}
                onClose={() => setCalcCard(null)}
            />
        )}

        </>

    );
}


/*
|--------------------------------------------------------------------------
| Card Filters Modal
|--------------------------------------------------------------------------
*/

function CardFiltersModal({ cardName, filters = [], onClose }) {
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10000, padding: 20
            }}
        >
            <div style={{
                width: 'min(700px,100%)', maxHeight: '80vh',
                background: 'var(--card-bg,#fff)', border: '1px solid var(--border-color)',
                borderRadius: 10, overflow: 'hidden', boxShadow: '0 15px 50px rgba(0,0,0,.2)',
                display: 'flex', flexDirection: 'column'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '15px 18px', borderBottom: '1px solid var(--border-color)'
                }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>Filter Values</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>{cardName}</div>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        border: 'none', background: 'var(--hover-bg)', borderRadius: 6,
                        width: 32, height: 32, cursor: 'pointer', fontSize: 18
                    }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                <div style={{ padding: 16, overflowY: 'auto' }}>
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr>
                                    {['Column', 'Operator', 'Values'].map(h => (
                                        <th key={h} style={tableHeaderStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filters.map((f, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <td style={{ ...tableCellStyle, fontWeight: 600 }}>{f.column || '-'}</td>
                                        <td style={tableCellStyle}>{f.operand || f.operator || '-'}</td>
                                        <td style={tableCellStyle}>
                                            {Array.isArray(f.values)
                                                ? f.values.join(', ')
                                                : (f.values || '-')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CardVariablesModal({ cardName, filters = [], onClose }) {
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10000, padding: 20
            }}
        >
            <div style={{
                width: 'min(700px,100%)', maxHeight: '80vh',
                background: 'var(--card-bg,#fff)', border: '1px solid var(--border-color)',
                borderRadius: 10, overflow: 'hidden', boxShadow: '0 15px 50px rgba(0,0,0,.2)',
                display: 'flex', flexDirection: 'column'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '15px 18px', borderBottom: '1px solid var(--border-color)'
                }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>Variables Values</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>{cardName}</div>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        border: 'none', background: 'var(--hover-bg)', borderRadius: 6,
                        width: 32, height: 32, cursor: 'pointer', fontSize: 18
                    }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                <div style={{ padding: 16, overflowY: 'auto' }}>
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr>
                                    {['Name', 'Description', 'Type' ,'Expression'].map(h => (
                                        <th key={h} style={tableHeaderStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filters.map((f, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <td style={{ ...tableCellStyle, fontWeight: 600 }}>{f.name || '-'}</td>
                                        <td style={tableCellStyle}>{f.description || '-'}</td>
                                        <td style={tableCellStyle}>{f.dataType || '-'}</td>
                                        <td style={tableCellStyle}>
                                            {f.expression || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CardHierarchyModal({ cardName, filters = [], onClose }) {
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10000, padding: 20
            }}
        >
            <div style={{
                width: 'min(700px,100%)', maxHeight: '80vh',
                background: 'var(--card-bg,#fff)', border: '1px solid var(--border-color)',
                borderRadius: 10, overflow: 'hidden', boxShadow: '0 15px 50px rgba(0,0,0,.2)',
                display: 'flex', flexDirection: 'column'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '15px 18px', borderBottom: '1px solid var(--border-color)'
                }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>Hierarchy Values</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>{cardName}</div>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        border: 'none', background: 'var(--hover-bg)', borderRadius: 6,
                        width: 32, height: 32, cursor: 'pointer', fontSize: 18
                    }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                <div style={{ padding: 16, overflowY: 'auto' }}>
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr>
                                    {['Name', 'Chart Type', 'Type' ,'Access', 'URN'].map(h => (
                                        <th key={h} style={tableHeaderStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filters.map((f, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <td style={{ ...tableCellStyle, fontWeight: 600 }}>{f.title || '-'}</td>
                                        <td style={tableCellStyle}>{f.chartType || '-'}</td>
                                        <td style={tableCellStyle}>{f.type || '-'}</td>
                                        <td style={tableCellStyle}>
                                            {JSON.stringify(f.access) || '-'}
                                        </td>
                                        <td style={tableCellStyle}>
                                            {f.urn || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CardSetsModal({ cardName, filters = [], onClose }) {
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10000, padding: 20
            }}
        >
            <div style={{
                width: 'min(700px,100%)', maxHeight: '80vh',
                background: 'var(--card-bg,#fff)', border: '1px solid var(--border-color)',
                borderRadius: 10, overflow: 'hidden', boxShadow: '0 15px 50px rgba(0,0,0,.2)',
                display: 'flex', flexDirection: 'column'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '15px 18px', borderBottom: '1px solid var(--border-color)'
                }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>Sets Values</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>{cardName}</div>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        border: 'none', background: 'var(--hover-bg)', borderRadius: 6,
                        width: 32, height: 32, cursor: 'pointer', fontSize: 18
                    }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                <div style={{ padding: 16, overflowY: 'auto' }}>
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr>
                                    {['Name', 'Type' ,'Status', 'Expression'].map(h => (
                                        <th key={h} style={tableHeaderStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filters.map((f, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <td style={{ ...tableCellStyle, fontWeight: 600 }}>{f.name || '-'}</td>
                                        <td style={tableCellStyle}>{f.dataType || '-'}</td>
                                        <td style={tableCellStyle}>{f.status || '-'}</td>
                                        <td style={tableCellStyle}>
                                            {JSON.stringify(f.expression) || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CardGroupsModal({ cardName, filters = [], onClose }) {
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10000, padding: 20
            }}
        >
            <div style={{
                width: 'min(700px,100%)', maxHeight: '80vh',
                background: 'var(--card-bg,#fff)', border: '1px solid var(--border-color)',
                borderRadius: 10, overflow: 'hidden', boxShadow: '0 15px 50px rgba(0,0,0,.2)',
                display: 'flex', flexDirection: 'column'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '15px 18px', borderBottom: '1px solid var(--border-color)'
                }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>Groups Values</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>{cardName}</div>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        border: 'none', background: 'var(--hover-bg)', borderRadius: 6,
                        width: 32, height: 32, cursor: 'pointer', fontSize: 18
                    }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                <div style={{ padding: 16, overflowY: 'auto' }}>
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr>
                                    {['Name', 'Type' ,'Status', 'Expression'].map(h => (
                                        <th key={h} style={tableHeaderStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filters.map((f, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <td style={{ ...tableCellStyle, fontWeight: 600 }}>{f.name || '-'}</td>
                                        <td style={tableCellStyle}>{f.dataType || '-'}</td>
                                        <td style={tableCellStyle}>{f.status || '-'}</td>
                                        <td style={tableCellStyle}>
                                            {JSON.stringify(f.expression) || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CardCalcModal({ cardName, filters = [], onClose }) {
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10000, padding: 20
            }}
        >
            <div style={{
                width: 'min(700px,100%)', maxHeight: '80vh',
                background: 'var(--card-bg,#fff)', border: '1px solid var(--border-color)',
                borderRadius: 10, overflow: 'hidden', boxShadow: '0 15px 50px rgba(0,0,0,.2)',
                display: 'flex', flexDirection: 'column'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '15px 18px', borderBottom: '1px solid var(--border-color)'
                }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>Calculated Values</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>{cardName}</div>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        border: 'none', background: 'var(--hover-bg)', borderRadius: 6,
                        width: 32, height: 32, cursor: 'pointer', fontSize: 18
                    }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                <div style={{ padding: 16, overflowY: 'auto' }}>
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr>
                                    {['Name', 'Type' ,'Status', 'Expression'].map(h => (
                                        <th key={h} style={tableHeaderStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filters.map((f, idx) => {
                                    const expression =
                                        f.expression ||
                                        f.formula ||
                                        f.sql ||
                                        '';
                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={{ ...tableCellStyle, fontWeight: 600 }}>{f.name || '-'}</td>
                                            <td style={tableCellStyle}>{f.dataType || '-'}</td>
                                            <td style={tableCellStyle}>{f.status || '-'}</td>
                                            <td style={tableCellStyle}>
                                                {expression
                                                    ? (typeof expression === 'string' ? expression : JSON.stringify(expression))
                                                    : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

/*
|--------------------------------------------------------------------------
| PAGE TABLE
|--------------------------------------------------------------------------
*/

function PageAssessmentTable({
    data,
    onViewPage,
    allPages = [],
    datasets = [],
    cards = [],
    beastmodes = [],
    pageComplexityConfig
}) {

    return (

        <AssessmentSimpleTable
            headers={[
                'Page',
                'Page ID',
                'Cards',
                'Datasets',
                'Complexity',
                'Details'
            ]}
            rows={
                data.map(
                    item => {

                        const pageComplexity =
                            calculatePageComplexity(
                                item,
                                allPages,
                                datasets,
                                cards,
                                beastmodes,
                                pageComplexityConfig
                            );

                        return [

                        item.pageName ||
                            item.name ||
                            item.title ||
                            '-',

                        item.pageId ||
                            item.id ||
                            '-',

                        item.cardCount ??
                            item.cards?.length ??
                            0,

                        item.datasetCount ??
                            getPageDatasetCount(item),

                        <StatusBadge
                            key={`complexity-${item.pageId || item.id}`}
                            value={pageComplexity.complexity}
                        />,

                        <button
                            key={
                                item.pageId ||
                                item.id
                            }
                            type="button"
                            onClick={() =>
                                onViewPage(
                                    item
                                )
                            }
                            style={{
                                border:
                                    '1px solid #1e447e',
                                background:
                                    '#eef3ff',
                                color:
                                    '#1e447e',
                                borderRadius: '5px',
                                padding: '6px 10px',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >

                            <FontAwesomeIcon
                                icon={faEye}
                            />

                            View Details

                        </button>

                        ];
                    }
                )
            }
        />

    );
}


/*
|--------------------------------------------------------------------------
| Page Complexity
|--------------------------------------------------------------------------
| Calculated from the page's own cards, datasets, variables, sets, groups
| and hierarchy usage, plus how many other pages are linked under the same
| Domo app (more pages sharing an app implies more coordination/migration
| complexity for this page).
|--------------------------------------------------------------------------
*/

/*
 * Default factor weights + Low/Medium/High thresholds for Page Complexity.
 * Used only until the backend config (GET /assessment/page-complexity-config)
 * loads, or if that request fails - the backend value is the source of
 * truth once available, same pattern as the Pairwise weights above.
 */
const DEFAULT_PAGE_COMPLEXITY_CONFIG = {
    weights: {
        cards: 2,
        datasets: 2,
        variables: 1,
        sets: 1,
        groups: 1,
        hierarchy: 1,
        appLinkedPages: 1.5
    },
    thresholds: {
        high: 20,
        medium: 8
    }
};

function calculatePageComplexity(
    page,
    allPages = [],
    datasets = [],
    allCards = [],
    beastmodes = [],
    config = DEFAULT_PAGE_COMPLEXITY_CONFIG
) {

    const weights = { ...DEFAULT_PAGE_COMPLEXITY_CONFIG.weights, ...(config?.weights || {}) };
    const thresholds = { ...DEFAULT_PAGE_COMPLEXITY_CONFIG.thresholds, ...(config?.thresholds || {}) };

    const rows = buildPageDetailsRows(page, datasets, allCards, beastmodes);

    const cardCount = new Set(
        rows.map(row => row.cardKey).filter(Boolean)
    ).size;

    const datasetCount = new Set(
        rows.map(row => row.datasetKey).filter(Boolean)
    ).size;

    const variablesCount = rows.reduce(
        (total, row) => total + (row.cardVariables?.length || 0),
        0
    );

    const setsCount = rows.reduce(
        (total, row) => total + (row.cardSets?.length || 0),
        0
    );

    const groupsCount = rows.reduce(
        (total, row) => total + (row.cardGroups?.length || 0),
        0
    );

    const hierarchyCount = rows.reduce(
        (total, row) => total + (row.cardHierarchy?.length || 0),
        0
    );

    const pageAppId = page?.app_id ?? page?.appId ?? page?.dataAppId;
    const pageId = page?.pageId ?? page?.id;

    const appLinkedPages =
        pageAppId != null && pageAppId !== ''
            ? (allPages || []).filter(other => {
                const otherAppId = other?.app_id ?? other?.appId ?? other?.dataAppId;
                const otherPageId = other?.pageId ?? other?.id;
                return (
                    otherAppId != null &&
                    String(otherAppId) === String(pageAppId) &&
                    String(otherPageId) !== String(pageId)
                );
            }).length
            : 0;

    const score =
        (cardCount * weights.cards) +
        (datasetCount * weights.datasets) +
        (variablesCount * weights.variables) +
        (setsCount * weights.sets) +
        (groupsCount * weights.groups) +
        (hierarchyCount * weights.hierarchy) +
        (appLinkedPages * weights.appLinkedPages);

    let complexity = 'Low';

    if (score >= thresholds.high) {
        complexity = 'High';
    } else if (score >= thresholds.medium) {
        complexity = 'Medium';
    }

    return {
        complexity,
        cardCount,
        datasetCount,
        variablesCount,
        setsCount,
        groupsCount,
        hierarchyCount,
        appLinkedPages,
        score
    };
}


/*
|--------------------------------------------------------------------------
| DataFlow Table
|--------------------------------------------------------------------------
*/

function DataflowAssessmentTable({
    data
}) {

    return (

        <AssessmentSimpleTable
            headers={[
                'DataFlow',
                'DataFlow ID',
                'State',
                'Last Execution',
                'Inputs Datasource',
                'Output Datasource'
            ]}
            rows={
                data.map(
                    item => [

                        item.dataflowName ||
                            item.name ||
                            item.title ||
                            '-',

                        item.dataflowId ||
                            item.id ||
                            '-',

                        item.runState || '-',

                        item.lastExecution.lastUpdated ? new Date(item.lastExecution.lastUpdated).toISOString() : '-',

                        item?.inputs?.length !== 0
                        ? item?.inputs?.map(input => input.dataSourceName).join(', ')
                        : '-',

                        item?.outputs?.length !== 0
                        ? item?.outputs?.map(input => input.dataSourceName).join(', ')
                        : '-',
                    ]
                )
            }
        />

    );
}


/*
|--------------------------------------------------------------------------
| Beast Mode Table
|--------------------------------------------------------------------------
*/

function BeastModeAssessmentTable({
    data
}) {

    return (

        <AssessmentSimpleTable
            headers={[
                'Beast Mode',
                'Expression'
            ]}
            rows={
                data.map(
                    item => [

                        item.beastmodeName ||
                            item.name ||
                            item.title ||
                            '-',

                        item.expression ||
                            item.formula ||
                            '-'

                    ]
                )
            }
        />

    );
}


/*
|--------------------------------------------------------------------------
| Generic Table
|--------------------------------------------------------------------------
*/

function AssessmentSimpleTable({
    headers,
    rows
}) {

    return (

        <div
            style={{
                overflowX: 'auto'
            }}
        >

            <table
                style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px'
                }}
            >

                <thead>

                    <tr>

                        {headers.map(
                            header => (

                                <th
                                    key={header}
                                    style={tableHeaderStyle}
                                >
                                    {header}
                                </th>

                            )
                        )}

                    </tr>

                </thead>


                <tbody>

                    {rows.map(
                        (
                            row,
                            rowIndex
                        ) => (

                            <tr
                                key={rowIndex}
                                style={{
                                    borderBottom:
                                        '1px solid var(--border-color)'
                                }}
                            >

                                {row.map(
                                    (
                                        value,
                                        columnIndex
                                    ) => (

                                        <td
                                            key={columnIndex}
                                            style={{
                                                ...tableCellStyle,
                                                maxWidth: '350px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={
                                                typeof value ===
                                                'string'
                                                    ? value
                                                    : undefined
                                            }
                                        >

                                            {value}

                                        </td>

                                    )
                                )}

                            </tr>

                        )
                    )}

                </tbody>

            </table>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Status Badge
|--------------------------------------------------------------------------
*/

function StatusBadge({
    value
}) {

    if (
        value === null ||
        value === undefined ||
        value === ''
    ) {

        return '-';
    }


    return (

        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 600,
                background:
                    getStatusBackground(value),
                color:
                    getStatusColor(value)
            }}
        >

            {getStatusIcon(value)}

            {value}

        </span>
    );
}


/*
|--------------------------------------------------------------------------
| Status Icon
|--------------------------------------------------------------------------
*/

function getStatusIcon(value) {

    const normalized =
        String(value)
            .toLowerCase();


    if (
        normalized.includes('ready') ||
        normalized.includes('low')
    ) {

        return (
            <FontAwesomeIcon
                icon={faCircleCheck}
            />
        );
    }


    if (
        normalized.includes('blocked') ||
        normalized.includes('high')
    ) {

        return (
            <FontAwesomeIcon
                icon={faCircleXmark}
            />
        );
    }


    return (
        <FontAwesomeIcon
            icon={faTriangleExclamation}
        />
    );
}


/*
|--------------------------------------------------------------------------
| Status Color
|--------------------------------------------------------------------------
*/

function getStatusColor(value) {

    if (!value) {
        return '#1e447e';
    }


    const normalized =
        String(value)
            .toLowerCase();


    if (
        normalized.includes('ready') ||
        normalized.includes('low')
    ) {

        return '#198754';
    }


    if (
        normalized.includes('blocked') ||
        normalized.includes('high')
    ) {

        return '#dc3545';
    }


    if (
        normalized.includes('medium') ||
        normalized.includes('review')
    ) {

        return '#d97706';
    }


    return '#1e447e';
}


/*
|--------------------------------------------------------------------------
| Status Background
|--------------------------------------------------------------------------
*/

function getStatusBackground(value) {

    if (!value) {
        return '#eef3ff';
    }


    const normalized =
        String(value)
            .toLowerCase();


    if (
        normalized.includes('ready') ||
        normalized.includes('low')
    ) {

        return '#e8f7ee';
    }


    if (
        normalized.includes('blocked') ||
        normalized.includes('high')
    ) {

        return '#fdecec';
    }


    if (
        normalized.includes('medium') ||
        normalized.includes('review')
    ) {

        return '#fff4e5';
    }


    return '#eef3ff';
}


/*
|--------------------------------------------------------------------------
| Assessment Counts
|--------------------------------------------------------------------------
*/

function getAssessmentCounts(
    items = [],
    field
) {

    return items.reduce(
        (
            counts,
            item
        ) => {

            const value =
                String(
                    item?.[field] || ''
                )
                    .toLowerCase()
                    .trim();


            if (
                field === 'complexity'
            ) {

                if (value === 'low') {
                    counts.low++;
                }

                else if (value === 'medium') {
                    counts.medium++;
                }

                else if (value === 'high') {
                    counts.high++;
                }

            }


            if (
                field === 'migrationReadiness'
            ) {

                if (value === 'ready') {
                    counts.ready++;
                }

                else if (value === 'blocked') {
                    counts.blocked++;
                }

                else if (
                    value === 'review' ||
                    value === 'review required'
                ) {

                    counts.review++;

                }

            }


            return counts;

        },

        field === 'complexity'

            ? {
                low: 0,
                medium: 0,
                high: 0
            }

            : {
                ready: 0,
                review: 0,
                blocked: 0
            }
    );
}


/*
|--------------------------------------------------------------------------
| Overall Readiness
|--------------------------------------------------------------------------
*/

function getOverallReadiness(
    counts
) {

    if (counts.blocked > 0) {
        return 'Blocked';
    }

    if (counts.review > 0) {
        return 'Review Required';
    }

    return 'Ready';
}


/*
|--------------------------------------------------------------------------
| Overall Complexity
|--------------------------------------------------------------------------
*/

function getOverallComplexity(
    counts
) {

    if (counts.high > 0) {
        return 'High';
    }

    if (counts.medium > 0) {
        return 'Medium';
    }

    return 'Low';
}

/*
|--------------------------------------------------------------------------
| Build Dashboard Analysis
|--------------------------------------------------------------------------
|
| Fallback if backend doesn't provide dependencies.
|
*/

function buildDashboardAnalysis(
    pages,
    cards,
    datasets,
    beastmodes,
    assessmentPages
) {

    const result = [];


    const pageList =
        assessmentPages.length
            ? assessmentPages
            : pages;


    for (
        const page of pageList
    ) {

        const pageCards =
            page.cards ||
            [];


        const pageCardIds =
            pageCards.map(
                card =>
                    typeof card ===
                        'object'
                        ? card.id
                        : card
            );


        const relatedCards =
            cards.filter(
                card => {

                    if (
                        !pageCardIds.length
                    ) {
                        return false;
                    }

                    return pageCardIds.some(
                        id =>
                            String(
                                id
                            ) ===
                            String(
                                card.id
                            )
                    );

                }
            );


        const datasetIds =
            relatedCards
                .map(
                    card =>
                        card.datasetId ||
                        card.datasourceId ||
                        card.dataSourceId
                )
                .filter(Boolean);


        const uniqueDatasetIds =
            [
                ...new Set(
                    datasetIds.map(
                        id =>
                            String(id)
                    )
                )
            ];


        for (
            const datasetId
            of uniqueDatasetIds
        ) {

            const dataset =
                datasets.find(
                    item =>
                        String(
                            item.id
                        ) ===
                        String(
                            datasetId
                        )
                );


            const datasetBeastmodes =
                beastmodes.filter(
                    item =>
                        String(
                            item.datasetId
                        ) ===
                        String(
                            datasetId
                        )
                );


            const complexity =
                calculateComplexity({
                    cardCount:
                        relatedCards.length,

                    beastmodeCount:
                        datasetBeastmodes.length,

                    pageCount:
                        1,

                    issueCount:
                        0
                });


            result.push({

                datasetId,

                datasetName:
                    dataset?.name ||
                    dataset?.displayName ||
                    '-',

                pages: [
                    {
                        pageId:
                            page.id,

                        pageName:
                            page.name ||
                            page.title
                    }
                ],

                cards:
                    relatedCards,

                beastmodes:
                    datasetBeastmodes,

                pageCount:
                    1,

                cardCount:
                    relatedCards.length,

                beastmodeCount:
                    datasetBeastmodes.length,

                issueCount:
                    0,

                complexity,

                migrationReadiness:
                    calculateReadiness({
                        issueCount:
                            0,

                        complexity
                    })

            });

        }

    }


    return result;
}


/*
|--------------------------------------------------------------------------
| Complexity
|--------------------------------------------------------------------------
*/

function calculateComplexity({
    cardCount = 0,
    beastmodeCount = 0,
    pageCount = 0,
    issueCount = 0
}) {
    
    const score =
        (cardCount * 3) +
        // (beastmodeCount * 1) +
        (pageCount * 4) +
        (issueCount * 4);


    if (score >= 20) {
        return 'High';
    }


    if (score >= 8) {
        return 'Medium';
    }


    return 'Low';
}


/*
|--------------------------------------------------------------------------
| Readiness
|--------------------------------------------------------------------------
*/

function calculateReadiness({
    issueCount = 0,
    complexity = 'Low'
}) {

    if (
        issueCount > 0
    ) {
        return 'Review Required';
    }


    if (
        complexity ===
        'High'
    ) {
        return 'Review Required';
    }


    return 'Ready';
}

/*
|--------------------------------------------------------------------------
| Assessment Status Banner
| Shows last sync time, failure info, and progress when running.
|--------------------------------------------------------------------------
*/

function AssessmentStatusBanner({ jobStatus, progress, isRunning, summary = {}, isHistoryView = false }) {
    const { lastSuccess, lastFailure } = jobStatus || {};

    const formatTime = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now - d;
        const diffMin = Math.floor(diffMs / 60000);
        const diffHr = Math.floor(diffMin / 60);

        if (diffMin < 1) return 'Just now';
        if (diffMin < 60) return `${diffMin} min ago`;
        if (diffHr < 24) return `${diffHr}h ago`;

        return d.toLocaleDateString(undefined, {
            month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div style={{ marginTop: '8px', fontSize: '11px', lineHeight: 1.7, textAlign: 'right' }}>

            {/* Running state - show progress bar. /assess/:id (a specific
                run, either historical or a fresh in-progress job reached
                by id) gets a bolder, labeled bar with a percent badge;
                the plain /assess (latest/live) view keeps the original
                slim inline bar. */}
            {isRunning && (
                isHistoryView ? (
                    <div style={{ marginBottom: '4px', minWidth: '220px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ color: 'var(--accent)', fontWeight: 600, fontSize: '11px' }}>
                                {progress.stage ? `${progress.stage}` : 'Starting...'}
                            </span>
                            <span style={{
                                color: '#fff', background: 'var(--accent)',
                                borderRadius: '10px', padding: '1px 8px',
                                fontSize: '10.5px', fontWeight: 700
                            }}>
                                {progress.percent || 0}%
                            </span>
                        </div>
                        <div style={{
                            width: '100%', height: '8px',
                            background: 'var(--border-color)', borderRadius: '4px', overflow: 'hidden',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)'
                        }}>
                            <div style={{
                                width: `${progress.percent || 0}%`, height: '100%',
                                background: 'var(--accent)',
                                borderRadius: '4px',
                                transition: 'width 0.4s ease'
                            }} />
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>
                            {progress.stage ? `${progress.stage}` : 'Starting...'}
                        </span>
                        <div style={{
                            width: '140px', height: '5px',
                            background: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden'
                        }}>
                            <div style={{
                                width: `${progress.percent || 0}%`, height: '100%',
                                background: 'var(--accent)', borderRadius: '3px',
                                transition: 'width 0.4s ease'
                            }} />
                        </div>
                    </div>
                )
            )}

            {/* Last successful sync */}
            {lastSuccess && (
                <div style={{ color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px' }}>
                    <FontAwesomeIcon icon={faCircleCheck} style={{ fontSize: '10px' }} />
                    <span>
                        Last sync: {formatTime(lastSuccess.completedAt || lastSuccess.snapshot?.createdAt)}
                    </span>
                </div>
            )}

            {/* Resource counts */}
            {/* {lastSuccess && (summary.datasets || summary.cards || summary.pages) && (
                <div style={{ color: 'var(--text-secondary)', marginTop: '3px', display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '4px 10px' }}>
                    {summary.datasets ? <span>{summary.datasets} Datasets</span> : null}
                    {summary.cards ? <span>{summary.cards} Cards</span> : null}
                    {summary.pages ? <span>{summary.pages} Pages</span> : null}
                    {summary.dataflows ? <span>{summary.dataflows} DataFlows</span> : null}
                    {summary.beastmodes ? <span>{summary.beastmodes} Beast Modes</span> : null}
                    {(summary.users || summary.groups) ? <span>{summary.users || 0} Users / {summary.groups || 0} Groups</span> : null}
                    {summary.dependencies ? <span>{summary.dependencies} Dependencies</span> : null}
                </div>
            )} */}

            {/* Last failure (only shown if more recent than success) */}
            {lastFailure && (
                <div style={{ color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', marginTop: '2px' }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '10px' }} />
                    <span>
                        Failed{lastFailure.failedAt ? ` at ${formatTime(lastFailure.failedAt)}` : ''}
                        {lastFailure.errorMessage ? `: ${lastFailure.errorMessage}` : ''}
                    </span>
                </div>
            )}

            {/* No data yet */}
            {!lastSuccess && !isRunning && (
                <div style={{ color: 'var(--text-tertiary)' }}>
                    No assessment data. Click "Start Assessment" to begin.
                </div>
            )}

            {/* Showing cached data while running */}
            {isRunning && lastSuccess && (
                <div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Showing data from: {formatTime(lastSuccess.completedAt || lastSuccess.snapshot?.createdAt)}
                </div>
            )}
        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Current Data
|--------------------------------------------------------------------------
*/

function getCurrentData(
    activeTab,
    datasets,
    cards,
    pages,
    dataflows,
    beastmodes,
    issues,
    columns = [],
    users = [],
    groups = [],
    dependencies = [],
    security = [],
    governance = [],
    apiCoverage = []
) {

    switch (activeTab) {

        case 'datasets':
            return datasets;

        case 'cards':
            return cards;

        case 'pages':
            return pages;

        case 'dataflows':
            return dataflows;

        case 'beastmodes':
            return beastmodes;

        case 'issues':
            return issues;
        case 'columns':
            return columns;
        case 'users-groups':
            return [...users, ...groups];
        case 'dependencies':
            return dependencies;
        case 'security-access':
            return security;
        case 'governance':
            return governance;
        case 'api-coverage':
            return apiCoverage;

        default:
            return [];

    }
}


/*
|--------------------------------------------------------------------------
| Tab Label
|--------------------------------------------------------------------------
*/

function getTabLabel(type) {

    const labels = {

        datasets: 'datasets',
        cards: 'cards',
        pages: 'pages',
        dataflows: 'dataflows',
        beastmodes: 'beast modes',
        issues: 'issues',
        columns: 'columns',
        'users-groups': 'users and groups',
        dependencies: 'dependencies',
        'security-access': 'security and access',
        governance: 'governance',
        'api-coverage': 'API coverage',
        'merge-sets': 'merge sets'

    };


    return labels[type] || type;
}


/*
|--------------------------------------------------------------------------
| Flatten Rows For Export
|--------------------------------------------------------------------------
| Converts each tab's raw data array into plain row objects suitable
| for an Excel sheet, mirroring the columns shown in each tab's table.
|--------------------------------------------------------------------------
*/

function flattenRowsForExport(type, data) {

    if (!Array.isArray(data) || !data.length) {
        return [];
    }

    switch (type) {

        case 'datasets':
            return data.map(item => {
                const ds = getDatasetDatasourceDetails(item);
                return {
                    Dataset: item.datasetName || item.name || '-',
                    'Dataset ID': item.datasetId || item.id || '-',
                    Columns: item.schema?.length ?? item.columns?.length ?? 0,
                    Rows: item.rows ?? 0,
                    'Datasource / Connector': ds.driver !== '-' ? ds.driver : 'File',
                    Schema: ds.schema,
                    'Source Table': ds.tableName,
                    'Query Type': ds.queryType,
                    'Update Mode': ds.updateMode,
                    'Updated At': item?.updatedAt || '-'
                };
            });

        case 'cards':
            return data.map(item => ({
                Card: item.cardName || item.name || item.title || '-',
                Dataset: item.datasetName || item.dataSetId || item.datasetId || '-',
                'Chart Type': item.chartType || item.type || '-',
                Page: item.pageName || '-',
                'Calculated Fields': (item.calculatedFields || []).map(f => `${f.name}: ${f.formula}`).join('; ') || '-',
                Filters: (item.detail?.filters || item.filters || []).length,
                Variables: (item.variables || []).length,
                Hierarchy: (item.hierarchy || []).length,
                Sets: (item.sets || []).length,
                Groups: (item.groups || []).length
            }));

        case 'columns':
            return data.map(item => ({
                Column: item.name || item.columnName || '-',
                'Data Type': item.type || item.dataType || item.columnType || '-',
                Dataset: item.datasetName || item.datasetId || '-'
            }));

        case 'pages':
            return data.map(item => ({
                Page: item.pageName || item.name || item.title || '-',
                'Page ID': item.pageId || item.id || '-',
                Cards: item.cardCount ?? item.cards?.length ?? 0,
                Datasets: item.datasetCount ?? getPageDatasetCount(item)
            }));

        case 'dataflows':
            return data.map(item => ({
                Name: item.name || item.dataflowName || '-',
                ID: item.id || item.dataflowId || '-',
                Type: item.type || item.dataFlowType || '-'
            }));

        case 'beastmodes':
            return data.map(item => ({
                Name: item.name || item.functionName || item.alias || '-',
                Formula: item.formula || item.expression || item.body || '-',
                Dataset: item.datasetName || item.datasetId || '-'
            }));

        case 'issues':
            return data.map(item => ({
                Severity: item.severity || '-',
                Type: item.type || '-',
                Issue: item.message || '-',
                Dataset: item.datasetName || '-',
                Page: item.pageName || '-',
                Card: item.cardName || '-'
            }));

        case 'users-groups':
            return data.map(item => ({
                Type: item.userId || item.email ? 'User' : 'Group',
                Name: item.name || item.displayName || item.username || item.email || item.groupName || '-',
                ID: item.id || item.userId || item.groupId || '-'
            }));

        case 'dependencies':
            return data.map(item => ({
                Dataset: item.datasetName || '-',
                'Dataset ID': item.datasetId || '-',
                Cards: item.cards?.length ?? 0,
                Pages: item.pages?.length ?? 0,
                DataFlows: item.dataflows?.length ?? 0,
                'Beast Modes': item.beastmodes?.length ?? 0
            }));

        case 'security-access':
            return data.map(item => ({
                'Resource Type': item.resourceType || '-',
                'Resource ID': item.resourceId || '-',
                Resource: item.resourceName || '-',
                Owner: item.owner || '-',
                Permissions: Array.isArray(item.permissions) ? item.permissions.join(', ') : (item.permissions || '-'),
                Security: item.security || '-',
                'Metadata Returned': item.metadataReturned ? 'Yes' : 'No'
            }));

        case 'governance':
            return data.map(item => ({
                'Resource Type': item.resourceType || '-',
                'Resource ID': item.resourceId || '-',
                Resource: item.resourceName || '-',
                Owner: item.owner || '-',
                Description: item.description || '-',
                Tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || '-'),
                Certification: item.certification || '-',
                Refresh: item.refresh || '-',
                Schedule: item.schedule || '-'
            }));

        case 'api-coverage':
            return data.map(item => ({
                Metadata: item.name || '-',
                Count: item.count ?? 0,
                Status: item.status || '-',
                Details: item.message || '-'
            }));

        default:
            return data.map(item => {
                if (item === null || typeof item !== 'object') {
                    return { Value: item };
                }
                const out = {};
                for (const [key, value] of Object.entries(item)) {
                    if (value === null || value === undefined || typeof value === 'object') continue;
                    out[key] = value;
                }
                return out;
            });
    }
}


/*
|--------------------------------------------------------------------------
| Page Dataset Count
|--------------------------------------------------------------------------
*/

function getPageDatasetCount(page) {

    const cards =
        page.cards || [];


    const ids =
        cards
            .map(
                card =>
                    card.dataSetId ||
                    card.datasetId ||
                    card.datasourceId ||
                    card.dataSourceId
            )
            .filter(Boolean);


    return [
        ...new Set(
            ids.map(
                id =>
                    String(id)
            )
        )
    ].length;
}


/*
|--------------------------------------------------------------------------
| Empty State
|--------------------------------------------------------------------------
*/

function EmptyState({
    message
}) {

    return (

        <div
            style={{
                textAlign: 'center',
                padding: '48px 20px',
                color:
                    'var(--text-tertiary)',
                fontSize: '13px'
            }}
        >

            {message}

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Common Styles
|--------------------------------------------------------------------------
*/

const summaryCardStyle = {

    background:
        'var(--card-bg, #fff)',

    border:
        '1px solid var(--border-color)',

    borderRadius: '10px',

    padding: '18px'

};


const smallLabelStyle = {

    fontSize: '11px',

    color:
        'var(--text-secondary)',

    marginBottom: '7px'

};


const tableHeaderStyle = {

    textAlign: 'left',

    padding: '11px 14px',

    borderBottom:
        '1px solid var(--border-color)',

    color:
        'var(--text-secondary)',

    fontWeight: 600,

    whiteSpace: 'nowrap',

    background:
        'var(--hover-bg)'

};


const tableCellStyle = {

    padding: '11px 14px',

    color:
        'var(--text-primary)',

    verticalAlign: 'middle'

};


/*
|--------------------------------------------------------------------------
| Pagination
|--------------------------------------------------------------------------
*/

function Pagination({
    page,
    totalPages,
    onPageChange,
    total,
    pageSize
}) {

    const start =
        ((page - 1) * pageSize) + 1;


    const end =
        Math.min(
            page * pageSize,
            total
        );


    return (

        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderTop:
                    '1px solid var(--border-color)'
            }}
        >

            <div
                style={{
                    fontSize: '12px',
                    color:
                        'var(--text-secondary)'
                }}
            >
                Showing {start} - {end} of {total}
            </div>


            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}
            >

                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() =>
                        onPageChange(
                            page - 1
                        )
                    }
                    style={{
                        width: '32px',
                        height: '30px',
                        border:
                            '1px solid var(--border-color)',
                        background:
                            'var(--card-bg)',
                        borderRadius: '5px',
                        cursor:
                            page <= 1
                                ? 'not-allowed'
                                : 'pointer',
                        opacity:
                            page <= 1
                                ? 0.5
                                : 1
                    }}
                >

                    <FontAwesomeIcon
                        icon={faChevronLeft}
                    />

                </button>


                <span
                    style={{
                        fontSize: '12px',
                        padding: '0 8px',
                        color:
                            'var(--text-secondary)'
                    }}
                >
                    Page {page} of {totalPages}
                </span>


                <button
                    type="button"
                    disabled={
                        page >= totalPages
                    }
                    onClick={() =>
                        onPageChange(
                            page + 1
                        )
                    }
                    style={{
                        width: '32px',
                        height: '30px',
                        border:
                            '1px solid var(--border-color)',
                        background:
                            'var(--card-bg)',
                        borderRadius: '5px',
                        cursor:
                            page >= totalPages
                                ? 'not-allowed'
                                : 'pointer',
                        opacity:
                            page >= totalPages
                                ? 0.5
                                : 1
                    }}
                >

                    <FontAwesomeIcon
                        icon={faChevronRight}
                    />

                </button>

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Loading
|--------------------------------------------------------------------------
*/

function Loading() {

    return (

        <div
            style={{
                textAlign: 'center',
                padding: '60px 20px',
                color:
                    'var(--text-secondary)'
            }}
        >

            <div
                style={{
                    width: '30px',
                    height: '30px',
                    border:
                        '3px solid var(--border-color)',
                    borderTopColor:
                        '#1e447e',
                    borderRadius: '50%',
                    margin:
                        '0 auto 14px',
                    animation:
                        'domoAssessmentSpin 0.8s linear infinite'
                }}
            />


            <div
                style={{
                    fontSize: '13px'
                }}
            >
                Assessing Domo resources...
            </div>


            <style>
                {`
                    @keyframes domoAssessmentSpin {
                        from {
                            transform: rotate(0deg);
                        }

                        to {
                            transform: rotate(360deg);
                        }
                    }
                `}
            </style>

        </div>
    );
}